import {
  keepPreviousData, useInfiniteQuery, useMutation, useQueries, useQuery, useQueryClient, type UseQueryOptions,
} from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@/lib/auth";
import { accountScope, retryQuery } from "@/lib/session-cache";
import type { AgentStep } from "@/lib/acd";
import type { ContactSms } from "@/lib/contact-activity";
import type { CallBack } from "@/lib/call-back";
import { localMonthStart } from "@/lib/contacts";
import { detectionSettled, type HeardVoicemail } from "@/lib/dialer";
import { CADENCE_POLL_MS, leadCadencePoll } from "@/lib/cadence";
import type { MonitorMode } from "@/lib/monitor";
import { ApiError, api, fetchFile, qs, type QueryValue } from "@/lib/api";
import { markReadInList, toggleReaction } from "@/lib/chats";
import type { FirmInput } from "@/lib/firms";
import { handoffRequest, type HandoffAsk } from "@/lib/handoff";
import { moveCard } from "@/lib/opportunities";
import { nextPageOffset } from "@/lib/paging";
import { transcriptKey, transcriptPollInterval } from "@/lib/transcripts";
import { atLeast } from "@/lib/types";
import type {
  CalendarCallback,
  CallbackTaker,
  WrittenCallSummary,
  CallDetection,
  AgentActionResponse, AgentState, Appointment, AppointmentBooked, AppointmentDetail, AppointmentListItem, AudioFile,
  CalendarBlock, AvailabilityRange, Calendar,
  CalendarSlots, CalendarType,
  MeetingLocationKind, StaffPriority,
  Cadence, CadenceActionResult, CadenceBody, CadenceCampaignRef, CadenceMigrate, CadenceVersion,
  CadenceWork, CampaignAbandonment, CampaignNumbers, LeadCadence,
  AcdFloor, AcdStats, AppUser, CallDispositionResponse, Campaign, CampaignLead, Cdr, CdrDetail,
  CdrSummary, Colleague, DialerActionResponse, DialerCall, DialerOffer, Did, Disposition, Domain,
  Endpoint, CallHoldResponse, CallLeadResponse, CallerNameResponse, CallerRelation, EndpointSecret, Handoff, HandoffClosers, Invitation, InvitationBulkSent,
  InternalTransferNumbers, InvitationSent, AccidentRole, LeadAccident, LeadCallingWindow, LeadCard, LeadFields, LeadHistory, ManualCall,
  LostCallsPage,
  MissedCall,
  AdminForm, AdminFormField, AdminFormSection, FirmFields, FormAccessRule, FormEntity, FormFieldConfig,
  FormFieldType, ReferenceSearch,
  CallbackBackupRequest, ContactBoard, ContactBulkResult, ContactChannel, ContactOutcome, ContactRow, ContactSummary,
  ContractAgreements, ContractCheck, ContractSent, Firm, LeadCallback, LeadContract, LeadDeleted, LeadNote,
  LeadReopen, LeadStageMove, LeadTimeline,
  LeadList, ConsultOutcome, InviteMode, RoomCall, RoomCommands, RoomInviteResult, CallTranscript,
  OpportunityListInput, OpportunityListRow, OutsideParticipantResult, SpeedDial, StageColor,
  MonitorResponse, QueueMember, QueueSession, Recording, ReportOptions, RetentionDue, RoutingSkill,
  SignInLinkMade, Team, ChatDetail, ChatMessage, ChatPerson, ChatPersonStatus, ChatReaction, ChatSummary, ChatThread,
  OpenTeamChat,
  CloserAsk, CloserAskState, CloserAskTargets, CloserNoteDraft,
  SearchedFields, SkillRequirement, StageRemoved, StageRow, StageRule, TranscribeAccepted, TranscriptLines,
  TranscriptListItem, TranscriptSegment, TranscriptSource, UserSkill, UserSkills, VoicemailMessage,
  GreetingField, GreetingPreview,
  Gateway, GatewaySecret, NumberOrder, OutboundRouting, PhoneNumber, Queue, RepNumberFallback, RepTextFallback,
  RouteType, Tenant,
  WebhookDelivery, WebhookDeliveryDetail, WebhookEndpoint, WebhookEndpointCreated, WebhookEventType,
  WebhookField, WebhookSubscription, Trigger, TriggerCreated, TriggerInboxDetail, TriggerInboxRow, TriggerSecret,
  TextCompose, TextConversation, TextInbox, TextMedia, TextMessage, TextMessages, TextSend,
  EmailCompose, EmailSend, MessageDetail, ReplySend,
  CampaignStageCadence, StaffingAlert, StaffingAlertInput,
  Person,
} from "@/lib/types";

/**
 * Server state. One key factory per resource so invalidation is exact — invalidating a
 * hand-written string is how a list quietly stops refreshing after a write.
 */

export function createQueryKeys(scope: string) {
  const prefix = ["account", scope] as const;
  return {
    scoped: (key: readonly unknown[]) => [...prefix, ...key] as const,
    users: (filters?: Readonly<Record<string, QueryValue>>) => [...prefix, "users", filters ?? {}] as const,
    user: (id: number) => [...prefix, "users", id] as const,
    /** Every team, and one team under it: a write to either refetches both (platform ADR-185). */
    teams: () => [...prefix, "teams"] as const,
    team: (id: number) => [...prefix, "teams", id] as const,
    /** Every chat the person is in, and each one's people and messages under it: a `chats` hint
     *  refetches them all (platform ADR-186). */
    chats: () => [...prefix, "chats"] as const,
    chat: (id: number) => [...prefix, "chats", id] as const,
    chatMessages: (id: number) => [...prefix, "chats", id, "messages"] as const,
    /** A thread in a chat (platform ADR-191), under the chat: its hint refetches it. */
    chatThread: (id: number, root: number) => [...prefix, "chats", id, "threads", root] as const,
    chatPeople: (q: string) => [...prefix, "chats", "people", q] as const,
    /** The teams anyone may chat with (platform ADR-190). */
    chatTeams: () => [...prefix, "chats", "teams"] as const,
    /** Where somebody one chats with is on the phone (platform ADR-186, "Status"). */
    chatPersonStatus: (userId: number) => [...prefix, "chats", "people", "status", userId] as const,
    /** Asks for a closer (platform ADR-187), under `chats`: their cards are messages, and the
     *  same hint refetches the setter's strip and the closer's hold. */
    closerAsks: () => [...prefix, "chats", "asks"] as const,
    closerAskTargets: (callId: string) => [...prefix, "chats", "asks", "targets", callId] as const,
    closerAskForCall: (callId: string) => [...prefix, "chats", "asks", "call", callId] as const,
    closerAskHeld: () => [...prefix, "chats", "asks", "held"] as const,
    /** Under `users`, like `endpointPages` under `endpoints`: a write to somebody refetches the
     *  pickers that list them too. */
    userPages: (filters?: Readonly<Record<string, QueryValue>>) =>
      [...prefix, "users", "paged", filters ?? {}] as const,
    /** Under `users`: every write to people refetches it, and accepting an invitation makes a user. */
    invitations: () => [...prefix, "users", "invitations"] as const,
    endpoints: (filters?: Readonly<Record<string, QueryValue>>) =>
      [...prefix, "endpoints", filters ?? {}] as const,
    endpoint: (id: number) => [...prefix, "endpoints", id] as const,
    /** Keyed by the channel uuid, because that is what a recording is of — one leg. */
    recording: (callId: string | undefined) => [...prefix, "recordings", callId ?? "none"] as const,
    /** By the same uuid as the recording it sits under, and outside `calls`: a finished call's
     *  list refetches must not re-read every transcript opened this session. */
    transcript: (callId: string | undefined) => [...prefix, "transcripts", callId ?? "none"] as const,
    writtenSummary: (callId: string | undefined) =>
      [...prefix, "written-summary", callId ?? "none"] as const,
    /** Under the `endpoints` prefix on purpose: the blanket invalidate after a write reaches the
     *  pickers' paged queries too, so assigning a phone updates the list you assigned it from. */
    endpointPages: (filters?: Readonly<Record<string, QueryValue>>) =>
      [...prefix, "endpoints", "paged", filters ?? {}] as const,
    domains: () => [...prefix, "domains"] as const,
    /** Who the signed-in rep can dial. Its own prefix: `/phone/directory` is a different route
     *  with a different scope, so `useInvalidateIdentity` names it explicitly. */
    colleagues: () => [...prefix, "phone", "directory"] as const,
    /** The calls the rep's phone did not take (ADR-048). Under `calls`, so what refetches the
     *  history refetches it; keyed by the window, since the page and a wider view ask differently. */
    missedCalls: (filters?: Readonly<Record<string, QueryValue>>) =>
      [...prefix, "calls", "missed", filters ?? {}] as const,
    /** The room one of the phone's legs is in (ADR-032): under `phone/room`, the prefix the
     *  `room` hint refetches. Keyed by the leg, since a phone can hold more than one. */
    roomCall: (callId: string) => [...prefix, "phone", "room", callId] as const,
    /** The tenant's named outside numbers (ADR-065). */
    speedDials: () => [...prefix, "speed-dials"] as const,
    /** Every caller blocked now (ADR-159), Settings' list. The lookups below are under it, so a
     *  block or an unblock refetches them all. */
    blockedCallers: () => [...prefix, "blocked-callers"] as const,
    /** The blocks in force on a few callers only, by the callers as a lookup names them. */
    blocksOn: (callers: readonly string[]) => [...prefix, "blocked-callers", "on", callers] as const,
    internalTransferNumbers: () => [...prefix, "phone", "internal-transfer-numbers"] as const,
    gateways: () => [...prefix, "gateways"] as const,
    tenants: () => [...prefix, "tenants"] as const,
    queues: () => [...prefix, "queues"] as const,
    /** Under `acd`, the prefix the `queue` hint refetches (`lib/acd.ts`). */
    acdMe: () => [...prefix, "acd", "me"] as const,
    /** The rep's own queue sessions, and a handoff: under `acd` too, so the `queue` hint refetches
     *  them with the state they belong to. */
    acdSessions: (user: string) => [...prefix, "acd", "sessions", user] as const,
    handoff: (id: string) => [...prefix, "acd", "handoffs", id] as const,
    /** Who a call can be handed to, with each closer's live state: under `acd` too, so the `queue`
     *  hint refetches it as closers come and go (ADR-031). */
    handoffClosers: (callId: string) => [...prefix, "acd", "handoffs", "closers", callId] as const,
    /** A lead's card (ADR-024). Not under `acd`, which every queue hint refetches: a lead does not
     *  change while their call rings, and the card would flicker. Keyed by the caller's stay in the
     *  line as well: the card lists only calls that are over, so a lead who calls back within the
     *  stale time is read again, and the call that just ended is on their card. */
    leadCard: (id: string, stay: string) => [...prefix, "leads", id, stay] as const,
    callerName: (callerChannelId: string) => [...prefix, "acd", "caller-name", callerChannelId] as const,
    /** Who a number that calls the rep's own phone is among the contacts (ACL-483). Not under
     *  `leads`: every contact written on the floor sends a `leads` hint, a second or two apart
     *  while the dialer runs, and the search is asked once for a call, not on each of them. */
    callerContacts: (digits: string) => [...prefix, "caller-contacts", digits] as const,
    /** The contacts list and record (ADR-044), all under `leads`: a write to a contact — a note,
     *  an outcome, an edit — refetches the list, the tiles, the record and its timeline together. */
    contacts: (filters?: Readonly<Record<string, QueryValue>>) =>
      [...prefix, "leads", "list", filters ?? {}] as const,
    contactSummary: (filters?: Readonly<Record<string, QueryValue>>) =>
      [...prefix, "leads", "summary", filters ?? {}] as const,
    contact: (id: string) => [...prefix, "leads", "detail", id] as const,
    person: (id: string) => [...prefix, "people", id] as const,
    /** The Opportunities board (ADR-078), under `leads` with the list: a write to a contact moves
     *  its card too. A column's own page is the same key with its stage in the filters. */
    contactBoard: (filters?: Readonly<Record<string, QueryValue>>) =>
      [...prefix, "leads", "board", filters ?? {}] as const,
    /** A few rows of the contacts list, for a record's previous and next arrows: outside `leads` on
     *  purpose, so a `leads` hint never reads it again (`useContactWindow`). */
    contactWindow: (filters: Readonly<Record<string, QueryValue>>, offset: number, size: number) =>
      [...prefix, "contact-window", { ...filters, offset, size }] as const,
    /** The tenant's stages (ADR-077), keyed by the tenant a superadmin names; "own" for everyone
     *  else. Every stage label and picker reads them. */
    stages: (tenantId: number | null) => [...prefix, "stages", tenantId ?? "own"] as const,
    /** `channel` narrows the messages to texts or emails (ADR-180); "any" when it is not asked. */
    contactTimeline: (id: string, kinds: string, channel?: string) =>
      [...prefix, "leads", "detail", id, "timeline", kinds, channel ?? "any"] as const,
    contactNotes: (id: string) => [...prefix, "leads", "detail", id, "notes"] as const,
    /** The contact's own fields (ACL-255), under the record so a write to the contact refetches
     *  them with it. */
    contactFields: (id: string) => [...prefix, "leads", "detail", id, "fields"] as const,
    /** The contact's accident and the records beside it (ACL-337), under the record so a write to
     *  the contact, and every hint the record refetches on, refetch it with it. */
    contactAccident: (id: string) => [...prefix, "leads", "detail", id, "accident"] as const,
    /** The records a phone or an e-mail typed into the new-contact form already has (ACL-409). Not
     *  under `leads`: every `leads` hint refetches what is under it but a record (`leadsHintTouches`),
     *  which on a dialing floor is every second or two, for as long as the form is open. */
    leadHistory: (phone: string, email: string) => [...prefix, "lead-history", phone, email] as const,
    /** A tenant's custom fields as their admin edits them (ACL-256), keyed by the kind of record and,
     *  for a superadmin, the tenant ("own" for everyone else). Under `forms`, so one invalidate after a
     *  write reaches every form read. */
    formDesign: (entity: FormEntity, tenantId: number | null) =>
      [...prefix, "forms", entity, "design", tenantId ?? "own"] as const,
    /** What a reference field's picker finds for a search (ACL-260), under `forms` with the design. */
    referenceSearch: (entity: FormEntity, key: string, q: string) =>
      [...prefix, "forms", entity, "references", key, q] as const,
    /** What a person's own search of contacts or firms looks in (ADR-101), for the hint over the search
     *  box: under `forms`, so saving the searchable fields refreshes it with the design. */
    searchedFields: (entity: FormEntity) => [...prefix, "forms", entity, "searchable"] as const,
    /** A contact's texts: under the contact, so a write to it refreshes them. A contact's appointments
     *  are an appointments list (`useContactAppointments`), which every booking refreshes. */
    contactSms: (id: string) => [...prefix, "leads", "detail", id, "sms"] as const,
    /** Texts (ADR-095), all under `conversations`: the prefix the `messages` hint refetches, and
     *  what opening a conversation invalidates. */
    textInboxes: () => [...prefix, "conversations", "inboxes"] as const,
    textConversations: (filters?: Readonly<Record<string, QueryValue>>) =>
      [...prefix, "conversations", "list", filters ?? {}] as const,
    /** The inbox's list, page after page as it is scrolled: its own key, as an infinite query's
     *  cache cannot share one with a single page's, and under `conversations` with the rest. */
    textConversationPages: (filters?: Readonly<Record<string, QueryValue>>) =>
      [...prefix, "conversations", "pages", filters ?? {}] as const,
    textConversation: (id: string) => [...prefix, "conversations", "detail", id] as const,
    textMessages: (id: string) => [...prefix, "conversations", "detail", id, "messages"] as const,
    /** Whether a text may leave now (ADR-098): under the conversation, or under the contact, so
     *  the `messages` hint and a write to the contact both ask again. */
    textCompose: (id: string) => [...prefix, "conversations", "detail", id, "compose"] as const,
    contactTextCompose: (id: string, from: string | null) =>
      [...prefix, "leads", "detail", id, "sms", "compose", from ?? "default"] as const,
    /** Whether an email may leave now (ADR-180): under the conversation and under the contact, as a
     *  text's, so the `messages` hint, a send and a write to the contact all ask again. */
    emailCompose: (id: string) => [...prefix, "conversations", "detail", id, "email-compose"] as const,
    contactEmailCompose: (id: string) => [...prefix, "leads", "detail", id, "email", "compose"] as const,
    /** One message whole (`GET /messages/{id}`): under `conversations`, so a status that changes
     *  while the email is open reads again on the `messages` hint. */
    message: (id: string) => [...prefix, "conversations", "message", id] as const,
    /** Whether Send contract can be pressed (ADR-134 in platform): under the contact, so setting
     *  its firm asks again. */
    contactContract: (id: string) => [...prefix, "leads", "detail", id, "contract"] as const,
    // Under the check's key, so whatever asks the check again asks these again too.
    contactContractAgreements: (id: string) => [...prefix, "leads", "detail", id, "contract", "agreements"] as const,
    /** The lead's presses of Send contract and what became of them (ACL-427): under the contact,
     *  so a send and the lead's `leads` hint (a signature moves the lead) read them again. */
    contactContracts: (id: string) => [...prefix, "leads", "detail", id, "contracts"] as const,
    /** The tenant's firms (ADR-045): the filter, the badge, Set firm. A firm's page is under it, so
     *  one invalidate after a write reaches the list and the page. */
    firms: () => [...prefix, "firms"] as const,
    /** The Firms page's search, answered by the server (ADR-101): under `firms`, so a write to a firm, or
     *  to which firm fields are searched, asks again. */
    firmSearch: (q: string) => [...prefix, "firms", "search", q] as const,
    firm: (id: number) => [...prefix, "firms", "detail", id] as const,
    /** A firm's own fields (ACL-258), under the firm so a write to it refetches them with it. */
    firmFields: (id: number) => [...prefix, "firms", "detail", id, "fields"] as const,
    /** What a rep on a campaign's call chooses from. */
    callDispositions: (campaignId: string | null) =>
      [...prefix, "dispositions", "campaign", campaignId ?? "none"] as const,
    /** The caller's own most used outcomes: under `dispositions`, so a catalog write reads them
     *  again with the catalog. */
    myOutcomes: (campaignId: string | null) =>
      [...prefix, "dispositions", "mine", campaignId ?? "all"] as const,
    /** Under `dialer`, the prefix the `dialer` hint refetches (`lib/acd.ts`). */
    dialerOffer: () => [...prefix, "dialer", "offer"] as const,
    /** The lead the dialer is calling for the rep, under `dialer` for the same reason. */
    dialerCall: () => [...prefix, "dialer", "current"] as const,
    /** The lead the rep last dialed, which only the console keeps: outside `dialer`, because the
     *  hint's refetch has nothing on the server to fetch it from. */
    dialedLead: () => [...prefix, "dialed-lead"] as const,
    /** The answered dial the phone is waiting to hear about, and the last one the dialer hung up
     *  as a voicemail: the console's own, outside `dialer`, like `dialedLead`. */
    watchedDial: () => [...prefix, "watched-dial"] as const,
    heardVoicemail: () => [...prefix, "heard-voicemail"] as const,
    /** A call the rep placed by hand through their campaign (ADR-047), under `dialer` so the
     *  hint about its leg refetches it. */
    manualCall: (callId: string) => [...prefix, "dialer", "calls", callId] as const,
    /** What detection made of a dialer call (ADR-106), under `dialer` so the dialer's hint that
     *  it heard a machine refetches it at once. */
    callDetection: (callId: string) => [...prefix, "dialer", "calls", callId, "detection"] as const,
    /** Which of those calls the phone is following, and the campaign the rep chose last: the
     *  console's own, outside `dialer`, like `dialedLead`. */
    manualDial: () => [...prefix, "manual-dial"] as const,
    callBack: () => [...prefix, "call-back"] as const,
    voicemailMessages: () => [...prefix, "voicemail-messages"] as const,
    /** The library with its retired messages, which only an admin may ask for. Under the list a rep
     *  picks from, so invalidating that one reaches this one too. */
    voicemailLibrary: (retired: boolean) => [...prefix, "voicemail-messages", "library", retired] as const,
    greetingFields: () => [...prefix, "voicemail-messages", "fields"] as const,
    greetingPreview: (asked: object) => [...prefix, "voicemail-messages", "preview", asked] as const,
    /** Webhooks (ADR-022), all under `webhooks`: an endpoint's page shows its subscriptions and its
     *  deliveries, so one invalidate after any write reaches every screen that shows one. */
    webhookEndpoints: () => [...prefix, "webhooks", "endpoints"] as const,
    webhookEndpoint: (id: number) => [...prefix, "webhooks", "endpoints", "detail", id] as const,
    webhookSubscriptions: (endpointId: number) =>
      [...prefix, "webhooks", "endpoints", "detail", endpointId, "subscriptions"] as const,
    webhookDeliveries: (filters?: Readonly<Record<string, QueryValue>>) =>
      [...prefix, "webhooks", "deliveries", filters ?? {}] as const,
    webhookDelivery: (id: number) => [...prefix, "webhooks", "delivery", id] as const,
    /** The contract's event types with their samples: they change only with a platform release. */
    webhookEventTypes: () => [...prefix, "webhooks", "event-types"] as const,
    /** The contact form's fields a subscription can name: they change when an admin edits the form. */
    webhookFields: (endpointId: number) =>
      [...prefix, "webhooks", "endpoints", "detail", endpointId, "fields"] as const,
    /** Triggers (ADR-021), all under `triggers`: a trigger's page reads its inbox, so one invalidate
     *  after a write or a replay reaches both. */
    triggers: () => [...prefix, "triggers"] as const,
    trigger: (id: number) => [...prefix, "triggers", "detail", id] as const,
    triggerInbox: (id: number, filters?: Readonly<Record<string, QueryValue>>) =>
      [...prefix, "triggers", "detail", id, "inbox", filters ?? {}] as const,
    triggerRequest: (id: number, rowId: number) =>
      [...prefix, "triggers", "detail", id, "request", rowId] as const,
    /** The tenant's audio store, by search. Everything that reads it shares the `audio` prefix, so
     *  one invalidate after a write reaches the page and every picker. */
    audioLibrary: (search = "") => [...prefix, "audio", "library", search] as const,
    /** Under `acd`, so the `queue` hint — a rep's or a line's — refetches the floor. */
    acdFloor: () => [...prefix, "acd", "floor"] as const,
    /** NOT under `acd`: the history behind the stats moves slowly, and a busy floor's hints would
     *  otherwise recount it several times a second. It polls instead. */
    acdStats: (filters: Readonly<Record<string, QueryValue>>) => [...prefix, "acd-stats", filters] as const,
    /** The reports (ADR-074), keyed by the route and its whole query: history, polled, never hinted. */
    report: (kind: string, query: string) => [...prefix, "reports", kind, query] as const,
    reportOptions: () => [...prefix, "reports", "options"] as const,
    /** The signed-in person's stored console preferences: the reports' saved views live here. */
    uiPrefs: () => [...prefix, "ui-prefs"] as const,
    /** Opportunities' smart lists (ADR-078): the person's own and the tenant's shared ones. */
    opportunityLists: () => [...prefix, "opportunity-lists"] as const,
    campaigns: (filters?: Readonly<Record<string, QueryValue>>) =>
      [...prefix, "campaigns", filters ?? {}] as const,
    /** Under the `campaigns` prefix, with its tabs under it: the blanket invalidate after a
     *  campaign write reaches the page that made it. */
    campaign: (id: string) => [...prefix, "campaigns", "detail", id] as const,
    campaignMembers: (id: string) => [...prefix, "campaigns", "detail", id, "members"] as const,
    campaignDispositions: (id: string) => [...prefix, "campaigns", "detail", id, "dispositions"] as const,
    campaignLists: (id: string) => [...prefix, "campaigns", "detail", id, "lists"] as const,
    campaignLeads: (id: string, filters?: Readonly<Record<string, QueryValue>>) =>
      [...prefix, "campaigns", "detail", id, "leads", filters ?? {}] as const,
    /** A campaign's work under its cadence (ADR-066): under the campaign, keyed by the bucket. */
    campaignCadenceWork: (id: string, bucket: string) =>
      [...prefix, "campaigns", "detail", id, "cadence-work", bucket] as const,
    /** A campaign's abandon rate (platform ADR-206): under the campaign. */
    campaignAbandonment: (id: string) =>
      [...prefix, "campaigns", "detail", id, "abandonment"] as const,
    /** A campaign's numbers (ACL-629): under the campaign, so every number write reaches it. */
    campaignNumbers: (id: string) => [...prefix, "campaigns", "detail", id, "numbers"] as const,
    /** Every dialing campaign's abandon rate, for the Live floor. */
    abandonmentRates: () => [...prefix, "abandonment"] as const,
    /** The cadence catalog (ADR-066), and one cadence's versions and campaigns under it. */
    cadences: () => [...prefix, "cadences"] as const,
    cadence: (id: number) => [...prefix, "cadences", "detail", id] as const,
    cadenceVersions: (id: number) => [...prefix, "cadences", "detail", id, "versions"] as const,
    cadenceCampaigns: (id: number) => [...prefix, "cadences", "detail", id, "campaigns"] as const,
    cadencePause: () => [...prefix, "cadences", "pause"] as const,
    leadLine: () => [...prefix, "tenant", "lead-line"] as const,
    /** The fields a cadence's text can use (ADR-135). Not under `cadences`: a cadence saved
     *  changes none of them. */
    cadenceTextFields: () => [...prefix, "cadence-text-fields"] as const,
    /** A lead's place on each cadence campaign: under `leads`, so what refetches the record
     *  refetches it. */
    leadCadence: (id: string) => [...prefix, "leads", "detail", id, "cadence"] as const,
    /** Keyed by tenant: a superadmin reads a campaign's catalog by naming its tenant. */
    dispositions: (tenantId: number | null) => [...prefix, "dispositions", tenantId] as const,
    /** The catalog with its retired outcomes, which only an admin may ask for. Under the tenant's
     *  key, so invalidating the active catalog reaches this one too. */
    dispositionCatalog: (tenantId: number) => [...prefix, "dispositions", tenantId, "all"] as const,
    /** Every outcome of the tenant, switched-off ones too, for the words of a recorded slug
     *  (ACL-574). Under `dispositions`, so a catalog write refetches it. */
    outcomeHistory: () => [...prefix, "dispositions", "history"] as const,
    /** The skill catalog, and each rep's set under the same prefix: saving a rep's skills moves the
     *  catalog's holder counts, so one invalidation has to reach both. */
    skills: () => [...prefix, "skills", "catalog"] as const,
    /** The tenant's calendars (ADR-085), the list and each one under the same prefix: a save
     *  changes what both show. */
    calendars: (filters?: Readonly<Record<string, QueryValue>>) =>
      [...prefix, "calendars", "list", filters ?? {}] as const,
    calendar: (id: number) => [...prefix, "calendars", "one", id] as const,
    /** Under `calendars`: a save to the calendar changes its slots too. */
    appointments: (filters?: Readonly<Record<string, QueryValue>>) =>
      [...prefix, "appointments", "list", filters ?? {}] as const,
    appointment: (id: number) => [...prefix, "appointments", "one", id] as const,
    calendarBlocks: (filters?: Readonly<Record<string, QueryValue>>) =>
      [...prefix, "calendar-blocks", filters ?? {}] as const,
    calendarSlots: (id: number, start: string, end: string, ignore?: number) =>
      [...prefix, "calendars", "slots", id, start, end, ignore ?? null] as const,
    userSkills: (userId: number) => [...prefix, "skills", "user", userId] as const,
    numbers: (filters?: Readonly<Record<string, QueryValue>>) => [...prefix, "numbers", filters ?? {}] as const,
    availableNumbers: (filters?: Readonly<Record<string, QueryValue>>) =>
      [...prefix, "numbers", "available", filters ?? {}] as const,
    orders: (filters?: Readonly<Record<string, QueryValue>>) => [...prefix, "orders", filters ?? {}] as const,
    dids: (filters?: Readonly<Record<string, QueryValue>>) => [...prefix, "dids", filters ?? {}] as const,
    calls: (filters?: Readonly<Record<string, QueryValue>>) => [...prefix, "calls", filters ?? {}] as const,
    /** The Calls page's "Never reached us" (platform ADR-178). */
    lostCalls: (filters?: Readonly<Record<string, QueryValue>>) =>
      [...prefix, "lost-calls", filters ?? {}] as const,
    /** Under the same `calls` prefix as the page, and keyed by the *same* filters minus paging —
     *  the summary describes the filter, so it must be cached per filter and not per page. */
    callSummary: (filters?: Readonly<Record<string, QueryValue>>) =>
      [...prefix, "calls", "summary", filters ?? {}] as const,
    /** A contact's Transcripts tab (ADR-081). Its own prefix, not `transcripts`, which is one
     *  call's transcript by its uuid, and not the contact's, which every note and outcome
     *  refetches. */
    contactTranscripts: (leadId: string) => [...prefix, "transcript-list", leadId] as const,
    /** One transcript's lines, by what names it on the tab: its source and its id. */
    transcriptLines: (id: string | undefined, source: string | undefined) =>
      [...prefix, "transcript-lines", source ?? "none", id ?? "none"] as const,
  };
}

/** Capture identity in each hook; callbacks must never consult a mutable global user. */
export function useKeys() {
  const { user } = useAuth();
  const scope = accountScope(user);
  return useMemo(() => createQueryKeys(scope), [scope]);
}

export interface UserFilters {
  /** Index signature so these satisfy `qs()` and the key factory; the named fields below are
   *  what callers actually pass, and TypeScript still rejects a misspelled one at object
   *  literal sites thanks to excess-property checking. */
  [key: string]: QueryValue;
  q?: string;
  role?: string;
  domain_id?: number;
  is_active?: boolean;
  unassigned?: boolean;
}

export const USERS_PAGE_SIZE = 50;

/**
 * One page of the Users screen, plus the unpaged total (ACL-491).
 *
 * There is no "every user" query, on purpose. `GET /app-users` answers one page (50 unless told
 * otherwise), and the hook this replaced asked without paging and showed what came back as the
 * whole list: the 51st person, by email, was on no screen and in no picker. A table pages
 * through here; a picker searches through `useUserPages`.
 */
export function useUserPage(filters: UserFilters = {}, page = 0) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.users({ ...filters, page }),
    queryFn: () =>
      api.getPage<AppUser>(
        `/app-users${qs({ ...filters, limit: USERS_PAGE_SIZE, offset: page * USERS_PAGE_SIZE })}`,
      ),
    placeholderData: (previous) => previous,
  });
}

/**
 * Users a page at a time for the pickers, searched on the server by name or email — the
 * endpoint picker's shape (`useEndpointPages`), for the same reason: filtering what one page
 * loaded cannot find whoever sorts after it.
 */
export function useUserPages(
  filters: Omit<UserFilters, "limit" | "offset"> = {},
  options?: { enabled?: boolean; pageSize?: number },
) {
  const keys = useKeys();
  const pageSize = options?.pageSize ?? PICKER_PAGE_SIZE;
  return useInfiniteQuery({
    queryKey: keys.userPages({ ...filters, limit: pageSize }),
    queryFn: ({ pageParam }) =>
      api.getPage<AppUser>(`/app-users${qs({ ...filters, limit: pageSize, offset: pageParam })}`),
    initialPageParam: 0,
    getNextPageParam: (_last, all) => nextPageOffset(all, pageSize),
    enabled: options?.enabled ?? true,
  });
}

/**
 * Particular people, one read each: the ones a filter has picked but the picker's current page
 * does not hold, so they can still be named and unpicked. Answers in the order asked, leaving out
 * any not read yet.
 */
export function useUsersById(ids: readonly number[], options?: { enabled?: boolean }) {
  const keys = useKeys();
  return useQueries({
    queries: ids.map((id) => ({
      queryKey: keys.user(id),
      queryFn: () => api.get<AppUser>(`/app-users/${id}`),
      enabled: options?.enabled ?? true,
      staleTime: 60_000,
    })),
    combine: (results) => results.flatMap((r) => (r.data ? [r.data] : [])),
  });
}

export interface EndpointFilters {
  [key: string]: QueryValue;
  q?: string;
  domain_id?: number;
  enabled?: boolean;
  assigned?: boolean;
  /** Endpoints free for this user: unassigned, or the one they already hold. Not the same as
   *  `assigned: false`, which would hide their current phone from the dialog that changes it. */
  limit?: number;
  offset?: number;
}

export function useEndpoints(
  filters: EndpointFilters = {},
  options?: Partial<UseQueryOptions<Endpoint[]>>,
) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.endpoints(filters),
    queryFn: () => api.get<Endpoint[]>(`/endpoints${qs(filters)}`),
    // Registration state goes stale quickly — a rep logging in should show up without a manual
    // refresh, but polling every few seconds for a screen nobody is watching is waste.
    refetchInterval: 20_000,
    ...options,
  });
}

/** How many rows a picker asks for at a time. Small enough that the first page is instant,
 *  large enough that a floor of reps rarely needs a second one. */
export const PICKER_PAGE_SIZE = 25;

/**
 * Endpoints a page at a time, for the pickers.
 *
 * Separate from `useEndpoints` rather than replacing it: the endpoints *screen* is a table that
 * wants one flat array and its own refetch button, while a picker wants pages, a total, and no
 * background polling — a list that reorders itself under the cursor while somebody is choosing
 * from it is worse than a slightly stale registration dot.
 */
export function useEndpointPages(
  filters: Omit<EndpointFilters, "limit" | "offset"> = {},
  options?: { enabled?: boolean; pageSize?: number },
) {
  const keys = useKeys();
  const pageSize = options?.pageSize ?? PICKER_PAGE_SIZE;
  return useInfiniteQuery({
    queryKey: keys.endpointPages({ ...filters, limit: pageSize }),
    queryFn: ({ pageParam }) =>
      api.getPage<Endpoint>(`/endpoints${qs({ ...filters, limit: pageSize, offset: pageParam })}`),
    initialPageParam: 0,
    getNextPageParam: (_last, all) => nextPageOffset(all, pageSize),
    enabled: options?.enabled ?? true,
  });
}

export function useDomains(options?: Partial<UseQueryOptions<Domain[]>>) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.domains(),
    queryFn: () => api.get<Domain[]>("/domains"),
    // Domains change about once a quarter; refetching them per screen is pointless.
    staleTime: 5 * 60_000,
    ...options,
  });
}

// --- SIP trunks, and which of them a domain dials out on ---------------------------------------

export function useGateways(options?: Partial<UseQueryOptions<Gateway[]>>) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.gateways(),
    queryFn: () => api.get<Gateway[]>("/gateways"),
    // Trunks change about as often as domains do, and admin+ is the only role that can ask —
    // a 403 for everyone else, which `retry` in App.tsx already knows not to repeat.
    staleTime: 5 * 60_000,
    ...options,
  });
}

/** A trunk write changes what a domain's outbound list resolves to, so both lists refresh. */
function useInvalidateTrunks() {
  const keys = useKeys();
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: keys.gateways() });
    void qc.invalidateQueries({ queryKey: keys.domains() });
  };
}

export interface GatewayInput {
  name: string;
  description?: string | null;
  username: string;
  /** Omitted on edit means "keep the stored one" — the console never reads a carrier password
   *  back just to save the form. */
  password?: string;
  realm: string;
  proxy?: string | null;
  register?: boolean;
  profile_name?: string;
  params?: Record<string, string>;
  enabled?: boolean;
}

export function useCreateGateway() {
  const invalidate = useInvalidateTrunks();
  return useMutation({
    mutationFn: (input: GatewayInput) => api.post<Gateway>("/gateways", input),
    onSuccess: invalidate,
  });
}

export function useUpdateGateway() {
  const invalidate = useInvalidateTrunks();
  return useMutation({
    mutationFn: ({ id, ...input }: Partial<GatewayInput> & { id: number }) =>
      api.put<Gateway>(`/gateways/${id}`, input),
    onSuccess: invalidate,
  });
}

export function useDeleteGateway() {
  const invalidate = useInvalidateTrunks();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/gateways/${id}`),
    onSuccess: invalidate,
  });
}

/** A mutation, not a query, for the reason `useRevealPassword` gives: revealing a carrier
 *  credential is an act, and caching it would leave it in memory after the dialog closed. */
export function useRevealGatewayPassword() {
  return useMutation({
    mutationFn: (id: number) => api.get<GatewaySecret>(`/gateways/${id}/password`),
  });
}

export function useSetDomainOutbound() {
  const invalidate = useInvalidateTrunks();
  return useMutation({
    mutationFn: ({ id, ...routing }: OutboundRouting & { id: number }) =>
      api.put<Domain>(`/domains/${id}/outbound`, routing),
    onSuccess: invalidate,
  });
}

/**
 * Whether a domain's recordings are transcribed. Admin and above, as the route.
 *
 * The saved domain goes straight into the list before the refetch: the switch is drawn from the
 * list, and without it the switch flicks back to the old value for as long as the refetch takes.
 */
export function useSetDomainTranscription() {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, transcribe_recordings }: { id: number; transcribe_recordings: boolean }) =>
      api.put<Domain>(`/domains/${id}/transcription`, { transcribe_recordings }),
    onSuccess: (saved) => {
      qc.setQueryData<Domain[]>(keys.domains(), (list) =>
        list?.map((d) => (d.id === saved.id ? saved : d)),
      );
      void qc.invalidateQueries({ queryKey: keys.domains() });
    },
  });
}

/**
 * How long a domain keeps its recordings and transcripts (`lib/retention.ts`). Admin and above,
 * as the route. The saved domain goes straight into the list, as the transcription switch does.
 */
export function useSetDomainRetention() {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, retention_days }: { id: number; retention_days: number | null }) =>
      api.put<Domain>(`/domains/${id}/retention`, { retention_days }),
    onSuccess: (saved) => {
      qc.setQueryData<Domain[]>(keys.domains(), (list) =>
        list?.map((d) => (d.id === saved.id ? saved : d)),
      );
      void qc.invalidateQueries({ queryKey: keys.domains() });
    },
  });
}

/**
 * How many of a domain's recordings a window of `days` would delete on the sweep's next run.
 * Asked before a shorter window is saved, and never cached: the answer is about to be acted on,
 * and it cannot be undone.
 */
export function useRetentionDue(domainId: number | undefined, days: number, enabled: boolean) {
  const keys = useKeys();
  return useQuery({
    queryKey: [...keys.domains(), domainId, "retention-due", days],
    queryFn: () => api.get<RetentionDue>(`/domains/${domainId}/retention/due${qs({ days })}`),
    enabled: enabled && domainId !== undefined,
    staleTime: 0,
    gcTime: 0,
  });
}

/**
 * The colleagues the signed-in rep can dial.
 *
 * Cached for a while and refetched on focus: it is a staff list, so it changes when somebody is
 * hired and not otherwise — but `registered` on each row is live state, and a picker showing a
 * colleague as online an hour after they closed the tab is worse than one that shows nothing.
 *
 * 404s for an account with no phone, which is not an error worth surfacing: every caller renders
 * this behind the softphone's `available`, and the empty list it falls back to is the truthful
 * answer for somebody who cannot dial at all.
 */
export function useColleagues(options?: Partial<UseQueryOptions<Colleague[]>>) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.colleagues(),
    queryFn: () => api.get<Colleague[]>("/phone/directory"),
    staleTime: 60_000,
    refetchOnWindowFocus: true,
    retry: false,
    ...options,
  });
}

/**
 * GoHighLevel's own numbers (`GET /phone/internal-transfer-numbers`): a call from one is a rep at
 * GHL sending a client on, and the phone says "Internal transfer" rather than GHL's line. The same
 * list Slack's messages use. It changes when an admin edits `.env`, so it is read once a session.
 *
 * An API older than the route answers 404, which is read as an empty list: no call is marked,
 * which is exactly what a phone did before.
 */
export function useInternalTransferNumbers(options?: { enabled?: boolean }) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.internalTransferNumbers(),
    queryFn: async () => {
      try {
        return (await api.get<InternalTransferNumbers>("/phone/internal-transfer-numbers")).numbers;
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) return [];
        throw e;
      }
    },
    staleTime: Infinity,
    retry: 1,
    ...options,
  });
}

export interface MissedCallFilters {
  /** RFC 3339. Inclusive. The last seven days when absent. */
  since?: string;
  limit?: number;
}

/**
 * The calls the signed-in rep's phone did not take (ADR-048 in `platform`): the ones that rang
 * it and went unanswered, the ones the ACD kept for it while it could not be rung, the ones on
 * its own number while it was not registered. One list from the server rather than three from
 * the browser: only the server can say that a ring and the stay it was rung for are one call.
 *
 * An account with no phone gets an empty list, not an error, which is the truthful answer.
 */
export function useMissedCalls(
  filters: MissedCallFilters = {},
  options?: { enabled?: boolean; refetchInterval?: number },
) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.missedCalls({ ...filters }),
    queryFn: () => api.get<MissedCall[]>(`/phone/missed${qs({ ...filters })}`),
    placeholderData: (previous) => previous,
    ...options,
  });
}

/**
 * The room one of this phone's legs is in, or is ringing for (ADR-032): who is on the call,
 * whose phones are ringing for it, whether the caller is on hold. Refetched on the `room` hint,
 * and on a short interval while there is a room to watch. A leg on no room call answers with no
 * room, which is most calls that ring: read once and left alone.
 */
export function useRoomCall(callId: string | null, { enabled = true }: { enabled?: boolean } = {}) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.roomCall(callId ?? "none"),
    queryFn: () => api.get<RoomCall>(`/phone/room/calls/${callId}`),
    enabled: enabled && callId !== null,
    staleTime: 2_000,
    refetchInterval: (query) => (query.state.data?.room ? 3_000 : false),
    retry: false,
  });
}

/**
 * Hold the caller on a direct call, or take them off hold (ADR-032). Never the phone's own hold,
 * which would hold the rep's leg and leave the caller hearing nobody: the switch holds the caller
 * in the room, on music. A 202: the roster's own flag says once it has.
 */
export function useRoomHold() {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ callId, held }: { callId: string; held: boolean }) =>
      api.post<RoomCommands>("/phone/room/hold", { call_id: callId, held }),
    onSuccess: () => {
      setTimeout(() => void qc.invalidateQueries({ queryKey: keys.scoped(["phone", "room"]) }), 800);
    },
  });
}

/** Bring a colleague into a direct call: to talk first while the caller waits on hold
 *  (`consult`), or straight onto the call (`add`). A 202: their phone is ringing. */
export function useInviteToRoom() {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ callId, extension, mode }: { callId: string; extension: string; mode: InviteMode }) =>
      api.post<RoomInviteResult>("/phone/room/invite", { call_id: callId, extension, mode }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.scoped(["phone", "room"]) });
    },
  });
}

/** Finish a briefing on a direct call (ADR-032). `cancel` names the colleague's leg. */
export function useEndRoomConsult() {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      callId, outcome, withChannelId,
    }: { callId: string; outcome: ConsultOutcome; withChannelId?: string | null }) =>
      api.post<RoomCommands>("/phone/room/consult/end", {
        call_id: callId,
        outcome,
        with_channel_id: withChannelId ?? null,
      }),
    onSuccess: () => {
      setTimeout(() => void qc.invalidateQueries({ queryKey: keys.scoped(["phone", "room"]) }), 800);
    },
  });
}

/** Bring an outside number into a room call, to talk first (ADR-065): a speed dial or a number. */
export function useAddOutsideParticipant() {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      callId, speedDialId, number,
    }: { callId: string; speedDialId?: number | null; number?: string | null }) =>
      api.post<OutsideParticipantResult>("/phone/room/outside", {
        call_id: callId,
        speed_dial_id: speedDialId ?? null,
        number: number ?? null,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.scoped(["phone", "room"]) });
    },
  });
}

/** Finish the briefing with an outside participant (ADR-065). `channelId` is their leg. */
export function useEndOutsideParticipant() {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      callId, channelId, outcome,
    }: { callId: string; channelId: string; outcome: ConsultOutcome }) =>
      api.post<RoomCommands>("/phone/room/outside/end", {
        call_id: callId,
        channel_id: channelId,
        outcome,
      }),
    onSuccess: () => {
      setTimeout(() => void qc.invalidateQueries({ queryKey: keys.scoped(["phone", "room"]) }), 800);
    },
  });
}

export function useSpeedDials(options?: Partial<UseQueryOptions<SpeedDial[]>>) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.speedDials(),
    queryFn: () => api.get<SpeedDial[]>("/speed-dials"),
    staleTime: 60_000,
    ...options,
  });
}

export interface SpeedDialInput {
  label: string;
  number: string;
  sort_order?: number;
  tenant_id?: number;
}

function useInvalidateSpeedDials() {
  const keys = useKeys();
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: keys.speedDials() });
}

export function useCreateSpeedDial() {
  const invalidate = useInvalidateSpeedDials();
  return useMutation({
    mutationFn: (input: SpeedDialInput) => api.post<SpeedDial>("/speed-dials", input),
    onSuccess: invalidate,
  });
}

export function useUpdateSpeedDial() {
  const invalidate = useInvalidateSpeedDials();
  return useMutation({
    mutationFn: ({ id, ...input }: Partial<SpeedDialInput> & { id: number }) =>
      api.put<SpeedDial>(`/speed-dials/${id}`, input),
    onSuccess: invalidate,
  });
}

export function useDeleteSpeedDial() {
  const invalidate = useInvalidateSpeedDials();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/speed-dials/${id}`),
    onSuccess: invalidate,
  });
}

export function useTenants(options?: Partial<UseQueryOptions<Tenant[]>>) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.tenants(),
    queryFn: () => api.get<Tenant[]>("/tenants"),
    staleTime: 5 * 60_000,
    ...options,
  });
}

/** Invalidate everything a user write can affect. Assigning an endpoint changes both lists. */
export function useInvalidateIdentity() {
  const keys = useKeys();
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: keys.scoped(["users"]) });
    void qc.invalidateQueries({ queryKey: keys.scoped(["endpoints"]) });
    // Hiring somebody adds a row to every rep's dial picker, and renaming an extension moves
    // one. Neither is under the two prefixes above — `/phone/directory` is its own route.
    void qc.invalidateQueries({ queryKey: keys.colleagues() });
  };
}

export interface UserInput {
  email: string;
  display_name?: string | null;
  role: string;
  tenant_id?: number | null;
  domain_id?: number | null;
  /** Four digits. On create, omitted means "allocate the lowest free one"; on update it renames
   *  the phone. There is no `sip_endpoint_id` — an account's endpoint is made with it and never
   *  moves, so the console has nothing to choose. */
  extension?: string;
  /** Where to put the phone of an admin or superadmin, who span domains rather than living in
   *  one. Meaningless for a supervisor or rep, whose phone lives in their own domain. */
  endpoint_domain_id?: number | null;
  record_calls?: boolean;
  is_active?: boolean;
  password?: string;
  /** The voice that speaks in their name (ADR-114 in `platform`). `null` is the tenant's default. */
  voice_gender?: "female" | "male" | null;
}

export function useCreateUser() {
  const invalidate = useInvalidateIdentity();
  return useMutation({
    mutationFn: (input: UserInput) => api.post<AppUser>("/app-users", input),
    onSuccess: invalidate,
  });
}

export function useUpdateUser() {
  const invalidate = useInvalidateIdentity();
  return useMutation({
    mutationFn: ({ id, ...input }: Partial<UserInput> & { id: number }) =>
      api.put<AppUser>(`/app-users/${id}`, input),
    onSuccess: invalidate,
  });
}

export function useDeleteUser() {
  const invalidate = useInvalidateIdentity();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/app-users/${id}`),
    onSuccess: invalidate,
  });
}

// --- invitations (ADR-028) ----------------------------------------------------------------------

/** Every invitation the admin may see, newest first, whatever its status. Admin-and-above. */
export function useInvitations(options?: Partial<UseQueryOptions<Invitation[]>>) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.invitations(),
    queryFn: () => api.get<Invitation[]>("/invitations"),
    ...options,
  });
}

/** What joining makes of somebody. No password and no extension: the person chooses the first,
 *  and their phone gets the lowest free number when they accept. */
export interface InvitationTermsInput {
  role: string;
  domain_id?: number | null;
  endpoint_domain_id?: number | null;
  campaign_ids?: string[];
  skills?: UserSkill[];
}

/** Who somebody will be once they accept. */
export interface InvitationInput extends InvitationTermsInput {
  email: string;
  /** Their real name (ADR-172 in `platform`), and the one they go by. */
  name?: string | null;
  display_name?: string | null;
}

/** Up to 20 pasted addresses or 100 CSV rows. A person may carry any of the batch's terms as
 *  their own; what they leave out is the batch's. */
export interface InvitationBulkInput extends InvitationTermsInput {
  source?: "paste" | "csv";
  people: (Partial<InvitationTermsInput> & {
    email: string;
    display_name: string | null;
    /** Their own number (ACL-176): held now, and ringing them on their first campaign once they
     *  accept. A person's own, never the batch's. */
    number?: string;
  })[];
}

export function useCreateInvitation() {
  const invalidate = useInvalidateIdentity();
  return useMutation({
    mutationFn: (input: InvitationInput) => api.post<InvitationSent>("/invitations", input),
    onSuccess: invalidate,
  });
}

/** A one-time link that signs another browser in as this user (ADR-040). Changes nothing shown. */
export function useMakeSignInLink() {
  return useMutation({
    mutationFn: (userId: number) => api.post<SignInLinkMade>(`/app-users/${userId}/sign-in-link`),
  });
}

/** Everybody on the list is invited but an address already taken, which the answer says why of. */
export function useCreateInvitations() {
  const invalidate = useInvalidateIdentity();
  return useMutation({
    mutationFn: (input: InvitationBulkInput) =>
      api.post<InvitationBulkSent>("/invitations/bulk", input),
    onSuccess: invalidate,
  });
}

/** A new link and a fresh 72 hours. The old link stops working. */
export function useResendInvitation() {
  const invalidate = useInvalidateIdentity();
  return useMutation({
    mutationFn: (id: number) => api.post<InvitationSent>(`/invitations/${id}/resend`),
    onSuccess: invalidate,
  });
}

export function useDeleteInvitation() {
  const invalidate = useInvalidateIdentity();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/invitations/${id}`),
    onSuccess: invalidate,
  });
}


export function useDeleteInvitations() {
  const invalidate = useInvalidateIdentity();
  return useMutation({
    mutationFn: (ids: number[]) => api.post<void>("/invitations/bulk-delete", { ids }),
    onSuccess: invalidate,
  });
}


export interface EndpointInput {
  extension: string;
  domain_id: number;
  password?: string | null;
  display_name?: string | null;
  caller_id_number?: string | null;
  number_alias?: string | null;
  enabled?: boolean;
  record_calls?: boolean;
}

/**
 * The recording of one call leg, or `null` when there is none.
 *
 * A list query filtered to one `call_id` rather than `GET /recordings/{id}`, because "there is
 * no recording" is the common answer — the toggle is off, the call was never answered, the
 * sidecar has not shipped it yet — and a 404 per un-recorded call would make every row of call
 * history log an error in the console.
 */
export function useCallRecording(callId: string | undefined) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.recording(callId),
    enabled: Boolean(callId),
    queryFn: async () => {
      const rows = await api.get<Recording[]>(`/recordings${qs({ call_id: callId })}`);
      return rows[0] ?? null;
    },
    // A recording arrives seconds to minutes after the call, so a dialog opened right after
    // hangup would otherwise cache "none" for the session.
    staleTime: 30_000,
  });
}

/**
 * The stored transcript of one call, or `null` when there is none to read.
 *
 * A 404 means "not a call, or a recording, you can see", which for the panel's purposes is the
 * same as no transcript — the row it was opened from came from a scoped list — so it collapses to
 * `null` rather than an error state.
 *
 * `enabled` is the caller's, deliberately: the panel asks only when the call row says there is a
 * transcript or there is a recording a pass could have been run on, so a call with neither does
 * not spend a request finding that out again.
 *
 * It polls while the whole-file job is pending, and when a job it watched finishes it refetches
 * the call tables: `has_transcript` on the row the dialog was opened from is what flips, and the
 * lists do not poll.
 */
export function useCallTranscript(callId: string | undefined, enabled = true) {
  const keys = useKeys();
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: keys.transcript(callId),
    enabled: Boolean(callId) && enabled,
    queryFn: async () => {
      try {
        return await api.get<CallTranscript>(`/calls/${encodeURIComponent(callId!)}/transcript`);
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) return null;
        throw e;
      }
    },
    refetchInterval: (q) => transcriptPollInterval(q.state.data),
    // As the recording's, for the same reason: the job is created when the file lands, which is
    // after the call, so a dialog opened right after hangup must not cache "nothing" for long.
    staleTime: 30_000,
  });

  const pending = query.data?.recording_status === "pending";
  const watched = useRef(false);
  useEffect(() => {
    if (watched.current && !pending) void qc.invalidateQueries({ queryKey: keys.scoped(["calls"]) });
    watched.current = pending;
  }, [pending, qc, keys]);

  return query;
}

/**
 * Ask for a whole-file pass over a call's recording. Supervisor and above, as the route.
 *
 * 202 means queued, not done, so this refetches the transcript — which now reads `pending` and
 * polls — and the recording row, whose `transcript_status` says the same.
 */
/**
 * What a call was about (ACL-399), or null when nobody has asked yet.
 *
 * Named `written`, not `summary`: `useCallSummary` is the call *statistics* for a filter, and two
 * hooks a letter apart over different things is a bug waiting to be imported.
 *
 * Null is the ordinary state of every call, so the API answers it rather than 404 and this does
 * not treat it as an error. A deployment with no provider configured never writes one, so this
 * stays null there for ever — which is what keeps the button off those consoles.
 */
export function useWrittenSummary(callId: string | undefined, enabled = true) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.writtenSummary(callId),
    enabled: Boolean(callId) && enabled,
    queryFn: () =>
      api.get<WrittenCallSummary | null>(`/calls/${encodeURIComponent(callId!)}/summary`),
    staleTime: 5 * 60_000,
  });
}

/**
 * Write this call's summary, replacing any it already has.
 *
 * The answer is the summary itself, so it is put straight into the cache: the button is a few
 * seconds of waiting and a refetch afterwards would be a second wait for something already in
 * hand.
 */
export function useWriteCallSummary() {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ callId }: { callId: string }) =>
      api.post<WrittenCallSummary>(`/calls/${encodeURIComponent(callId)}/summary`, {}),
    onSuccess: (summary, { callId }) => qc.setQueryData(keys.writtenSummary(callId), summary),
  });
}

export function useTranscribeCall() {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ callId, language }: { callId: string; language?: "en" | "es" }) =>
      api.post<TranscribeAccepted>(
        `/calls/${encodeURIComponent(callId)}/transcribe`,
        language ? { language } : {},
      ),
    onSuccess: (_accepted, { callId }) => {
      void qc.invalidateQueries({ queryKey: keys.transcript(callId) });
      void qc.invalidateQueries({ queryKey: keys.recording(callId) });
    },
  });
}

/**
 * Put a call on legal hold, and take the hold off (`lib/recording-hold.ts`). Admin and above, as
 * the routes. The answer is the recording as it now stands, so it goes straight into the query
 * the call's details read: the row flips without waiting for a refetch.
 *
 * `callId` is the call the dialog was opened on, which is what that query is keyed by. It is not
 * always the recording's own id (a bridged call has one file and several legs), so the cache is
 * set under the id that was asked with, not the one that came back.
 */
export function useHoldRecording() {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ callId, reason }: { callId: string; reason: string }) =>
      api.put<Recording>(`/recordings/${encodeURIComponent(callId)}/hold`, { reason }),
    onSuccess: (saved, { callId }) => {
      qc.setQueryData(keys.recording(callId), saved);
    },
  });
}

export function useReleaseRecordingHold() {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ callId }: { callId: string }) =>
      api.delete<Recording>(`/recordings/${encodeURIComponent(callId)}/hold`),
    onSuccess: (saved, { callId }) => {
      qc.setQueryData(keys.recording(callId), saved);
    },
  });
}

export function useCreateEndpoint() {
  const invalidate = useInvalidateIdentity();
  return useMutation({
    mutationFn: (input: EndpointInput) => api.post<Endpoint>("/endpoints", input),
    onSuccess: invalidate,
  });
}

export function useUpdateEndpoint() {
  const invalidate = useInvalidateIdentity();
  return useMutation({
    mutationFn: ({ id, ...input }: Partial<EndpointInput> & { id: number }) =>
      api.put<Endpoint>(`/endpoints/${id}`, input),
    onSuccess: invalidate,
  });
}

export function useDeleteEndpoint() {
  const invalidate = useInvalidateIdentity();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/endpoints/${id}`),
    onSuccess: invalidate,
  });
}

/** Deliberately a mutation, not a query: revealing a credential is an act, and caching it in
 *  the query client would leave the password sitting in memory after the dialog closed. */
export function useRevealPassword() {
  return useMutation({
    mutationFn: (id: number) => api.get<EndpointSecret>(`/endpoints/${id}/password`),
  });
}

export function useRotatePassword() {
  const invalidate = useInvalidateIdentity();
  return useMutation({
    mutationFn: (id: number) => api.post<EndpointSecret>(`/endpoints/${id}/rotate-password`),
    onSuccess: invalidate,
  });
}

/**
 * The two things about their own phone a person may change.
 *
 * Not *which* phone: there is no route for that anywhere. Calls are matched to a rep by the
 * numbers their endpoint answers to, so an endpoint that changed hands would hand its new holder
 * the last holder's history — see `provisioning.py` in core-api.
 */
export function useUpdateOwnEndpoint() {
  const invalidate = useInvalidateIdentity();
  return useMutation({
    mutationFn: (input: { extension?: string; record_calls?: boolean }) =>
      api.put<AppUser>("/auth/me/endpoint", input),
    onSuccess: invalidate,
  });
}

/**
 * Choose the voice that speaks in your own name (ADR-114 in `platform`): the female one, the male
 * one, or `null` for the tenant's default. The account is read again afterwards, so the page shows
 * what was saved.
 */
export function useSetOwnVoice() {
  const invalidate = useInvalidateIdentity();
  const { reload } = useAuth();
  return useMutation({
    mutationFn: (gender: "female" | "male" | null) => api.put<AppUser>("/auth/me/voice", { gender }),
    onSuccess: () => {
      invalidate();
      void reload().catch(() => undefined);
    },
  });
}

/** The name you go by (ADR-172 in `platform`). Null goes back to your real name. */
export function useSetOwnDisplayName() {
  const invalidate = useInvalidateIdentity();
  const { reload } = useAuth();
  return useMutation({
    mutationFn: (display_name: string | null) =>
      api.put<AppUser>("/auth/me/display-name", { display_name }),
    onSuccess: () => {
      invalidate();
      void reload().catch(() => undefined);
    },
  });
}

export function useChangeOwnPassword() {
  return useMutation({
    mutationFn: (input: { current_password: string; new_password: string }) =>
      api.put<void>("/auth/me/password", input),
  });
}

/**
 * Set or replace your own profile picture (ADR-079 in `platform`). The account is read again
 * afterwards, so the menu's avatar changes at once, and the people lists refetch so theirs does too.
 * A failed reload does not make the upload look failed: the picture is saved either way.
 */
export function useSetOwnAvatar() {
  const invalidate = useInvalidateIdentity();
  const { reload } = useAuth();
  return useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append("file", file);
      return api.put<AppUser>("/auth/me/avatar", form);
    },
    onSuccess: () => {
      invalidate();
      void reload().catch(() => undefined);
    },
  });
}

/**
 * A profile picture as an object URL an `<img>` can show, or `undefined` while it loads, when there
 * is none, or when it cannot be fetched. The avatar shows initials in all three cases.
 *
 * Fetched with the session, not linked. core-api authorizes with a bearer header this console keeps
 * away from the DOM, so an `<img src>` pointed at the API would arrive unauthenticated, the way the
 * recording player's file would (`fetchFile`). The picture's path names one picture for good, so it
 * is fetched once per session and never refetched; a new picture comes with a new path. Each
 * caller makes its own object URL from the shared blob and releases it when it unmounts.
 */
export function useAvatarImage(path: string | null | undefined): string | undefined {
  const keys = useKeys();
  const { data: blob } = useQuery({
    queryKey: keys.scoped(["avatars", path ?? "none"]),
    queryFn: async () => (await fetchFile(path as string)).blob,
    enabled: Boolean(path),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    // A picture that will not load shows initials. A retry would mostly re-ask for one that has
    // since been replaced, and the next read of the account brings its new path anyway.
    retry: false,
  });
  const [url, setUrl] = useState<string>();
  useEffect(() => {
    if (!blob) {
      setUrl(undefined);
      return;
    }
    const next = URL.createObjectURL(blob);
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [blob]);
  return url;
}


/**
 * One picture a text carried (ADR-140), as an object URL a `<img src>` can use.
 *
 * Fetched with the session's token, like a recording and a profile picture: the media route is
 * scoped to the conversation, so a plain `<img src>` pointed at it would arrive without one and
 * get a 401. The address names one picture for good — a key is never reused — so it is fetched
 * once per session and the blob is shared between callers (a thumbnail and the lightbox showing
 * the same picture fetch it once).
 */
export function useMessageMedia(
  conversationId: string | null | undefined,
  mediaId: string | null | undefined,
  enabled = true,
): string | undefined {
  const keys = useKeys();
  const path = conversationId && mediaId ? `/conversations/${conversationId}/media/${mediaId}` : null;
  const { data: blob } = useQuery({
    queryKey: keys.scoped(["message-media", mediaId ?? "none"]),
    queryFn: async () => (await fetchFile(path as string)).blob,
    enabled: Boolean(path) && enabled,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    // A picture that will not load shows its placeholder. Retrying would mostly re-ask for one
    // the store does not have.
    retry: false,
  });
  const [url, setUrl] = useState<string>();
  useEffect(() => {
    if (!blob) {
      setUrl(undefined);
      return;
    }
    const next = URL.createObjectURL(blob);
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [blob]);
  return url;
}

// --- the ACD: taking calls ---------------------------------------------------------------------

/**
 * The signed-in rep's lines and live ACD state.
 *
 * Refetched on the `queue` hint (`lib/live-hints.tsx`) the moment the event-tracker records a
 * change, and every 30 seconds besides, for a hint stream that is down. Not retried: the one
 * refusal is a 409 for an account with no phone, which no retry changes.
 */
export function useAcdMe(options?: Partial<UseQueryOptions<AgentState>>) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.acdMe(),
    queryFn: () => api.get<AgentState>("/acd/me"),
    staleTime: 15_000,
    refetchInterval: 30_000,
    retry: false,
    ...options,
  });
}

/**
 * Ready, a break, back, or sign out — for the caller's own phone, as the steps `availability()`
 * gives: each one command, on one line or on all of them. Sent one at a time and in order, and
 * stopped at the first the server refuses, whose error is the mutation's.
 *
 * Each answer is a 202: the command is on the bus and the ACD applies it after. The state is
 * refetched when the `queue` hint says it changed; the delayed refetch here covers a hint that
 * never comes. It follows a refusal too: the steps before it went out, and a line refused with a
 * 422 is one the rep no longer staffs, which a fresh `/acd/me` stops offering.
 */
export function useAgentAction() {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (steps: readonly AgentStep[]) => {
      for (const { action, queue, code, ownCallsOnly, ownCallsNext } of steps) {
        await api.post<AgentActionResponse>(`/acd/agent/${action}`, {
          queue,
          code: code ?? null,
          // Left out, a login keeps whether the rep takes only their own calls (ADR-037), and
          // whether only until their next call (ADR-070).
          ...(ownCallsOnly === undefined ? {} : { own_calls_only: ownCallsOnly }),
          ...(ownCallsNext === undefined ? {} : { own_calls_next: ownCallsNext }),
        });
      }
    },
    onSettled: () => {
      setTimeout(() => void qc.invalidateQueries({ queryKey: keys.scoped(["acd"]) }), 1_500);
    },
  });
}

/**
 * A handoff, for the closer it rings: who sent the caller, and their note (ADR-016).
 *
 * `watch` follows one the rep asked for and is waiting on — a consult while the ACD looks for a
 * closer (ADR-027) — where the status changes without anything the phone does.
 */
export function useHandoff(id: string | null, { watch = false }: { watch?: boolean } = {}) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.handoff(id ?? "none"),
    queryFn: () => api.get<Handoff>(`/handoffs/${id}`),
    enabled: id !== null,
    staleTime: watch ? 0 : 60_000,
    refetchInterval: watch ? 2_000 : false,
    retry: false,
  });
}

/**
 * A lead's card, for the call the ACD rang this phone for (ADR-024), read once per `stay`: the
 * caller's queue session. Not retried: a 404 means this rep may not read the lead, which no retry
 * changes, and the card simply does not show.
 */
/**
 * The lead's card as the phone already holds it by the lead's id (`useLeadCard(id, null)`, which the
 * Phone page reads for an offered lead), from the cache only: it asks for nothing. The dialer offer's
 * "Seen before" line takes the other records with the phone from it when it is there (ACL-409), and
 * reads no card of its own: `GET /leads/{id}` is core-api's costliest read on a dialing floor (ACL-390).
 */
export function useHeldLeadCard(id: string | null) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.leadCard(id ?? "none", "none"),
    queryFn: () => api.get<LeadCard>(`/leads/${id}`),
    enabled: false,
  });
}

export function useLeadCard(id: string | null, stay: string | null) {
  const keys = useKeys();
  const client = useQueryClient();
  return useQuery({
    queryKey: keys.leadCard(id ?? "none", stay ?? "none"),
    queryFn: () => api.get<LeadCard>(`/leads/${id}`),
    enabled: id !== null,
    staleTime: 60_000,
    retry: false,
    // The same lead's card as it was read before this stay had a name (the Phone page shows it
    // under the dial, by the lead's id) stands in until this stay's own read lands, so the card
    // does not blink out at the moment the call connects.
    placeholderData: () =>
      id !== null && stay !== null
        ? client.getQueryData<LeadCard>(keys.leadCard(id, "none"))
        : undefined,
  });
}

/**
 * The carrier's name for the unknown caller on the rep's phone (CNAM, ACL-286), asked once per call
 * while it rings or is up. A hint, not an identity: no name is the usual answer, and a failure is the
 * same as none, never an error on the call screen.
 */
export function useCallerName(callerChannelId: string | null) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.callerName(callerChannelId ?? "none"),
    queryFn: () => api.get<CallerNameResponse>(`/acd/calls/${callerChannelId}/caller-name`),
    enabled: callerChannelId !== null,
    staleTime: Infinity,
    retry: false,
  });
}

/**
 * Who the caller the rep is talking to can be sent to (ADR-031): the closers on the line a handoff
 * of that call would wait in, with the ACD's live state of each, and the skills to narrow them by.
 * Polled while the picker is open, since a closer coming free is exactly what the setter is
 * watching for and a hint can be slow; only for a call the rep is on, which is all core-api answers.
 */
export function useHandoffClosers(callId: string | null, { enabled = true }: { enabled?: boolean } = {}) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.handoffClosers(callId ?? "none"),
    queryFn: () => api.get<HandoffClosers>(`/handoffs/closers${qs({ call_id: callId })}`),
    enabled: enabled && callId !== null,
    staleTime: 5_000,
    refetchInterval: 5_000,
    retry: false,
  });
}

/**
 * Send the caller the rep is talking to, to a closer (ADR-016) — any closer free, or one the rep
 * named (ADR-031). A 202: the request is filed and on the bus, and the ACD takes the caller off this
 * rep when it applies it — the call ending is the confirmation. `handoffRequest` builds the body.
 */
export function useSendToCloser() {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ask: HandoffAsk) => api.post<Handoff>("/handoffs", handoffRequest(ask)),
    onSuccess: () => {
      setTimeout(() => void qc.invalidateQueries({ queryKey: keys.scoped(["acd"]) }), 1_500);
    },
  });
}

export type { HandoffMode } from "@/lib/handoff";

/** How a consult ends: the closer takes the caller (`complete`), the setter takes them back
 *  (`cancel`), or all three stay on the call (`join`, ADR-029). */
export type ConsultEnding = "complete" | "cancel" | "join";

/**
 * Finish a consult the rep is in the middle of: hand the client over, take them back, or stay on
 * the call with both of them.
 *
 * A 202 like the request itself — the ACD does it, and the rep's own record and their call are the
 * confirmation. Only the rep who asked may, which core-api and the ACD both check.
 */
export function useFinishConsult() {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ handoffId, action }: { handoffId: string; action: ConsultEnding }) =>
      api.post<Handoff>(`/handoffs/${handoffId}/${action}`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.scoped(["acd"]) });
      setTimeout(() => void qc.invalidateQueries({ queryKey: keys.scoped(["acd"]) }), 1_500);
    },
  });
}

/**
 * Hold the caller on a call the ACD connected, or take them off hold (ADR-029).
 *
 * Such a call lives in a conference room, where the phone's own hold would hold the rep's leg and
 * leave the caller listening to nobody, so the ACD plays the caller their line's music in the room
 * instead. A 202: the rep's record says once it has (`callerHeld`), which the `queue` hint
 * refetches; the refetches here cover a hint that is slow to arrive.
 */
export function useCallHold() {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ callerChannelId, held }: { callerChannelId: string; held: boolean }) =>
      api.post<CallHoldResponse>(`/acd/calls/${callerChannelId}/hold`, { held }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.scoped(["acd"]) });
      setTimeout(() => void qc.invalidateQueries({ queryKey: keys.scoped(["acd"]) }), 1_500);
    },
  });
}

/**
 * Name the contact an unknown inbound caller is from (ACL-285): the one the rep just made for them,
 * or the record with their number. The call's history names the lead from here; the rep's record
 * says so once the event-tracker has it (`leadId`), which the `queue` hint refetches, and the phone
 * shows the lead card in the place of "No record".
 */
export function useLinkCallToLead() {
  const keys = useKeys();
  const qc = useQueryClient();
  const invalidateContacts = useInvalidateContacts();
  return useMutation({
    mutationFn: ({ callerChannelId, leadId }: { callerChannelId: string; leadId: string }) =>
      api.post<CallLeadResponse>(`/acd/calls/${callerChannelId}/lead`, { lead_id: leadId }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.scoped(["acd"]) });
      setTimeout(() => void qc.invalidateQueries({ queryKey: keys.scoped(["acd"]) }), 1_500);
      invalidateContacts();
    },
  });
}

/**
 * Listen in on a rep's call from your own phone (ADR-029). The ACD rings the supervisor's phone into
 * the call's room, muted; the answer's `monitor_id` is the id that call rings with
 * (`rememberMonitor`). Asked for by the caller's leg, which is how the floor names a rep's call.
 */
export function useStartMonitor() {
  return useMutation({
    mutationFn: (callerChannelId: string) =>
      api.post<MonitorResponse>(`/acd/calls/${callerChannelId}/monitor`, {}),
  });
}

/** Listen, whisper to the reps, or barge in, on a call the supervisor is listening in on. */
export function useMonitorMode() {
  return useMutation({
    mutationFn: ({ monitorId, mode }: { monitorId: string; mode: MonitorMode }) =>
      api.put<MonitorResponse>(`/acd/monitors/${monitorId}`, { mode }),
  });
}

/**
 * The rep's own newest queue sessions, where the disposition window finds its call. Asked for by
 * extension, because a supervisor's `/acd/sessions` is the whole tenant's; `pendingDisposition`
 * matches the realm as well.
 */
export function useMyAcdSessions(agent: { user: string; domain: string } | null) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.acdSessions(agent?.user ?? "none"),
    queryFn: () =>
      api.get<QueueSession[]>(`/acd/sessions${qs({ agent_user: agent?.user, limit: 5 })}`),
    enabled: agent !== null,
    retry: false,
  });
}

/** What a rep on a campaign's call chooses from: its pick, or the tenant's whole catalog. */
/** The outcomes the signed-in person recorded on their own calls this week, most used first, on
 *  one campaign when it is named (`GET /acd/me/outcomes`, ADR-160): the window's suggested row. */
export function useMyOutcomes(campaignId: string | null, enabled = true) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.myOutcomes(campaignId),
    queryFn: () =>
      api.get<{ slug: string; count: number }[]>(`/acd/me/outcomes${qs({ campaign_id: campaignId })}`),
    enabled,
    staleTime: 5 * 60_000,
    retry: false,
  });
}

export function useCallDispositions(campaignId: string | null, enabled = true) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.callDispositions(campaignId),
    queryFn: () => api.get<Disposition[]>(`/dispositions${qs({ campaign_id: campaignId })}`),
    enabled,
    staleTime: 5 * 60_000,
  });
}

/**
 * The outcome of a call the rep handled (ADR-017). A 202: the ACD closes the rep's window when the
 * event reaches it, and the `queue` hint refetches the state; the delayed refetch covers a hint
 * that never comes.
 */
export function useDispositionCall() {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      callId, disposition, note, callbackAt, callbackPurpose, callbackBackupTimes, leadId,
    }: {
      callId: string;
      disposition: string;
      note?: string;
      /** The case the call was about (ADR-223): another record of the caller's person. */
      leadId?: string | null;
      /** A callback's time, RFC 3339 with its offset (ADR-080). */
      callbackAt?: string | null;
      /** What the callback is for (ADR-162): a few words. */
      callbackPurpose?: string | null;
      /** Its backup times in order, for when the call before each reaches nobody (ACL-559): only with
       *  a time. */
      callbackBackupTimes?: readonly string[];
    }) =>
      api.post<CallDispositionResponse>(`/acd/calls/${callId}/disposition`, {
        disposition,
        ...(leadId ? { lead_id: leadId } : {}),
        note: note?.trim() || null,
        ...(callbackAt ? { callback_at: callbackAt } : {}),
        ...(callbackPurpose ? { callback_purpose: callbackPurpose } : {}),
        ...(callbackAt ? backupFields(callbackBackupTimes) : {}),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.scoped(["acd", "sessions"]) });
      setTimeout(() => void qc.invalidateQueries({ queryKey: keys.scoped(["acd"]) }), 1_500);
    },
  });
}

// --- the live floor -------------------------------------------------------------------------------

/**
 * Every rep and line in the supervisor's reach, live. Refetched on the `queue` hint as reps and
 * callers move, and every 10 seconds besides for a hint stream that is down. The previous floor
 * stays on screen while the next loads, so a refetch never flashes the tables empty.
 */
export function useAcdFloor(options?: { enabled?: boolean }) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.acdFloor(),
    queryFn: () => api.get<AcdFloor>("/acd/floor"),
    refetchInterval: 10_000,
    placeholderData: (previous) => previous,
    enabled: options?.enabled ?? true,
  });
}

/**
 * Sign another rep out of the ACD (ADR-052 in `platform`), for a supervisor watching the floor:
 * the rep who went home with their phone still on it, or whose suspension is not going to time out
 * because the ACD was not running when their tab closed.
 *
 * A 202, like the rep's own Sign out: the ACD takes them off their lines after, and one on a call
 * is signed out when the call ends. The floor follows on the `queue` hint; the delayed refetch
 * covers a hint that never comes.
 */
export function useSignOutRep() {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ user, domain }: { user: string; domain: string }) =>
      api.post<AgentActionResponse>(
        `/acd/floor/agents/${encodeURIComponent(domain)}/${encodeURIComponent(user)}/sign-out`,
        {},
      ),
    onSettled: () => {
      setTimeout(() => void qc.invalidateQueries({ queryKey: keys.scoped(["acd"]) }), 1_500);
    },
  });
}

export interface AcdStatsFilters {
  [key: string]: QueryValue;
  queue?: string;
  /** RFC 3339, minute-rounded by the caller so the key holds still between renders. */
  since?: string;
  sla_seconds?: number;
}

/** Counts over a window from the queue history. Every 30 seconds, as tap-ucaas's monitor does:
 *  history changes slowly, and nothing about it needs the hint stream's cadence. */
export function useAcdStats(filters: AcdStatsFilters, options?: { enabled?: boolean }) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.acdStats(filters),
    queryFn: () => api.get<AcdStats>(`/acd/stats${qs(filters)}`),
    refetchInterval: 30_000,
    placeholderData: (previous) => previous,
    enabled: options?.enabled ?? true,
  });
}

// --- reports (ADR-074) ----------------------------------------------------------------------------

export type ReportKind = "lines" | "dialing" | "outcomes" | "lists" | "leads" | "reps";

/**
 * One report over one filter. The previous answer stays on screen while a new filter loads — a
 * chart that blanks on every change is a chart nobody can compare. Nothing polls: a window that
 * ends "now" moves on the minute, which is a new query, and one that ended yesterday is final.
 */
export function useReport<T>(kind: ReportKind, query: string, options?: { enabled?: boolean }) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.report(kind, query),
    queryFn: () => api.get<T>(`/reports/${kind}${query}`),
    placeholderData: (previous) => previous,
    staleTime: 30_000,
    enabled: options?.enabled ?? true,
  });
}

/** What the filters offer. It changes when an admin edits a campaign, not while a report is read. */
export function useReportOptions() {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.reportOptions(),
    queryFn: () => api.get<ReportOptions>("/reports/options"),
    staleTime: 5 * 60_000,
  });
}

export function useUiPrefs() {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.uiPrefs(),
    queryFn: () => api.get<Record<string, unknown>>("/auth/me/ui-prefs"),
    staleTime: Infinity,
  });
}

/** A top-level-key merge: send only the keys that changed; `null` deletes one. */
export function useSaveUiPrefs() {
  const keys = useKeys();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (prefs: Record<string, unknown>) =>
      api.put<Record<string, unknown>>("/auth/me/ui-prefs", { prefs }),
    onSuccess: (merged) => client.setQueryData(keys.uiPrefs(), merged),
  });
}

// --- the dialer on the rep's phone (ADR-019) -----------------------------------------------------

/**
 * The lead the dialer offers this phone, or null. Refetched on the `dialer` hint the moment an
 * offer is made, taken or released, and every 15 seconds besides: an offer lasts about a minute,
 * and a hint stream that is down must not cost the rep one.
 */
export function useDialerOffer(options?: { enabled?: boolean }) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.dialerOffer(),
    queryFn: () => api.get<DialerOffer | null>("/dialer/offer"),
    refetchInterval: 15_000,
    retry: false,
    enabled: options?.enabled ?? true,
  });
}

/** The lead the rep dialed from an offer, and when. The offer is gone from the server by the time
 *  the dialer is calling them, and the phone still names them (`recentDialedLead`). */
export interface DialedLead {
  offer: DialerOffer;
  at: number;
}

/** Dial the offer, or pass on it. A 202: the dialer checks the offer again, and it can have ended
 *  in between — the `dialer` hint says what happened. */
export function useOfferAction() {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ action, offer }: { action: "dial" | "skip"; offer: DialerOffer }) =>
      api.post<DialerActionResponse>(`/dialer/offer/${action}`, {
        campaign_lead_id: offer.campaign_lead_id,
      }),
    onSuccess: (_response, { action, offer }) => {
      if (action === "dial") {
        qc.setQueryData<DialedLead>(keys.dialedLead(), { offer, at: Date.now() });
      }
      setTimeout(() => {
        void qc.invalidateQueries({ queryKey: keys.scoped(["dialer"]) });
        void qc.invalidateQueries({ queryKey: keys.scoped(["acd"]) });
      }, 1_500);
    },
  });
}

/**
 * No more redials of the lead being called for this phone (ADR-105 in `platform`). A 202: the
 * dialer hangs up the redial that is ringing, makes no more for that call, and dials the rep's
 * next lead. Its `dialer` hint brings the next dial to the card; the dial is also read again a
 * moment later, for a hint that was missed.
 */
export function useStopRedials() {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    // The request names no call: the dialer stops the caller's own. `callId` is the dial the
    // rep was looking at, so the card can tell this stop from one of an earlier lead's.
    mutationFn: (_callId: string) => api.post<DialerActionResponse>("/dialer/redials/stop"),
    onSuccess: () => {
      setTimeout(() => {
        void qc.invalidateQueries({ queryKey: keys.scoped(["dialer"]) });
        void qc.invalidateQueries({ queryKey: keys.scoped(["acd"]) });
      }, 1_500);
    },
  });
}

/** The lead the rep last dialed, as `useOfferAction` kept it. Read from the cache, never fetched:
 *  a component that closes and opens again with the phone still has it. */
export function useDialedLead(): DialedLead | null {
  const keys = useKeys();
  const { data } = useQuery({
    queryKey: keys.dialedLead(),
    queryFn: (): DialedLead | null => null,
    enabled: false,
    staleTime: Infinity,
  });
  return data ?? null;
}

/** An answered dial on a campaign whose detection decides for itself (ADR-104): the phone keeps
 *  asking what the switch heard, which it often says after the dial has left `GET /dialer/current`. */
export interface WatchedDial {
  callId: string;
  leadId: string;
  name: string;
}

function useKept<T>(key: readonly unknown[]): [T | null, (value: T | null) => void] {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: key,
    queryFn: (): T | null => null,
    enabled: false,
    staleTime: Infinity,
  });
  const set = useCallback((value: T | null) => qc.setQueryData(key, value), [qc, key]);
  return [data ?? null, set];
}

/** Kept in the cache, never fetched, as `useDialedLead` is: the dialing view closes and opens
 *  between two dials, and what it was waiting to hear must not go with it. */
export function useWatchedDial(): [WatchedDial | null, (dial: WatchedDial | null) => void] {
  const keys = useKeys();
  return useKept<WatchedDial>(useMemo(() => keys.watchedDial(), [keys]));
}

export function useHeardVoicemail(): [HeardVoicemail | null, (heard: HeardVoicemail | null) => void] {
  const keys = useKeys();
  return useKept<HeardVoicemail>(useMemo(() => keys.heardVoicemail(), [keys]));
}

/**
 * The lead the dialer is calling for this phone, until they are brought to the rep, or null. The
 * dialer's `dialer` hint as it dials refetches it at once; nothing hints the lead's phone starting to
 * ring, so it is read again every two seconds while it is shown. Dropped from the cache the moment
 * nothing shows it (`gcTime: 0`), so the next dial never opens on the last one's lead.
 */
export function useDialerCall(options?: { enabled?: boolean }) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.dialerCall(),
    queryFn: () => api.get<DialerCall | null>("/dialer/current"),
    enabled: options?.enabled ?? true,
    retry: false,
    refetchInterval: 2_000,
    gcTime: 0,
  });
}

/**
 * What answering machine detection made of the dialer call the rep is on (ADR-106). The dialer's
 * `dialer` hint refetches it the moment a passive detection hears a machine; it is also read once
 * a second while the switch may still be listening, which is over within five seconds of the
 * answer, so the asking stops at a verdict, at a call nothing listens to, at an error (a core-api
 * that predates the route), or after twelve tries.
 */
export function useCallDetection(callId: string | null, enabled = true) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.callDetection(callId ?? "none"),
    queryFn: () => api.get<CallDetection>(`/dialer/calls/${callId}/detection`),
    enabled: enabled && callId !== null,
    retry: false,
    refetchInterval: (query) =>
      detectionSettled(query.state.data, query.state.dataUpdateCount, query.state.status === "error")
        ? false
        : 1_000,
    gcTime: 0,
  });
}

// --- a number dialed by hand, through the rep's campaign (ADR-047) --------------------------------

/** What the phone remembers of the dial it asked for: the call to follow, and the campaign the rep
 *  chose, which the picker offers first next time. */
export interface ManualDialMemory {
  /** The call the dock follows, until the rep puts its result away. */
  callId: string | null;
  campaignId: string | null;
}

/** The campaign chosen last, kept per browser like the keypad preference: a rep Ready on several
 *  campaigns should not have to choose again after every reload, and from a number in a table
 *  there is no picker to choose in. */
const DIAL_CAMPAIGN_KEY = "acl.softphone.dialCampaign";

function initialManualDial(): ManualDialMemory {
  try {
    return { callId: null, campaignId: localStorage.getItem(DIAL_CAMPAIGN_KEY) || null };
  } catch {
    return { callId: null, campaignId: null };
  }
}

/** The dropped lead to call back (`lib/call-back.ts`), kept in the cache as the manual dial is:
 *  set when "Call dropped" is recorded, cleared when the call is placed or declined. */
export function useCallBackMemory(): CallBack | null {
  const keys = useKeys();
  const { data } = useQuery<CallBack | null>({
    queryKey: keys.callBack(),
    queryFn: () => null,
    enabled: false,
    staleTime: Infinity,
    initialData: null,
  });
  return data ?? null;
}

export function useSetCallBackMemory(): (next: CallBack | null) => void {
  const keys = useKeys();
  const qc = useQueryClient();
  return useCallback((next) => qc.setQueryData<CallBack | null>(keys.callBack(), next), [qc, keys]);
}

/** Read from the cache, never fetched — a dock that closes and opens again still has it. */
export function useManualDialMemory(): ManualDialMemory {
  const keys = useKeys();
  const { data } = useQuery({
    queryKey: keys.manualDial(),
    queryFn: initialManualDial,
    enabled: false,
    staleTime: Infinity,
    initialData: initialManualDial,
  });
  return data;
}

export function useSetManualDialMemory(): (patch: Partial<ManualDialMemory>) => void {
  const keys = useKeys();
  const qc = useQueryClient();
  return useCallback(
    (patch) => {
      if (patch.campaignId !== undefined) {
        try {
          if (patch.campaignId) localStorage.setItem(DIAL_CAMPAIGN_KEY, patch.campaignId);
          else localStorage.removeItem(DIAL_CAMPAIGN_KEY);
        } catch {
          /* non-fatal — the choice simply will not survive a reload */
        }
      }
      qc.setQueryData<ManualDialMemory>(keys.manualDial(), (old) => ({
        ...(old ?? initialManualDial()),
        ...patch,
      }));
    },
    [qc, keys],
  );
}

/**
 * Place an outside number through a campaign. A 202 with the call as `requested`: the dialer has
 * been told, and the call is followed from here by `useManualCall`. Refusals are the server's
 * sentences — an extension, not Ready, the do-not-call list — shown as they come.
 */
export function useStartManualCall() {
  const keys = useKeys();
  const qc = useQueryClient();
  const remember = useSetManualDialMemory();
  return useMutation({
    mutationFn: ({
      phone, campaignId, leadId,
    }: { phone: string; campaignId: string | null; leadId?: string | null }) =>
      // `lead_id` places the call for that lead, to their own number or their emergency contact's
      // (ACL-382): it is then on their record. Sent only when given, so every other dial is as it was.
      api.post<ManualCall>("/dialer/calls", {
        phone,
        campaign_id: campaignId,
        ...(leadId ? { lead_id: leadId } : {}),
      }),
    onSuccess: (call) => {
      qc.setQueryData<ManualCall>(keys.manualCall(call.call_id), call);
      remember({ callId: call.call_id, campaignId: call.campaign_id });
      // The rep shows `dialing` on their record a moment later, and the dock's other views read it.
      setTimeout(() => void qc.invalidateQueries({ queryKey: keys.scoped(["acd"]) }), 1_000);
    },
  });
}

/**
 * The call, as far as it has got. Refetched on the `dialer` hint — the dialer sends one as it
 * takes the call and as it ends, the event-tracker one as the leg rings and answers — and every
 * second besides while it is on its way, since a hint stream that is down must not leave a rep
 * looking at "Ringing…" for a call that ended.
 */
export function useManualCall(callId: string | null) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.manualCall(callId ?? "none"),
    queryFn: () => api.get<ManualCall>(`/dialer/calls/${callId}`),
    enabled: callId !== null,
    retry: false,
    refetchInterval: (query) => {
      const stage = query.state.data?.stage;
      return stage === undefined || (stage !== "ended" && stage !== "connected") ? 1_000 : false;
    },
  });
}

/** Give up on the call: the dialer hangs the lead leg up, and the call ends as cancelled. */
export function useCancelManualCall() {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (callId: string) =>
      api.post<DialerActionResponse>(`/dialer/calls/${callId}/cancel`),
    onSuccess: (_response, callId) => {
      setTimeout(() => void qc.invalidateQueries({ queryKey: keys.manualCall(callId) }), 800);
    },
  });
}

/** The tenant's active voicemail messages, which a rep picks from on a dialer call. */
export function useVoicemailMessages(enabled = true) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.voicemailMessages(),
    queryFn: () => api.get<VoicemailMessage[]>("/voicemail-messages"),
    enabled,
    staleTime: 5 * 60_000,
  });
}

/** The library as the Voicemail messages page lists it, by name: recordings and scripts, the active
 *  ones, and for an admin the retired ones too. A key of its own: the list on a rep's phone
 *  (`voicemailMessages`) holds recordings only, since nothing plays a script yet. */
export function useVoicemailLibrary(includeRetired: boolean) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.voicemailLibrary(includeRetired),
    queryFn: () =>
      api.get<VoicemailMessage[]>(
        `/voicemail-messages${qs({ include_scripts: true, include_inactive: includeRetired || undefined })}`,
      ),
  });
}

/** The fields a script can use, for the form's Insert field menu. An admin's to read. */
export function useGreetingFields(enabled = true) {
  const keys = useKeys();
  return useQuery({
    enabled,
    queryKey: keys.greetingFields(),
    queryFn: () => api.get<GreetingField[]>("/voicemail-messages/fields"),
    staleTime: 5 * 60_000,
  });
}

/** Whose greeting to show: a draft filled for the lead, or with no `script` the library's own pick. */
export interface GreetingPreviewInput {
  lead_id: string;
  script?: string;
  language?: string;
}

/** A greeting as one lead would hear it. A read, although it is a POST: the draft rides the body. The
 *  last answer stays on screen while the next is fetched, so the words do not blink as a draft is typed. */
export function useGreetingPreview(asked: GreetingPreviewInput | null) {
  const keys = useKeys();
  return useQuery({
    enabled: asked !== null,
    queryKey: keys.greetingPreview(asked ?? {}),
    queryFn: () => api.post<GreetingPreview>("/voicemail-messages/preview", asked),
    placeholderData: (previous) => previous,
    retry: false,
  });
}

/** A new message. `tenant_id` is a superadmin's to send, as for every tenant-owned row. A recording
 *  sends `media`, a script sends `script` and a language. */
export interface VoicemailMessageInput {
  slug: string;
  name: string;
  kind?: "recording" | "script";
  language?: string | null;
  stages?: string[];
  media?: string;
  script?: string;
  /** A script's version B (ADR-116 in `platform`). Left out, the greeting has one version. */
  script_b?: string;
  active?: boolean;
  tenant_id?: number | null;
}

/** What `PUT /voicemail-messages/{id}` takes. No slug, no kind, and no delete anywhere: retiring is
 *  `active: false`. Only the fields sent are written; null clears a recording's language, an
 *  empty `stages` makes it the general message, and a null `script_b` takes version B away. */
export interface VoicemailMessageUpdateInput {
  name?: string;
  language?: string | null;
  stages?: string[];
  media?: string;
  script?: string;
  script_b?: string | null;
  active?: boolean;
}

/** A library write changes what a rep can leave: the library, and the list on a rep's phone
 *  (`voicemailMessages`), which drops a retired message. The audio page's "Plays in" reads messages
 *  too, and refetches on arrival, as it does after a line or a campaign is saved. */
function useInvalidateVoicemailMessages() {
  const keys = useKeys();
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: keys.scoped(["voicemail-messages"]) });
}

export function useCreateVoicemailMessage() {
  const invalidate = useInvalidateVoicemailMessages();
  return useMutation({
    mutationFn: (input: VoicemailMessageInput) =>
      api.post<VoicemailMessage>("/voicemail-messages", input),
    onSuccess: invalidate,
  });
}

export function useUpdateVoicemailMessage() {
  const invalidate = useInvalidateVoicemailMessages();
  return useMutation({
    mutationFn: ({ id, ...input }: VoicemailMessageUpdateInput & { id: number }) =>
      api.put<VoicemailMessage>(`/voicemail-messages/${id}`, input),
    onSuccess: invalidate,
  });
}

/**
 * The tenant's audio store (ADR-020 in platform), by name, each file with the settings that play
 * it. Supervisors and up — the audio page and every setting's picker read it. `search` narrows by
 * name or description on the server, and the previous list stays up while the next one loads.
 */
export function useAudioLibrary(search = "", options?: Partial<UseQueryOptions<AudioFile[]>>) {
  const keys = useKeys();
  const q = search.trim();
  return useQuery({
    queryKey: keys.audioLibrary(q),
    queryFn: () => api.get<AudioFile[]>(`/audio/library${qs({ search: q })}`),
    staleTime: 30_000,
    placeholderData: (previous) => previous,
    ...options,
  });
}

export interface AudioUploadInput {
  file: File;
  name: string;
  description: string;
  tags: string[];
}

function useInvalidateAudio() {
  const keys = useKeys();
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: keys.scoped(["audio"]) });
}

/** A WAV into the store. core-api reads its header and refuses what the switch cannot play. */
export function useUploadAudio() {
  const invalidate = useInvalidateAudio();
  return useMutation({
    mutationFn: ({ file, name, description, tags }: AudioUploadInput) => {
      const form = new FormData();
      form.append("file", file);
      if (name) form.append("name", name);
      if (description) form.append("description", description);
      if (tags.length > 0) form.append("tags", tags.join(","));
      return api.upload<AudioFile>("/audio/library", form);
    },
    onSuccess: invalidate,
  });
}

/** A file's name, description and tags. Its audio, and so its URL, never change. */
export function useUpdateAudio() {
  const invalidate = useInvalidateAudio();
  return useMutation({
    mutationFn: ({
      id, ...input
    }: { id: number; name?: string; description?: string | null; tags?: string[] }) =>
      api.put<AudioFile>(`/audio/library/${id}`, input),
    onSuccess: invalidate,
  });
}

/** Refused (409) while a setting still plays the file. */
export function useDeleteAudio() {
  const invalidate = useInvalidateAudio();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/audio/library/${id}`),
    onSuccess: invalidate,
  });
}

/**
 * Leave a message on the voicemail the rep is listening to: the dialer moves the lead's leg off the
 * rep onto the message, and the rep's call ends — which is the confirmation of the 202.
 */
export function useDropVoicemail() {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ callId, template }: { callId: string; template: string }) =>
      api.post<DialerActionResponse>(`/dialer/calls/${callId}/voicemail`, { template }),
    onSuccess: () => {
      setTimeout(() => void qc.invalidateQueries({ queryKey: keys.scoped(["acd"]) }), 1_500);
    },
  });
}

// --- queues, campaigns ------------------------------------------------------------------------

export function useQueues(options?: Partial<UseQueryOptions<Queue[]>>) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.queues(),
    queryFn: () => api.get<Queue[]>("/queues"),
    staleTime: 5 * 60_000,
    ...options,
  });
}

export interface CampaignFilters {
  [key: string]: QueryValue;
  q?: string;
  enabled?: boolean;
}

export function useCampaigns(
  filters: CampaignFilters = {},
  options?: Partial<UseQueryOptions<Campaign[]>>,
) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.campaigns(filters),
    queryFn: () => api.get<Campaign[]>(`/campaigns${qs(filters)}`),
    ...options,
  });
}

export function useCampaign(id: string | undefined) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.campaign(id ?? "none"),
    queryFn: () => api.get<Campaign>(`/campaigns/${id}`),
    enabled: id !== undefined,
  });
}

/**
 * A campaign's identity, which the drawer on the list creates and edits. No line in it: core-api
 * creates the campaign's primary line with it and refuses a client that names another (ADR-014).
 */
export interface CampaignInput {
  name: string;
  slug: string;
  state?: string | null;
  language?: string;
  enabled?: boolean;
  tenant_id?: number | null;
}

/** Everything else `PUT /campaigns/{id}` takes: how the campaign is worked. Mirrors
 *  `_CampaignSettings`; only the fields sent are written. */
export interface CampaignSettingsInput {
  status?: string;
  purpose?: string;
  dial_mode?: string;
  language_routing?: string;
  force_auto_answer?: boolean;
  dial_priority?: number;
  dial_timeout_seconds?: number;
  preview_timeout_seconds?: number;
  calling_window_start?: string;
  calling_window_end?: string;
  respect_lead_timezone?: boolean;
  default_timezone?: string | null;
  abandon_after_seconds?: number;
  safe_harbor_action?: string;
  safe_harbor_media?: string | null;
  amd_mode?: string;
  amd_passive?: boolean;
  outbound_gateway_ids?: number[];
  caller_id_number?: string | null;
  list_horizon_days?: number;
  rep_number_fallback?: RepNumberFallback;
  rep_number_wait_seconds?: number;
  rep_number_message?: string | null;
  rep_number_caller_id?: boolean;
  own_calls_only_allowed?: boolean;
  rep_text_fallback?: RepTextFallback;
  rep_text_wait_seconds?: number;
  /** The cadence the campaign runs (ADR-067); null clears it, which the server takes only while
   *  the dial mode is off: a campaign that dials needs a cadence. */
  cadence_id?: number | null;
}

/** A line's settings as a write sends them. Mirrors `LineSettings`: only the fields sent are
 *  written, and null puts a column back to its default. */
export interface LineSettingsInput {
  description?: string | null;
  ring_timeout_seconds?: number | null;
  ring_strategy?: string | null;
  previous_rep_first?: boolean | null;
  wrap_up_seconds?: number | null;
  disposition_seconds?: number | null;
  max_wait_seconds?: number | null;
  moh?: string | null;
  outbound_hold_media?: string | null;
  entry_announcement?: string | null;
  required_skills?: SkillRequirement[] | null;
  preferred_skills?: SkillRequirement[] | null;
  skill_strategy?: string | null;
  skill_relax_seconds?: number | null;
  default_priority?: number | null;
  enabled?: boolean | null;
  /** Replaced whole when sent; null or [] removes every rule (ADR-073). */
  stage_rules?: StageRule[] | null;
}

/** A campaign write touches the numbers routed to it (their queue name), so both refresh. */
function useInvalidateRouting() {
  const keys = useKeys();
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: keys.scoped(["campaigns"]) });
    void qc.invalidateQueries({ queryKey: keys.scoped(["dids"]) });
  };
}

export function useCreateCampaign() {
  const invalidate = useInvalidateRouting();
  return useMutation({
    mutationFn: (input: CampaignInput) => api.post<Campaign>("/campaigns", input),
    onSuccess: invalidate,
  });
}

export function useUpdateCampaign() {
  const invalidate = useInvalidateRouting();
  return useMutation({
    mutationFn: ({
      id, ...input
    }: Partial<CampaignInput> & CampaignSettingsInput & { id: string }) =>
      api.put<Campaign>(`/campaigns/${id}`, input),
    onSuccess: invalidate,
  });
}

/**
 * The stages a campaign gives a cadence of its own (ADR-087), for the words under an outcome: an
 * end stage on this list keeps being called (`keepsCalling` in `lib/dispositions.ts`). Core-api
 * lets a supervisor read it; a rep's console does not ask, and gets the empty set.
 */
export function useStagesWithCadence(campaignId: string | null, enabled = true): ReadonlySet<string> {
  const keys = useKeys();
  const { user } = useAuth();
  const { data } = useQuery({
    queryKey: [...keys.campaign(campaignId ?? "none"), "stage-cadences"],
    queryFn: () => api.get<CampaignStageCadence[]>(`/campaigns/${campaignId}/stage-cadences`),
    enabled: enabled && campaignId !== null && atLeast(user?.role, "supervisor"),
    staleTime: 60_000,
    retry: false,
  });
  return useMemo(() => new Set((data ?? []).map((row) => row.stage)), [data]);
}

/** The stages a campaign gives a cadence of their own (ADR-087): the whole set, replaced. */
export function useSetStageCadences() {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, items }: { id: string; items: { stage: string; cadence_id: number }[] }) =>
      api.put<CampaignStageCadence[]>(`/campaigns/${id}/stage-cadences`, { items }),
    onSuccess: (_result, { id }) => {
      void qc.invalidateQueries({ queryKey: keys.campaign(id) });
      void qc.invalidateQueries({ queryKey: keys.scoped(["campaigns"]) });
    },
  });
}

export function useDeleteCampaign() {
  const invalidate = useInvalidateRouting();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/campaigns/${id}`),
    onSuccess: invalidate,
  });
}

// --- a campaign's lines, staffing, dispositions and dial lists (ADR-014, -017, -019) ------------

/** A line write moves its campaign's counts and settings, and `/queues` is what the number
 *  routing dialog offers — both refresh. */
function useInvalidateLines() {
  const keys = useKeys();
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: keys.scoped(["campaigns"]) });
    void qc.invalidateQueries({ queryKey: keys.queues() });
    // Whether a number's rep still staffs its campaign follows the staffing (ADR-037).
    void qc.invalidateQueries({ queryKey: keys.scoped(["dids"]) });
  };
}

export interface LineCreateInput extends LineSettingsInput {
  /** `closer`, `inbound` or `custom`: the primary line comes with the campaign. */
  purpose: string;
  /** The `acl_queue` value; core-api names it `<slug>-<purpose>` when this is left out. */
  name?: string | null;
}

export function useAddLine() {
  const invalidate = useInvalidateLines();
  return useMutation({
    mutationFn: ({ campaignId, ...input }: LineCreateInput & { campaignId: string }) =>
      api.post<Queue>(`/campaigns/${campaignId}/queues`, input),
    onSuccess: invalidate,
  });
}

export function useUpdateLine() {
  const invalidate = useInvalidateLines();
  return useMutation({
    mutationFn: ({ id, ...input }: LineSettingsInput & { id: number }) =>
      api.put<Queue>(`/queues/${id}`, input),
    onSuccess: invalidate,
  });
}

/** A line's staffing alert (ACL-567): who is told when it has nobody to take calls. */
export function useStaffingAlert(queueId: number | null, { enabled = true }: { enabled?: boolean } = {}) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.scoped(["staffing-alert", queueId]),
    queryFn: () => api.get<StaffingAlert>(`/queues/${queueId}/staffing-alert`),
    enabled: enabled && queueId !== null,
  });
}

export function useSaveStaffingAlert() {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ queueId, ...input }: StaffingAlertInput & { queueId: number }) =>
      api.put<StaffingAlert>(`/queues/${queueId}/staffing-alert`, input),
    onSuccess: (saved) => {
      qc.setQueryData(keys.scoped(["staffing-alert", saved.queue_id]), saved);
      void qc.invalidateQueries({ queryKey: keys.acdFloor() });
    },
  });
}

export function useDeleteLine() {
  const invalidate = useInvalidateLines();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/queues/${id}`),
    onSuccess: invalidate,
  });
}

/** Everyone on any of the campaign's lines, one row per rep per line. */
export function useCampaignMembers(campaignId: string, { enabled = true }: { enabled?: boolean } = {}) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.campaignMembers(campaignId),
    queryFn: () => api.get<QueueMember[]>(`/campaigns/${campaignId}/members`),
    enabled: enabled && campaignId !== "",
  });
}

/** Staffing is per line, through `/queues/{id}/members`: the campaign-level routes add to the
 *  primary line only and remove from every line. A line is also staffed by the teams put on it
 *  (platform ADR-185); taking a rep off by hand leaves them on a line a team still puts them on. */
export function useAddLineMember() {
  const invalidate = useInvalidateLines();
  return useMutation({
    mutationFn: ({ lineId, sipEndpointId }: { lineId: number; sipEndpointId: number }) =>
      api.post<QueueMember[]>(`/queues/${lineId}/members`, { sip_endpoint_id: sipEndpointId }),
    onSuccess: invalidate,
  });
}

export function useRemoveLineMember() {
  const invalidate = useInvalidateLines();
  return useMutation({
    mutationFn: ({ lineId, sipEndpointId }: { lineId: number; sipEndpointId: number }) =>
      api.delete<void>(`/queues/${lineId}/members/${sipEndpointId}`),
    onSuccess: invalidate,
  });
}

// --- messages (platform ADR-186) ---------------------------------------------------------------

/** How often the chats are read again with no hint: a lost hint costs this long, no more. */
export const CHATS_POLL = 60_000;

/** The chats the person is in, newest first, with each one's unread count. */
export function useChats(options?: Partial<UseQueryOptions<ChatSummary[]>>) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.chats(),
    queryFn: () => api.get<ChatSummary[]>("/chats"),
    refetchInterval: CHATS_POLL,
    ...options,
  });
}

export function useChat(id: number | null) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.chat(id ?? 0),
    queryFn: () => api.get<ChatDetail>(`/chats/${id}`),
    enabled: id !== null,
  });
}

/** How often an admin's open chat of another team is read again, with its open thread and the
 *  chats list: no hint comes for it (platform ADR-207). */
export const OTHER_TEAM_POLL = 10_000;

/** The newest hundred messages of a chat, oldest first. `otherTeam` reads it again every few
 *  seconds, for a chat whose new messages send the reader no hint. */
export function useChatMessages(id: number | null, otherTeam = false) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.chatMessages(id ?? 0),
    queryFn: () => api.get<ChatMessage[]>(`/chats/${id}/messages?limit=100`),
    enabled: id !== null,
    refetchInterval: otherTeam ? OTHER_TEAM_POLL : false,
  });
}

/** The tenant's people, to start a direct message with. */
export function useChatPeople(q: string, enabled = true) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.chatPeople(q),
    queryFn: () => api.get<ChatPerson[]>(`/chats/people${qs({ q: q || undefined })}`),
    enabled,
    staleTime: 60_000,
  });
}

export function useOpenDirectChat() {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: number) => api.post<ChatSummary>("/chats/direct", { user_id: userId }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.chats() }),
  });
}

/** A thread: its first message and replies (platform ADR-191). */
/** A thread. `otherTeam` reads it again every few seconds, as `useChatMessages` does. */
export function useChatThread(chatId: number, rootId: number | null, otherTeam = false) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.chatThread(chatId, rootId ?? 0),
    queryFn: () => api.get<ChatThread>(`/chats/${chatId}/threads/${rootId}`),
    enabled: rootId !== null,
    refetchInterval: otherTeam ? OTHER_TEAM_POLL : false,
  });
}

/** A follower's thread read: its unread replies stop counting (platform ADR-191). */
export function useMarkThreadRead() {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ chatId, rootId, messageId }: { chatId: number; rootId: number; messageId?: number }) =>
      api.post<void>(`/chats/${chatId}/threads/${rootId}/read`, { message_id: messageId ?? null }),
    onSuccess: (_void, { chatId }) => {
      void qc.invalidateQueries({ queryKey: keys.chats(), exact: true });
      void qc.invalidateQueries({ queryKey: keys.chatMessages(chatId) });
    },
  });
}

/** Where somebody one chats with is on the phone now, while their card is open: read again every
 *  few seconds, since nothing tells the console when another person's state changes. */
export function usePersonStatus(userId: number, enabled: boolean) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.chatPersonStatus(userId),
    queryFn: () => api.get<ChatPersonStatus>(`/chats/people/${userId}/status`),
    enabled,
    refetchInterval: enabled ? 5_000 : false,
    staleTime: 0,
    retry: false,
  });
}

/** The tenant's teams whose chat is open to everyone (platform ADR-190), for a new message. */
export function useOpenTeamChats(enabled = true) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.chatTeams(),
    queryFn: () => api.get<OpenTeamChat[]>("/chats/teams"),
    enabled,
    staleTime: 60_000,
  });
}

/** Into an open team's chat as a guest; for one of its people, the chat they are in. */
export function useJoinTeamChat() {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (teamId: number) => api.post<ChatSummary>(`/chats/teams/${teamId}/join`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.chats() }),
  });
}

/** A guest leaves an open team's chat; what they wrote stays. */
export function useLeaveChat() {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (chatId: number) => api.post<void>(`/chats/${chatId}/leave`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.chats() }),
  });
}

/** Close a direct chat or a contact's conversation (platform ADR-186, "Closing a chat"): off the
 *  person's list at once, until something new comes in it. Nothing is deleted. */
export function useCloseChat() {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (chatId: number) => api.post<void>(`/chats/${chatId}/close`),
    onSuccess: (_void, chatId) => {
      qc.setQueryData<ChatSummary[]>(keys.chats(), (old) => old?.filter((c) => c.id !== chatId));
      void qc.invalidateQueries({ queryKey: keys.chats(), exact: true });
    },
  });
}

/** A closed chat opened by a link to it: back on the person's list. */
export function useReopenChat() {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (chatId: number) => api.post<ChatSummary>(`/chats/${chatId}/open`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.chats(), exact: true }),
  });
}

export interface ReactionChange {
  chatId: number;
  messageId: number;
  emoji: string;
  /** Put it on; false takes the reader's own back. */
  on: boolean;
  /** How the reader is named among the others, for the screen before core-api answers. */
  myName: string;
}

/** Put an emoji on a message, or take one's own back (platform ADR-186, "Reactions"): on the screen
 *  at once wherever the message is shown, its chat and any of its threads, then as core-api has it.
 *  A failure reads the chat again. */
export function useReaction() {
  const keys = useKeys();
  const qc = useQueryClient();
  const apply = (chatId: number, messageId: number, change: (list: readonly ChatReaction[]) => ChatReaction[]) => {
    const one = (m: ChatMessage): ChatMessage => (m.id === messageId ? { ...m, reactions: change(m.reactions ?? []) } : m);
    qc.setQueryData<ChatMessage[]>(keys.chatMessages(chatId), (old) => old?.map(one));
    qc.setQueriesData<ChatThread>({ queryKey: [...keys.chat(chatId), "threads"] }, (old) =>
      old ? { ...old, root: one(old.root), replies: old.replies.map(one) } : old,
    );
  };
  return useMutation({
    mutationFn: ({ chatId, messageId, emoji, on }: ReactionChange) => {
      const path = `/chats/${chatId}/messages/${messageId}/reactions/${encodeURIComponent(emoji)}`;
      return on ? api.put<ChatReaction[]>(path) : api.delete<ChatReaction[]>(path);
    },
    onMutate: ({ chatId, messageId, emoji, on, myName }) =>
      apply(chatId, messageId, (list) => toggleReaction(list, emoji, on, myName)),
    onSuccess: (list, { chatId, messageId }) => apply(chatId, messageId, () => list),
    onError: (_e, { chatId }) => void qc.invalidateQueries({ queryKey: keys.chat(chatId) }),
  });
}

export interface ChatSend {
  chatId: number;
  body: string;
  leadId?: string | null;
  /** This console's id for the message: a retried send stays one message. */
  clientId: string;
  /** The people it mentions, by account id (platform ADR-186, "Mentions"). */
  mentionIds?: number[];
  /** A reply in the thread of this message (platform ADR-191). */
  threadId?: number;
}

export function useSendChatMessage() {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ chatId, body, leadId, clientId, mentionIds, threadId }: ChatSend) =>
      api.post<ChatMessage>(`/chats/${chatId}/messages`, {
        body,
        lead_id: leadId ?? null,
        client_id: clientId,
        mention_ids: mentionIds ?? [],
        thread_id: threadId ?? null,
      }),
    onSuccess: (sent, { chatId, threadId }) => {
      if (threadId) {
        // A reply: on the thread at once, and the line under its message read again.
        qc.setQueryData<ChatThread>(keys.chatThread(chatId, threadId), (old) =>
          old && !old.replies.some((m) => m.id === sent.id) ? { ...old, replies: [...old.replies, sent] } : old,
        );
        void qc.invalidateQueries({ queryKey: keys.chat(chatId) });
        void qc.invalidateQueries({ queryKey: keys.chats(), exact: true });
        return;
      }
      // On the screen at once, before the hint's refetch brings the same row.
      qc.setQueryData<ChatMessage[]>(keys.chatMessages(chatId), (old) =>
        old && !old.some((m) => m.id === sent.id) ? [...old, sent] : old,
      );
      void qc.invalidateQueries({ queryKey: keys.chats() });
    },
  });
}

export function useMarkChatRead() {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ chatId, messageId }: { chatId: number; messageId?: number }) =>
      api.post<void>(`/chats/${chatId}/read`, { message_id: messageId ?? null }),
    // The chat's own messages are read; its threads' unread replies stay until each is opened.
    onSuccess: (_void, { chatId }) => markReadInList(qc, keys.chats(), chatId),
  });
}

// --- asking for a closer (platform ADR-187) ----------------------------------------------------

/** While an ask is open, read it again this often with no hint: its clock is on the screen. */
const ASK_POLL = 5_000;

const OPEN_ASK: readonly CloserAskState[] = ["waiting", "taken"];

/** Whether an ask is still waiting for somebody, or held for the transfer. */
export function askIsOpen(ask: CloserAsk | null | undefined): ask is CloserAsk {
  return ask != null && OPEN_ASK.includes(ask.state);
}

/** The teams and people the caller on this call can be asked of. Read when the dialog opens. */
export function useCloserAskTargets(callId: string | null) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.closerAskTargets(callId ?? ""),
    queryFn: () =>
      api.get<CloserAskTargets>(`/handoff-requests/targets${qs({ call_id: callId ?? "" })}`),
    enabled: callId !== null,
  });
}

/** The setter's latest ask about this call. A core-api older than asks answers 404: no button. */
export function useCloserAskForCall(callId: string | null) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.closerAskForCall(callId ?? ""),
    queryFn: () =>
      api.get<CloserAsk | null>(`/handoff-requests/for-call${qs({ call_id: callId ?? "" })}`),
    enabled: callId !== null,
    retry: (count, error) => !(error instanceof ApiError && error.status === 404) && count < 2,
    refetchInterval: (query) => (askIsOpen(query.state.data) ? ASK_POLL : false),
  });
}

/** The ask this closer took and is held for, if any. */
export function useHeldCloserAsk(enabled = true) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.closerAskHeld(),
    queryFn: () => api.get<CloserAsk | null>("/handoff-requests/held"),
    enabled,
    retry: (count, error) => !(error instanceof ApiError && error.status === 404) && count < 2,
    refetchInterval: (query) => (askIsOpen(query.state.data) ? ASK_POLL : 60_000),
  });
}

/** The note for a closer, drafted from the call so far (platform ADR-188). A mutation, not a
 *  query: it asks an outside service each time, so it runs when the dialog opens or the setter
 *  asks again, never on a refetch. */
export function useDraftCloserNote() {
  return useMutation({
    mutationFn: (callId: string) =>
      api.post<CloserNoteDraft>("/handoff-requests/draft", { call_id: callId }),
  });
}

export interface CloserAskCreate {
  callId: string;
  teamId?: number | null;
  userId?: number | null;
  note?: string;
}

export function useAskForCloser() {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ callId, teamId, userId, note }: CloserAskCreate) =>
      api.post<CloserAsk>("/handoff-requests", {
        call_id: callId,
        team_id: teamId ?? null,
        user_id: userId ?? null,
        note: note?.trim() || null,
      }),
    onSuccess: (ask) => {
      qc.setQueryData(keys.closerAskForCall(ask.caller_channel_id), ask);
      void qc.invalidateQueries({ queryKey: keys.chats() });
    },
  });
}

/** What a closer or the setter does to an open ask. */
export type CloserAskAction = "take" | "give-back" | "cancel";

export function useCloserAskAction() {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, action }: { id: number; action: CloserAskAction }) =>
      api.post<CloserAsk>(`/handoff-requests/${id}/${action}`, {}),
    onSettled: () => void qc.invalidateQueries({ queryKey: keys.chats() }),
  });
}

/** The setter sent the caller to the closer who took it, with the handoff that carried them. */
export function useMarkCloserAskTransferred() {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, handoffId }: { id: number; handoffId: string | null }) =>
      api.post<CloserAsk>(`/handoff-requests/${id}/transferred`, { handoff_id: handoffId }),
    onSettled: () => void qc.invalidateQueries({ queryKey: keys.chats() }),
  });
}

// --- teams (platform ADR-185) ------------------------------------------------------------------

/** Every team of the tenant, with its people and lines. Supervisors and up. */
export function useTeams(options?: Partial<UseQueryOptions<Team[]>>) {
  const keys = useKeys();
  return useQuery({ queryKey: keys.teams(), queryFn: () => api.get<Team[]>("/teams"), ...options });
}

export function useTeam(id: number) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.team(id),
    queryFn: () => api.get<Team>(`/teams/${id}`),
    enabled: Number.isFinite(id),
  });
}

/** A team write can move reps onto or off lines, so the staffing is read again with the teams. */
function useInvalidateTeams() {
  const keys = useKeys();
  const qc = useQueryClient();
  const lines = useInvalidateLines();
  return () => {
    void qc.invalidateQueries({ queryKey: keys.teams() });
    lines();
  };
}

export interface TeamInput {
  name: string;
  lead_user_ids?: number[];
  user_ids?: number[];
  tenant_id?: number | null;
  open_chat?: boolean;
}

export function useCreateTeam() {
  const invalidate = useInvalidateTeams();
  return useMutation({
    mutationFn: (input: TeamInput) => api.post<Team>("/teams", input),
    onSuccess: invalidate,
  });
}

/** Only the fields sent are written; `lead_user_ids` sent replaces the leads, `[]` clears them. */
export function useUpdateTeam() {
  const invalidate = useInvalidateTeams();
  return useMutation({
    mutationFn: ({ id, ...input }: { id: number; name?: string; lead_user_ids?: number[]; open_chat?: boolean }) =>
      api.patch<Team>(`/teams/${id}`, input),
    onSuccess: invalidate,
  });
}

export function useDeleteTeam() {
  const invalidate = useInvalidateTeams();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/teams/${id}`),
    onSuccess: invalidate,
  });
}

export function useAddTeamMembers() {
  const invalidate = useInvalidateTeams();
  return useMutation({
    mutationFn: ({ teamId, userIds }: { teamId: number; userIds: number[] }) =>
      api.post<Team>(`/teams/${teamId}/members`, { user_ids: userIds }),
    onSuccess: invalidate,
  });
}

export function useRemoveTeamMember() {
  const invalidate = useInvalidateTeams();
  return useMutation({
    mutationFn: ({ teamId, userId }: { teamId: number; userId: number }) =>
      api.delete<void>(`/teams/${teamId}/members/${userId}`),
    onSuccess: invalidate,
  });
}

/** Putting a team on a line, or off it, is staffing: an admin's. */
export function useAddTeamLine() {
  const invalidate = useInvalidateTeams();
  return useMutation({
    mutationFn: ({ teamId, lineId }: { teamId: number; lineId: number }) =>
      api.post<Team>(`/teams/${teamId}/lines`, { queue_id: lineId }),
    onSuccess: invalidate,
  });
}

export function useRemoveTeamLine() {
  const invalidate = useInvalidateTeams();
  return useMutation({
    mutationFn: ({ teamId, lineId }: { teamId: number; lineId: number }) =>
      api.delete<void>(`/teams/${teamId}/lines/${lineId}`),
    onSuccess: invalidate,
  });
}

/** "Make a team from reps added by hand": the reps added to a line by hand become a team holding
 *  it. */
export function useTeamFromLine() {
  const invalidate = useInvalidateTeams();
  return useMutation({
    mutationFn: ({ lineId, name, leadUserIds }: { lineId: number; name: string; leadUserIds?: number[] }) =>
      api.post<Team>(`/queues/${lineId}/team`, { name, lead_user_ids: leadUserIds ?? [] }),
    onSuccess: invalidate,
  });
}

/** The tenant's active catalog, in catalog order. Anyone in the tenant may read it. */
/** The tenant's outcomes. Null for no tenant reads nothing, under a key of its own. */
export function useDispositions(
  tenantId: number | null,
  options?: Partial<UseQueryOptions<Disposition[]>>,
) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.dispositions(tenantId),
    queryFn: () => api.get<Disposition[]>(`/dispositions${qs({ tenant_id: tenantId })}`),
    staleTime: 5 * 60_000,
    ...options,
    enabled: tenantId !== null && (options?.enabled ?? true),
  });
}

/** Every outcome the tenant has had, switched-off ones too (ACL-574): what a recorded slug reads
 *  as when the list in use no longer holds it, so an old record keeps "NQ — statute of
 *  limitations" rather than "Nq sol". Never a list to pick from: those offer active outcomes only. */
export function useOutcomeHistory(enabled = true) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.outcomeHistory(),
    queryFn: () => api.get<Disposition[]>(`/dispositions${qs({ include_inactive: true })}`),
    staleTime: 5 * 60_000,
    enabled,
  });
}

/** The catalog as the Dispositions page lists it, in catalog order: the active outcomes, and for an
 *  admin the retired ones too (the page asks for them only for an admin). */
export function useDispositionCatalog(tenantId: number | null, includeRetired: boolean) {
  const keys = useKeys();
  return useQuery({
    queryKey: includeRetired
      ? keys.dispositionCatalog(tenantId ?? 0)
      : keys.dispositions(tenantId ?? 0),
    queryFn: () =>
      api.get<Disposition[]>(
        `/dispositions${qs({ tenant_id: tenantId, include_inactive: includeRetired || undefined })}`,
      ),
    enabled: tenantId !== null,
  });
}

/** A new outcome. `tenant_id` is a superadmin's to send, as for every tenant-owned row. */
export interface DispositionInput {
  slug: string;
  label: string;
  kind: string;
  category?: string | null;
  backoff_seconds?: number | null;
  hold_seconds?: number | null;
  enlist_campaign_id?: string | null;
  requires_note?: boolean;
  sort_order?: number;
  tenant_id?: number | null;
  /** The stage the outcome puts the lead in (ADR-046); null keeps it. */
  stage?: string | null;
  /** Null starts it in the group its kind, category and stage say (ADR-160). */
  outcome_group?: string | null;
  rep_can_pick?: boolean;
  /** A booking outcome's no-show limit (ADR-181): both or neither, on a callback only. */
  no_show_limit?: number | null;
  no_show_outcome?: string | null;
}

/** What `PUT /dispositions/{id}` takes. No slug, and no delete anywhere: retiring is
 *  `active: false`. Only the fields sent are written; null clears a category, a backoff or a hold. */
export interface DispositionUpdateInput {
  label?: string;
  kind?: string;
  category?: string | null;
  backoff_seconds?: number | null;
  hold_seconds?: number | null;
  enlist_campaign_id?: string | null;
  requires_note?: boolean;
  sort_order?: number;
  active?: boolean;
  stage?: string | null;
  outcome_group?: string;
  rep_can_pick?: boolean;
  /** Sent together: both null takes the no-show limit off (ADR-181). */
  no_show_limit?: number | null;
  no_show_outcome?: string | null;
}

/** A catalog write changes what every rep chooses from: the catalog, a rep's buttons on a call
 *  (`callDispositions`), and each campaign's pick, which drops an outcome that is retired. */
function useInvalidateDispositions() {
  const keys = useKeys();
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: keys.scoped(["dispositions"]) });
    void qc.invalidateQueries({ queryKey: keys.scoped(["campaigns", "detail"]) });
  };
}

export function useCreateDisposition() {
  const invalidate = useInvalidateDispositions();
  return useMutation({
    mutationFn: (input: DispositionInput) => api.post<Disposition>("/dispositions", input),
    onSuccess: invalidate,
  });
}

export function useUpdateDisposition() {
  const invalidate = useInvalidateDispositions();
  return useMutation({
    mutationFn: ({ id, ...input }: DispositionUpdateInput & { id: number }) =>
      api.put<Disposition>(`/dispositions/${id}`, input),
    onSuccess: invalidate,
  });
}

/**
 * Puts the catalog in a new order: one `PUT /dispositions/{id}` per row whose number moves
 * (`orderChanges` in `lib/dispositions.ts`), then one refetch. Every write is let finish before a
 * failure is reported, and the catalog refetches either way, so an order that half saved shows as
 * what it is.
 */
export function useReorderDispositions() {
  const invalidate = useInvalidateDispositions();
  return useMutation({
    mutationFn: async (changes: readonly { id: number; sort_order: number }[]) => {
      const results = await Promise.allSettled(
        changes.map(({ id, sort_order }) =>
          api.put<Disposition>(`/dispositions/${id}`, { sort_order }),
        ),
      );
      const failed = results.find((r): r is PromiseRejectedResult => r.status === "rejected");
      if (failed) throw failed.reason;
    },
    onSettled: invalidate,
  });
}

/** What a rep on the campaign chooses from: its own pick in its order, or — while it has picked
 *  none — the whole active catalog. */
export function useCampaignDispositions(campaignId: string) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.campaignDispositions(campaignId),
    queryFn: () => api.get<Disposition[]>(`/campaigns/${campaignId}/dispositions`),
  });
}

/** Replaces the pick. An empty list clears it, and the campaign offers the whole catalog again. */
export function useSetCampaignDispositions() {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ campaignId, ids }: { campaignId: string; ids: number[] }) =>
      api.put<Disposition[]>(`/campaigns/${campaignId}/dispositions`, {
        items: ids.map((id) => ({ disposition_id: id })),
      }),
    onSuccess: (saved, { campaignId }) => {
      qc.setQueryData(keys.campaignDispositions(campaignId), saved);
      // A rep's disposition buttons read `GET /dispositions?campaign_id=`.
      void qc.invalidateQueries({ queryKey: keys.scoped(["dispositions"]) });
    },
  });
}

/**
 * The campaign's dial lists, newest first. An import runs after the upload has answered and
 * writes its counts when it finishes, so the list polls while one is still importing — and only
 * then.
 */
export function useCampaignLists(campaignId: string) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.campaignLists(campaignId),
    queryFn: () => api.get<LeadList[]>(`/campaigns/${campaignId}/lists`),
    refetchInterval: (query) =>
      query.state.data?.some((list) => list.status === "importing") ? 2_000 : false,
  });
}

export interface ListImportInput {
  campaignId: string;
  file: File;
  name: string;
}

export function useImportList() {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    // No consent: core-api writes the fixed evidence on each new lead (platform ADR-194).
    mutationFn: ({ campaignId, file, name }: ListImportInput) => {
      const form = new FormData();
      form.append("file", file);
      form.append("name", name);
      return api.upload<LeadList>(`/campaigns/${campaignId}/lists`, form);
    },
    onSuccess: (_list, { campaignId }) => {
      void qc.invalidateQueries({ queryKey: keys.campaignLists(campaignId) });
    },
  });
}

export interface LeadFilters {
  [key: string]: QueryValue;
  state?: string;
  list_id?: number;
}

/** Refetches the campaign's leads: an import adds them after its upload has answered. */
export function useRefetchCampaignLeads(campaignId: string) {
  const keys = useKeys();
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: keys.scoped(["campaigns", "detail", campaignId, "leads"]) });
}

export const LEADS_PAGE_SIZE = 50;

/** One page of the campaign's leads, offset-paged like the call report and for the same reason. */
export function useCampaignLeads(campaignId: string, filters: LeadFilters = {}, page = 0) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.campaignLeads(campaignId, { ...filters, page }),
    queryFn: () =>
      api.getPage<CampaignLead>(
        `/campaigns/${campaignId}/leads${qs({
          ...filters, limit: LEADS_PAGE_SIZE, offset: page * LEADS_PAGE_SIZE,
        })}`,
      ),
    placeholderData: (previous) => previous,
  });
}

// --- skills (ADR-015) ----------------------------------------------------------------------------

/** The skill catalog, in the server's order: category, then name. Supervisor-and-above; a
 *  superadmin's is every tenant's. */
export function useSkills(options?: Partial<UseQueryOptions<RoutingSkill[]>>) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.skills(),
    queryFn: () => api.get<RoutingSkill[]>("/skills"),
    ...options,
  });
}

/** A new skill. `tenant_id` is a superadmin's to send, as for every tenant-owned row. */
export interface SkillInput {
  slug: string;
  name: string;
  category?: string | null;
  description?: string | null;
  is_agent_skill?: boolean;
  is_lead_tag?: boolean;
  tenant_id?: number | null;
}

/** What `PUT /skills/{id}` takes. No slug — lines, numbers, handoffs and reps name the skill by it
 *  — and no delete anywhere: retiring is `active: false`. Only the fields sent are written, and null
 *  clears a category or a description. */
export interface SkillUpdateInput {
  name?: string;
  category?: string | null;
  description?: string | null;
  is_agent_skill?: boolean;
  is_lead_tag?: boolean;
  active?: boolean;
}

/** A skill write moves the catalog, its holder counts, and the skills a campaign's Staffing tab
 *  lists on each rep (`QueueMember.skills`). It moves nobody onto or off a line (platform
 *  ADR-185). */
function useInvalidateSkills() {
  const keys = useKeys();
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: keys.scoped(["skills"]) });
    void qc.invalidateQueries({ queryKey: keys.scoped(["campaigns", "detail"]) });
  };
}

export function useCreateSkill() {
  const invalidate = useInvalidateSkills();
  return useMutation({
    mutationFn: (input: SkillInput) => api.post<RoutingSkill>("/skills", input),
    onSuccess: invalidate,
  });
}

export function useUpdateSkill() {
  const invalidate = useInvalidateSkills();
  return useMutation({
    mutationFn: ({ id, ...input }: SkillUpdateInput & { id: number }) =>
      api.put<RoutingSkill>(`/skills/${id}`, input),
    onSuccess: invalidate,
  });
}

/** A rep's skills, on the phone the ACD rings. `user` is null for an account with no phone. */
export function useUserSkills(userId: number) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.userSkills(userId),
    queryFn: () => api.get<UserSkills>(`/app-users/${userId}/skills`),
  });
}

/** Replaces the rep's whole set with `skills`: a skill left out is taken off them. */
export function useSetUserSkills() {
  const keys = useKeys();
  const qc = useQueryClient();
  const invalidate = useInvalidateSkills();
  return useMutation({
    mutationFn: ({ userId, skills }: { userId: number; skills: UserSkill[] }) =>
      api.put<UserSkills>(`/app-users/${userId}/skills`, { skills }),
    onSuccess: (saved, { userId }) => {
      qc.setQueryData(keys.userSkills(userId), saved);
      invalidate();
      // A team's page shows each member's skills.
      void qc.invalidateQueries({ queryKey: keys.teams() });
    },
  });
}

// --- the number pool and requests -------------------------------------------------------------

/** What a number write makes stale: the inventory, orders, the tenant's numbers, and every
 *  campaign. `campaigns` is the prefix of each campaign's detail (its caller id, which Make
 *  primary and Replace change) and of its Numbers tab (ACL-629), so every number write refreshes
 *  both: this tab's own, the Staffing tab's Give and Take, and the Numbers page's route and
 *  release. */
export function numberWriteKeys(keys: Pick<ReturnType<typeof createQueryKeys>, "scoped">) {
  return [
    keys.scoped(["numbers"]), keys.scoped(["orders"]), keys.scoped(["dids"]), keys.scoped(["campaigns"]),
  ];
}

function useInvalidateNumbers() {
  const keys = useKeys();
  const qc = useQueryClient();
  return () => {
    for (const queryKey of numberWriteKeys(keys)) void qc.invalidateQueries({ queryKey });
  };
}

export interface NumberFilters {
  [key: string]: QueryValue;
  q?: string;
  status?: string;
  tenant_id?: number;
}

/** The whole inventory — superadmin only. */
export function useNumbers(filters: NumberFilters = {}, options?: Partial<UseQueryOptions<PhoneNumber[]>>) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.numbers(filters),
    queryFn: () => api.get<PhoneNumber[]>(`/numbers${qs(filters)}`),
    ...options,
  });
}

export interface AvailableFilters {
  [key: string]: QueryValue;
  q?: string;
  area_code?: string;
  number_type?: string;
}

/** What a tenant admin may request. */
export function useAvailableNumbers(
  filters: AvailableFilters = {},
  options?: Partial<UseQueryOptions<PhoneNumber[]>>,
) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.availableNumbers(filters),
    queryFn: () => api.get<PhoneNumber[]>(`/numbers/available${qs(filters)}`),
    ...options,
  });
}

export interface OrderFilters {
  [key: string]: QueryValue;
  status?: string;
  tenant_id?: number;
}

export function useOrders(filters: OrderFilters = {}, options?: Partial<UseQueryOptions<NumberOrder[]>>) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.orders(filters),
    queryFn: () => api.get<NumberOrder[]>(`/numbers/orders${qs(filters)}`),
    ...options,
  });
}

export interface PhoneNumberInput {
  number: string;
  number_type?: string;
  region?: string | null;
  locality?: string | null;
  notes?: string | null;
  capabilities?: Record<string, boolean>;
}

export function useAddNumbers() {
  const invalidate = useInvalidateNumbers();
  return useMutation({
    mutationFn: (numbers: PhoneNumberInput[]) =>
      api.post<PhoneNumber[]>("/numbers", { numbers }),
    onSuccess: invalidate,
  });
}

export function useUpdateNumber() {
  const invalidate = useInvalidateNumbers();
  return useMutation({
    mutationFn: ({ id, ...input }: Partial<Omit<PhoneNumberInput, "number">> & { id: number; enabled?: boolean }) =>
      api.put<PhoneNumber>(`/numbers/${id}`, input),
    onSuccess: invalidate,
  });
}

export function useDeleteNumber() {
  const invalidate = useInvalidateNumbers();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/numbers/${id}`),
    onSuccess: invalidate,
  });
}

export function useAssignNumber() {
  const invalidate = useInvalidateNumbers();
  return useMutation({
    mutationFn: ({ id, tenantId }: { id: number; tenantId: number }) =>
      api.post<PhoneNumber>(`/numbers/${id}/assign`, { tenant_id: tenantId }),
    onSuccess: invalidate,
  });
}

export function useReleaseNumber() {
  const invalidate = useInvalidateNumbers();
  return useMutation({
    mutationFn: (id: number) => api.post<PhoneNumber>(`/numbers/${id}/release`),
    onSuccess: invalidate,
  });
}

export function useReserveNumber() {
  const invalidate = useInvalidateNumbers();
  return useMutation({
    mutationFn: (input: { phone_number_id: number; note?: string | null; tenant_id?: number | null }) =>
      api.post<NumberOrder>("/numbers/orders", input),
    onSuccess: invalidate,
  });
}

export function useCancelOrder() {
  const invalidate = useInvalidateNumbers();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/numbers/orders/${id}`),
    onSuccess: invalidate,
  });
}

export function useApproveOrder() {
  const invalidate = useInvalidateNumbers();
  return useMutation({
    mutationFn: (id: number) => api.post<NumberOrder>(`/numbers/orders/${id}/approve`),
    onSuccess: invalidate,
  });
}

export function useRejectOrder() {
  const invalidate = useInvalidateNumbers();
  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason?: string | null }) =>
      api.post<NumberOrder>(`/numbers/orders/${id}/reject`, { reason: reason ?? null }),
    onSuccess: invalidate,
  });
}

// --- the tenant's numbers and their routes ----------------------------------------------------

export interface DidFilters {
  [key: string]: QueryValue;
  q?: string;
  /** A route type, or `none` for numbers not routed yet. */
  action_type?: string;
  campaign_id?: string;
  enabled?: boolean;
  tenant_id?: number;
}

export function useDids(filters: DidFilters = {}, options?: Partial<UseQueryOptions<Did[]>>) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.dids(filters),
    queryFn: () => api.get<Did[]>(`/dids${qs(filters)}`),
    ...options,
  });
}

/** The whole route travels together: type and the targets it uses. Mirrors `DidRoute`. */
export interface DidRouteInput {
  action_type: RouteType | null;
  action_target?: string | null;
  campaign_id?: string | null;
  sip_endpoint_id?: number | null;
  /** Left out, the server keeps the number's tags; `[]` clears them. */
  routing_tags?: SkillRequirement[];
}

export interface DidUpdateInput extends Partial<DidRouteInput> {
  description?: string | null;
  enabled?: boolean;
}

export function useUpdateDid() {
  const invalidate = useInvalidateNumbers();
  return useMutation({
    mutationFn: ({ id, ...input }: DidUpdateInput & { id: number }) =>
      api.put<Did>(`/dids/${id}`, input),
    onSuccess: invalidate,
  });
}

// --- a campaign's numbers (ACL-629, platform ADR-202) -------------------------------------------

export function useCampaignNumbers(campaignId: string) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.campaignNumbers(campaignId),
    queryFn: () => api.get<CampaignNumbers>(`/campaigns/${campaignId}/numbers`),
  });
}

/** One write on a campaign's numbers. Each answers with the campaign's numbers as they now are,
 *  and a number moving touches the Numbers page and the campaign too: `useInvalidateNumbers`
 *  refreshes the campaign's detail, so the Dialing tab shows a new primary as its caller id. */
function useCampaignNumbersWrite<T>(write: (input: T) => Promise<CampaignNumbers>) {
  const keys = useKeys();
  const qc = useQueryClient();
  const invalidate = useInvalidateNumbers();
  return useMutation({
    mutationFn: write,
    onSuccess: (data) => {
      qc.setQueryData(keys.campaignNumbers(data.campaign_id), data);
      invalidate();
    },
  });
}

/** Routes unrouted numbers to the campaign as local numbers. */
export function useAddCampaignNumbers() {
  return useCampaignNumbersWrite(({ campaignId, didIds }: { campaignId: string; didIds: number[] }) =>
    api.post<CampaignNumbers>(`/campaigns/${campaignId}/numbers`, { did_ids: didIds }),
  );
}

/** Takes a number off the campaign: it stops ringing there, callbacks included. */
export function useRemoveCampaignNumber() {
  return useCampaignNumbersWrite(({ campaignId, didId }: { campaignId: string; didId: number }) =>
    api.delete<CampaignNumbers>(`/campaigns/${campaignId}/numbers/${didId}`),
  );
}

/** Another number takes this one's place; this one becomes inbound only. */
export function useReplaceCampaignNumber() {
  return useCampaignNumbersWrite(
    ({ campaignId, didId, withDidId }: { campaignId: string; didId: number; withDidId: number }) =>
      api.post<CampaignNumbers>(`/campaigns/${campaignId}/numbers/${didId}/replace`, { did_id: withDidId }),
  );
}

/** Flags a number inbound only, or lets it be presented again. */
export function useInboundOnly() {
  return useCampaignNumbersWrite(
    ({ campaignId, didId, on }: { campaignId: string; didId: number; on: boolean }) => {
      const path = `/campaigns/${campaignId}/numbers/${didId}/inbound-only`;
      return on ? api.post<CampaignNumbers>(path, {}) : api.delete<CampaignNumbers>(path);
    },
  );
}

/** Makes one of the campaign's local numbers its primary: its caller id. */
export function useSetCampaignPrimary() {
  return useCampaignNumbersWrite(({ campaignId, didId }: { campaignId: string; didId: number }) =>
    api.put<CampaignNumbers>(`/campaigns/${campaignId}/primary`, { did_id: didId }),
  );
}

/** Releases the number back to the pool — the route goes with it. */
export function useReleaseDid() {
  const invalidate = useInvalidateNumbers();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/dids/${id}`),
    onSuccess: invalidate,
  });
}

// --- calls (CDRs) ------------------------------------------------------------------------------

/**
 * The call report's filter set. Mirrors `CallFilters` in core-api's `routers/cdrs.py`, and the
 * mirroring is the point: the list, the summary and the export all take the same filters
 * server-side, so the console sends one object to all three and the totals above a table can
 * never describe a different population than the table.
 *
 * `limit`/`offset` are deliberately not in here — they are paging, not filtering, and folding
 * them in would key the summary per page.
 */
export interface CallFilters {
  [key: string]: QueryValue;
  q?: string;
  /**
   * Only legs the signed-in user's own endpoint was on. Server-side this is a second *scope*,
   * not a column match — `cdrs` carries no user id, so core-api reconstructs it from the SIP
   * realm and the extension. It only ever narrows: a rep is narrowed to their own legs whether
   * or not this is sent, so the Call history tab reads the same for every role.
   */
  mine?: boolean;
  /**
   * `placed` | `received` — which half of the signed-in user's own legs. A second scope like
   * `mine` rather than a column match, and *not* the same question as `direction`: the switch
   * stamps a channel it created `outbound`, so a call the console placed for a rep by ringing
   * them first is an `outbound` leg on a call they placed.
   */
  mine_direction?: string;
  /** The switch's own reading of the channel. The floor's report filters on this; a personal
   *  history filters on `mine_direction`, which is the reader's. */
  direction?: string;
  leg?: string;
  /** A leg kind to leave out — the symmetric partner of `leg`. */
  exclude_leg?: string;
  campaign_id?: string;
  did?: string;
  domain?: string;
  hangup_cause?: string;
  amd_result?: string;
  answered?: boolean;
  abandoned?: boolean;
  /** How the caller's queue stay ended (`cdrs.queue_outcome`, ADR-025): ABANDONED, TIMEOUT, DROP… */
  queue_outcome?: string;
  min_duration?: number;
  /** RFC 3339. Inclusive. */
  since?: string;
  /** RFC 3339. Exclusive, so day-by-day paging counts every leg exactly once. */
  until?: string;
}

export const CALLS_PAGE_SIZE = 50;

/**
 * One page of call legs, plus the unpaged total.
 *
 * Offset paging rather than the infinite scroll the endpoint picker uses: a report is something
 * people page through, jump around in and cite ("row 3 of page 4"), and an accumulating list
 * cannot express that. `placeholderData` keeps the previous page on screen while the next
 * loads, so paging does not flash a skeleton over a table the reader is mid-sentence about.
 */
export function useCalls(
  filters: CallFilters = {},
  page = 0,
  options?: { enabled?: boolean; refetchInterval?: number },
) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.calls({ ...filters, page }),
    queryFn: () =>
      api.getPage<Cdr>(
        `/cdrs${qs({ ...filters, limit: CALLS_PAGE_SIZE, offset: page * CALLS_PAGE_SIZE })}`,
      ),
    placeholderData: (previous) => previous,
    ...options,
  });
}

export interface LostCallFilters {
  [key: string]: QueryValue;
  since?: string;
}

/**
 * The inbound calls that never reached us (platform ADR-178): what the carrier's log has and our
 * switch never took. Read again every minute while it is open, since the check finds a new one
 * every few minutes.
 */
export function useLostCalls(filters: LostCallFilters = {}, page = 0) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.lostCalls({ ...filters, page }),
    queryFn: () =>
      api.get<LostCallsPage>(
        `/lost-calls${qs({ ...filters, limit: CALLS_PAGE_SIZE, offset: page * CALLS_PAGE_SIZE })}`,
      ),
    placeholderData: (previous) => previous,
    refetchInterval: 60_000,
  });
}

/** Totals for the same filters. Separate request because it is separate work server-side. */
export function useCallSummary(
  filters: CallFilters = {},
  options?: { enabled?: boolean; refetchInterval?: number },
) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.callSummary(filters),
    queryFn: () => api.get<CdrSummary>(`/cdrs/summary${qs(filters)}`),
    ...options,
  });
}

/** One leg in full, including the raw switch event. Only fetched when a row is opened. */
export function useCall(id: number | undefined) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.scoped(["calls", "detail", id]),
    queryFn: () => api.get<CdrDetail>(`/cdrs/${id}`),
    enabled: id !== undefined,
    // A finished call never changes. Refetching one because the window regained focus is pure
    // waste, and re-rendering a detail pane under someone reading it is worse than waste.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
}

// --- a contact's Transcripts tab (ADR-081) -----------------------------------------------------

/** The most transcripts one `POST /transcripts/lines` returns (core-api's
 *  `TRANSCRIPT_LINES_LIMIT`). A bigger copy asks in batches of this, side by side. */
export const TRANSCRIPT_LINES_BATCH = 100;

/**
 * The transcripts of a contact's calls, newest first, with how many there are. Scoped as the
 * contact's Calls tab: a rep's own calls with them, a supervisor's tenant's. A transcript arrives a
 * minute or so after its call, so a tab opened right after hangup must not keep "none" for long.
 */
export function useContactTranscripts(leadId: string) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.contactTranscripts(leadId),
    queryFn: () =>
      api.getPage<TranscriptListItem>(`/leads/${encodeURIComponent(leadId)}/transcripts`),
    staleTime: 30_000,
  });
}

type TranscriptRef = { id: string; source: TranscriptSource };

/**
 * The lines of the transcripts named, by `transcriptKey`: from the cache for any opened already,
 * and the rest from `POST /transcripts/lines`, in batches the route takes, side by side. What comes
 * back is cached one transcript at a time, so a row opened after a copy needs no request.
 *
 * A transcript the reader may no longer read is missing from the answer rather than an error, so
 * the caller counts what it got.
 */
export function useFetchTranscriptLines() {
  const keys = useKeys();
  const qc = useQueryClient();
  return useCallback(
    async (items: readonly TranscriptRef[]): Promise<Map<string, TranscriptSegment[]>> => {
      const found = new Map<string, TranscriptSegment[]>();
      const missing: TranscriptRef[] = [];
      for (const item of items) {
        const cached = qc.getQueryData<TranscriptSegment[]>(keys.transcriptLines(item.id, item.source));
        if (cached?.length) found.set(transcriptKey(item), cached);
        else missing.push(item);
      }
      const batches: TranscriptRef[][] = [];
      for (let i = 0; i < missing.length; i += TRANSCRIPT_LINES_BATCH) {
        batches.push(missing.slice(i, i + TRANSCRIPT_LINES_BATCH));
      }
      const answers = await Promise.all(
        batches.map((batch) =>
          api.post<TranscriptLines[]>("/transcripts/lines", {
            transcripts: batch.map(({ id, source }) => ({ id, source })),
          }),
        ),
      );
      for (const transcript of answers.flat()) {
        found.set(transcriptKey(transcript), transcript.lines);
        qc.setQueryData(keys.transcriptLines(transcript.id, transcript.source), transcript.lines);
      }
      return found;
    },
    [keys, qc],
  );
}

/** One transcript's lines, for the row opened on the tab. A stored transcript changes only when
 *  its recording is transcribed again, so it is not refetched while the tab is open. */
export function useTranscriptLines(item: TranscriptRef | undefined) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.transcriptLines(item?.id, item?.source),
    enabled: item !== undefined,
    queryFn: async () => {
      const answer = await api.post<TranscriptLines[]>("/transcripts/lines", {
        transcripts: [{ id: item!.id, source: item!.source }],
      });
      return answer[0]?.lines ?? [];
    },
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
}

// --- webhooks (ADR-022) --------------------------------------------------------------------------

/** The tenant's endpoints, by name (a superadmin's: every tenant's). Admins only, as the routes. */
export function useWebhookEndpoints() {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.webhookEndpoints(),
    queryFn: () => api.get<WebhookEndpoint[]>("/webhooks/endpoints"),
  });
}

export function useWebhookEndpoint(id: number | undefined) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.webhookEndpoint(id ?? -1),
    queryFn: () => api.get<WebhookEndpoint>(`/webhooks/endpoints/${id}`),
    enabled: id !== undefined,
  });
}

/** A new endpoint. core-api makes its signing secret, and answers it this once. */
export interface WebhookEndpointInput {
  name: string;
  kind?: "http" | "slack";
  slack_connection_id?: number | null;
  slack_channel_id?: string | null;
  slack_channel_name?: string | null;
  url?: string | null;
  method?: string;
  headers?: Record<string, string>;
  enabled?: boolean;
  tenant_id?: number | null;
}

/** What `PUT /webhooks/endpoints/{id}` takes. Only the fields sent change; headers go whole. */
export interface WebhookEndpointUpdateInput {
  name?: string;
  slack_connection_id?: number | null;
  slack_channel_id?: string | null;
  slack_channel_name?: string | null;
  url?: string | null;
  method?: string;
  headers?: Record<string, string>;
  enabled?: boolean;
}

/** Every webhooks write changes something another webhooks screen shows. */
function useInvalidateWebhooks() {
  const keys = useKeys();
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: keys.scoped(["webhooks"]) });
}

export function useCreateWebhookEndpoint() {
  const invalidate = useInvalidateWebhooks();
  return useMutation({
    mutationFn: (input: WebhookEndpointInput) =>
      api.post<WebhookEndpointCreated>("/webhooks/endpoints", input),
    onSuccess: invalidate,
  });
}

export function useUpdateWebhookEndpoint() {
  const invalidate = useInvalidateWebhooks();
  return useMutation({
    mutationFn: ({ id, ...input }: WebhookEndpointUpdateInput & { id: number }) =>
      api.put<WebhookEndpoint>(`/webhooks/endpoints/${id}`, input),
    onSuccess: invalidate,
  });
}

/** Deletes the endpoint, its subscriptions, and what was sent through them. */
export function useDeleteWebhookEndpoint() {
  const invalidate = useInvalidateWebhooks();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/webhooks/endpoints/${id}`),
    onSuccess: invalidate,
  });
}

/** A new signing secret, answered this once. Every attempt from now on is signed with it. */
export function useRotateWebhookSecret() {
  return useMutation({
    mutationFn: (id: number) => api.post<{ secret: string }>(`/webhooks/endpoints/${id}/rotate-secret`),
  });
}

export function useWebhookSubscriptions(endpointId: number | undefined) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.webhookSubscriptions(endpointId ?? -1),
    queryFn: () => api.get<WebhookSubscription[]>(`/webhooks/endpoints/${endpointId}/subscriptions`),
    enabled: endpointId !== undefined,
  });
}

/** One more event type for an endpoint. Its tenant is the endpoint's. */
export interface WebhookSubscriptionInput {
  event_type: string;
  source_type?: string;
  source_id?: string | null;
  conditions?: Record<string, string | null>;
  post_in?: "thread" | "channel" | null;
  body_mode?: string;
  template?: string | null;
  enabled?: boolean;
}

/** Only the fields sent change; a null `source_id` or `template` clears it. */
export type WebhookSubscriptionUpdateInput = Partial<WebhookSubscriptionInput>;

export function useCreateWebhookSubscription() {
  const invalidate = useInvalidateWebhooks();
  return useMutation({
    mutationFn: ({ endpointId, ...input }: WebhookSubscriptionInput & { endpointId: number }) =>
      api.post<WebhookSubscription>(`/webhooks/endpoints/${endpointId}/subscriptions`, input),
    onSuccess: invalidate,
  });
}

export function useUpdateWebhookSubscription() {
  const invalidate = useInvalidateWebhooks();
  return useMutation({
    mutationFn: ({
      endpointId, id, ...input
    }: WebhookSubscriptionUpdateInput & { endpointId: number; id: number }) =>
      api.put<WebhookSubscription>(`/webhooks/endpoints/${endpointId}/subscriptions/${id}`, input),
    onSuccess: invalidate,
  });
}

/** Deletes the subscription and what was sent through it. */
export function useDeleteWebhookSubscription() {
  const invalidate = useInvalidateWebhooks();
  return useMutation({
    mutationFn: ({ endpointId, id }: { endpointId: number; id: number }) =>
      api.delete<void>(`/webhooks/endpoints/${endpointId}/subscriptions/${id}`),
    onSuccess: invalidate,
  });
}

/** Queues a sample of the subscription's event, sent once. Answers the delivery, to follow it. */
export function useSendWebhookTest() {
  const invalidate = useInvalidateWebhooks();
  return useMutation({
    mutationFn: ({ endpointId, id }: { endpointId: number; id: number }) =>
      api.post<WebhookDelivery>(`/webhooks/endpoints/${endpointId}/subscriptions/${id}/test`),
    onSuccess: invalidate,
  });
}

export function useWebhookEventTypes() {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.webhookEventTypes(),
    queryFn: () => api.get<WebhookEventType[]>("/webhooks/event-types"),
    staleTime: 30 * 60_000,
  });
}

/** The contact form's fields a subscription of this endpoint can name, each with an example answer. */
export function useWebhookFields(endpointId: number, options: { enabled?: boolean } = {}) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.webhookFields(endpointId),
    queryFn: () => api.get<WebhookField[]>(`/webhooks/endpoints/${endpointId}/fields`),
    enabled: options.enabled ?? true,
  });
}

export interface WebhookDeliveryFilters {
  [key: string]: QueryValue;
  endpoint_id?: number;
  status?: string;
  event_type?: string;
  test?: boolean;
}

export const DELIVERIES_PAGE_SIZE = 25;

/** One page of the delivery log, newest first. It polls while a delivery on the page is still
 *  queued, so a test's answer arrives without a reload. */
export function useWebhookDeliveries(filters: WebhookDeliveryFilters = {}, page = 0) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.webhookDeliveries({ ...filters, page }),
    queryFn: () =>
      api.getPage<WebhookDelivery>(
        `/webhooks/deliveries${qs({
          ...filters, limit: DELIVERIES_PAGE_SIZE, offset: page * DELIVERIES_PAGE_SIZE,
        })}`,
      ),
    placeholderData: (previous) => previous,
    refetchInterval: (query) =>
      query.state.data?.items.some((delivery) => delivery.status === "pending") ? 2_000 : false,
  });
}

/** Whether a delivery is still to be sent: queued, or asked to be sent again and not yet tried. */
function stillSending(delivery: WebhookDelivery | undefined): boolean {
  if (!delivery) return false;
  if (delivery.status === "pending") return true;
  const asked = delivery.replay_requested_at;
  return asked !== null && (delivery.last_attempt_at === null || asked > delivery.last_attempt_at);
}

/** One delivery with its event. It polls while it is being sent, so its answer shows up. */
export function useWebhookDelivery(id: number | undefined) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.webhookDelivery(id ?? -1),
    queryFn: () => api.get<WebhookDeliveryDetail>(`/webhooks/deliveries/${id}`),
    enabled: id !== undefined,
    refetchInterval: (query) => (stillSending(query.state.data) ? 1_500 : false),
  });
}

/** Sends a settled delivery once more, to the endpoint as it now stands. */
export function useReplayWebhookDelivery() {
  const invalidate = useInvalidateWebhooks();
  return useMutation({
    mutationFn: (id: number) => api.post<WebhookDelivery>(`/webhooks/deliveries/${id}/replay`),
    onSuccess: invalidate,
  });
}

// --- triggers (ADR-021) --------------------------------------------------------------------------

/** The tenant's triggers, by name (a superadmin's: every tenant's). Admins only, as the routes. */
export function useTriggers() {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.triggers(),
    queryFn: () => api.get<Trigger[]>("/triggers"),
  });
}

export function useTrigger(id: number | undefined) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.trigger(id ?? -1),
    queryFn: () => api.get<Trigger>(`/triggers/${id}`),
    enabled: id !== undefined,
  });
}

/** A new trigger. core-api makes its URL key and its secret, and answers the secret this once. */
export interface TriggerInput {
  name: string;
  description?: string | null;
  action: string;
  mapping?: Record<string, unknown>;
  options?: Record<string, unknown>;
  campaign_id?: string | null;
  lead_source?: string | null;
  /** header | basic: where a request carries the secret. */
  secret_kind?: string;
  secret_header?: string;
  enabled?: boolean;
  tenant_id?: number | null;
}

/** What `PUT /triggers/{id}` takes. Only the fields sent change; a null description, campaign or
 *  lead source clears it, and the whole trigger is checked as it will stand. */
export type TriggerUpdateInput = Partial<Omit<TriggerInput, "tenant_id">>;

function useInvalidateTriggers() {
  const keys = useKeys();
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: keys.scoped(["triggers"]) });
}

export function useCreateTrigger() {
  const invalidate = useInvalidateTriggers();
  return useMutation({
    mutationFn: (input: TriggerInput) => api.post<TriggerCreated>("/triggers", input),
    onSuccess: invalidate,
  });
}

export function useUpdateTrigger() {
  const invalidate = useInvalidateTriggers();
  return useMutation({
    mutationFn: ({ id, ...input }: TriggerUpdateInput & { id: number }) =>
      api.put<Trigger>(`/triggers/${id}`, input),
    onSuccess: invalidate,
  });
}

/** A new secret, answered this once. A request with the old one is refused from now on. */
export function useRotateTriggerSecret() {
  return useMutation({
    mutationFn: (id: number) => api.post<TriggerSecret>(`/triggers/${id}/rotate-secret`),
  });
}

export interface TriggerInboxFilters {
  [key: string]: QueryValue;
  status?: string;
}

export const REQUESTS_PAGE_SIZE = 25;

/** One page of what a trigger received, newest first. It polls, a little faster while a request is
 *  still waiting, so what a zap sends shows up while the zap is being set up. */
export function useTriggerInbox(id: number | undefined, filters: TriggerInboxFilters = {}, page = 0) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.triggerInbox(id ?? -1, { ...filters, page }),
    queryFn: () =>
      api.getPage<TriggerInboxRow>(
        `/triggers/${id}/inbox${qs({ ...filters, limit: REQUESTS_PAGE_SIZE, offset: page * REQUESTS_PAGE_SIZE })}`,
      ),
    enabled: id !== undefined,
    placeholderData: (previous) => previous,
    refetchInterval: (query) =>
      query.state.data?.items.some((row) => row.status === "received") ? 2_000 : 10_000,
  });
}

/** One request with its body. It polls while it waits to be applied. */
export function useTriggerRequest(id: number | undefined, rowId: number | undefined) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.triggerRequest(id ?? -1, rowId ?? -1),
    queryFn: () => api.get<TriggerInboxDetail>(`/triggers/${id}/inbox/${rowId}`),
    enabled: id !== undefined && rowId !== undefined,
    refetchInterval: (query) => (query.state.data?.status === "received" ? 1_500 : false),
  });
}

/** Applies a stored request again, as the trigger now stands, with a fresh count of tries. */
export function useReplayTriggerRequest() {
  const invalidate = useInvalidateTriggers();
  return useMutation({
    mutationFn: ({ id, rowId }: { id: number; rowId: number }) =>
      api.post<TriggerInboxRow>(`/triggers/${id}/inbox/${rowId}/replay`),
    onSuccess: invalidate,
  });
}


// --- contacts (ADR-044) --------------------------------------------------------------------------------

export interface ContactListFilters {
  [key: string]: QueryValue;
  q?: string;
  firm?: string;
  stage?: string;
  stage_group?: string;
  language?: string;
  state?: string;
  dial_state?: string;
  dnc?: boolean;
  member?: boolean;
  source?: string;
  created_from?: string;
  created_to?: string;
  sort?: string;
  order?: string;
}

/** Twenty a page: the list is read, not scrolled, and a page of twenty fits a laptop screen. */
export const CONTACTS_PAGE_SIZE = 20;

/** How often the list and the tiles re-read themselves. A rep's outcome on a live call moves a
 *  lead on the list without a reload; a tenant-wide hint replaces this in phase 2. */
const CONTACTS_POLL = 30_000;

export function useContacts(filters: ContactListFilters, page = 0, enabled = true) {
  const keys = useKeys();
  return useQuery({
    enabled,
    queryKey: keys.contacts({ ...filters, page }),
    queryFn: () =>
      api.getPage<ContactRow>(
        `/leads${qs({ ...filters, limit: CONTACTS_PAGE_SIZE, offset: page * CONTACTS_PAGE_SIZE })}`,
      ),
    placeholderData: (previous) => previous,
    refetchInterval: CONTACTS_POLL,
  });
}

/** The rows a caller's number is searched for (`useCallerContacts`). Newest first, so the newest
 *  contact with exactly the number is on this page unless this many newer contacts merely contain
 *  its digits, which a ten-digit number does not do. */
export const CALLER_SEARCH_LIMIT = 20;

/**
 * The contacts whose phone holds a caller's digits (`callerSearchDigits`), newest first, for a call
 * the ACD did not bring (ACL-483). The contacts list's own route and scope, read once for each
 * number: no poll and never stale, under a key no hint refetches (`keys.callerContacts`), so a long
 * call asks once and the card under it does not change while the rep talks. The next call from the
 * number is read again once the phone has let this one go for the cache's five minutes.
 */
export function useCallerContacts(digits: string | null) {
  const keys = useKeys();
  return useQuery({
    enabled: digits !== null,
    queryKey: keys.callerContacts(digits ?? "none"),
    queryFn: () =>
      api.getPage<ContactRow>(
        `/leads${qs({ q: digits ?? "", sort: "created", order: "desc", limit: CALLER_SEARCH_LIMIT })}`,
      ),
    staleTime: Infinity,
  });
}

/** How long the record pager's window of the list stays fresh. */
export const CONTACT_WINDOW_STALE_MS = 30_000;

/**
 * A few rows of the contacts list around one place in it (`lib/contact-pager.ts`): the contact a
 * record was opened on and the ones on either side, for its previous and next arrows.
 *
 * Its key is outside `leads` on purpose. A `leads` hint makes every key under `leads` stale
 * (`leadsHintTouches`), and on a dialing floor one comes every second or two: under `leads` the
 * window would be read again, and the whole filtered list counted, on every burst for as long as a
 * record is open, the load ACL-390 took off the record page. It is read when the record opens, is
 * fresh for `CONTACT_WINDOW_STALE_MS`, and an arrow pressed after that reads it again first
 * (`ContactPager`), so the arrows are right when pressed. No placeholder: the window shown while the
 * next one loads would be another place's, and could say the wrong "n of m".
 */
export function useContactWindow(filters: ContactListFilters, offset: number, size: number, enabled = true) {
  const keys = useKeys();
  return useQuery({
    enabled,
    queryKey: keys.contactWindow(filters, offset, size),
    queryFn: () => api.getPage<ContactRow>(`/leads${qs({ ...filters, limit: size, offset })}`),
    staleTime: CONTACT_WINDOW_STALE_MS,
  });
}

/** The tiles over the list: the same filter, minus the stage, which the tiles are. */
export function useContactSummary(filters: ContactListFilters) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.contactSummary(filters),
    queryFn: () => api.get<ContactSummary>(`/leads/summary${qs(filters)}`),
    placeholderData: (previous) => previous,
    refetchInterval: CONTACTS_POLL,
  });
}

/** Whether the lead behind a failed `/leads/{id}…` read is gone, or not the caller's to open:
 *  core-api answers both 404, and asking again does not change that. */
export function leadNotFound(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404;
}

/** A lead read is asked again as any other is, except a 404 (`leadNotFound`). */
function retryLeadRead(failureCount: number, error: Error): boolean {
  return !leadNotFound(error) && retryQuery(failureCount, error);
}

export function useContact(id: string | undefined) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.contact(id ?? "none"),
    queryFn: () => api.get<LeadCard>(`/leads/${id}`),
    enabled: id !== undefined,
    refetchInterval: CONTACTS_POLL,
    retry: retryLeadRead,
  });
}

/** The record's timeline, newest first, a page at a time back through `next_before`. */
/**
 * The lead's timeline, newest first, a page at a time. A write here (a note, an outcome) and the
 * `leads` hint refetch it, as they sit under `leads`. `refetchInterval` is for a view kept open, which
 * nothing hints for everything: another rep's note, or a call that ended without an outcome.
 * `channel` keeps only the texts or only the emails among its messages (ADR-180).
 */
export function useContactTimeline(
  id: string,
  kinds: string,
  options?: { refetchInterval?: number; channel?: "sms" | "email" },
) {
  const keys = useKeys();
  const channel = options?.channel;
  return useInfiniteQuery({
    queryKey: keys.contactTimeline(id, kinds, channel),
    queryFn: ({ pageParam }) =>
      api.get<LeadTimeline>(
        `/leads/${id}/timeline${qs({ before: pageParam, limit: 50, kinds: kinds || undefined, channel })}`,
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_before ?? undefined,
    refetchInterval: options?.refetchInterval,
    retry: retryLeadRead,
  });
}

/**
 * The record's timeline read again when the contact's conversation (platform ADR-189) gets a new
 * message, for a reader who is in it: the `chats` hint reaches only them and refetches their chats,
 * which this watches. A reader who is not in it gets the new messages the next time it loads.
 */
export function useTimelineFollowsConversation(contactId: string) {
  const keys = useKeys();
  const client = useQueryClient();
  const chats = useChats({ retry: false });
  const newest =
    chats.data?.find((c) => c.kind === "contact" && c.contact?.lead_id === contactId)?.last_message?.id ?? null;
  const seen = useRef(newest);
  useEffect(() => {
    if (newest !== null && seen.current !== null && newest !== seen.current) {
      void client.invalidateQueries({ queryKey: keys.contactTimeline(contactId, "").slice(0, -1) });
    }
    seen.current = newest;
  }, [newest, contactId, client, keys]);
}

export function useContactNotes(id: string) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.contactNotes(id),
    queryFn: () => api.get<LeadNote[]>(`/leads/${id}/notes`),
  });
}

/**
 * The record's SMS tab: the lead's texts in the conversations the reader may open (all of them for a
 * supervisor or an admin, a rep's own inboxes for a rep), oldest first, whichever of our numbers each
 * was on (`GET /leads/{id}/sms`, the newest 500). It sits under `leads/detail`, so the `messages` hint
 * and a text sent from Conversations refetch it; the poll is the backstop for a stream that is down.
 */
export function useContactSms(id: string) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.contactSms(id),
    queryFn: () => api.get<ContactSms[]>(`/leads/${id}/sms`),
    retry: (failures, error) => !textsMissing(error) && failures < 2,
    refetchInterval: (q) => (textsMissing(q.state.error) ? false : TEXTS_POLL),
  });
}

/**
 * A contact's appointments, every one the reader may see, earliest first (`GET /appointments?lead_id=`,
 * class sessions they sit in included). The appointments list it is, under its key, so a booking or a
 * change to an appointment refreshes it; without the list's placeholder, so another contact's never
 * shows while this one's loads.
 */
export function useContactAppointments(id: string) {
  const keys = useKeys();
  const filters: AppointmentFilters = { lead_id: id };
  return useQuery({
    queryKey: keys.appointments(filters),
    queryFn: () => api.get<AppointmentListItem[]>(appointmentsPath(filters)),
  });
}

/** The record's calls: the report, narrowed to one lead, scoped as the report is. */
export function useContactCalls(id: string, page = 0) {
  return useCalls({ lead_id: id }, page);
}

// --- texts (ADR-095 in platform) ---------------------------------------------------------------------
//
// What core-api serves for the inboxes. The `messages` hint refetches all of it (`lib/acd.ts`), and
// each query polls slowly as the backstop for a stream that is down.

const TEXTS_POLL = 30_000;
export const CONVERSATIONS_PAGE_SIZE = 50;

/** What `GET /conversations` filters on. `inbox` is an id `GET /conversations/inboxes` gave. */
export interface TextConversationFilters {
  inbox?: string;
  unread?: boolean;
  lead_id?: string;
  q?: string;
  /** core-api's order; newest exchange first when absent. */
  sort?: "oldest" | "unread";
  /** Only the ones whose last exchange was in the last day: the Recent tab. */
  recent?: boolean;
  /** What the last exchange was, comma-separated (`sms,call`); every kind when absent. */
  type?: string;
}

/** The inboxes the signed-in person reads, each with what waits in it. */
export function useTextInboxes(options?: { enabled?: boolean }) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.textInboxes(),
    queryFn: () => api.get<TextInbox[]>("/conversations/inboxes"),
    refetchInterval: TEXTS_POLL,
    ...options,
  });
}

/** A server without texts (ADR-095 not deployed to it) answers their routes 404: nothing to retry,
 *  and nothing to poll for. */
export function textsMissing(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404;
}

/** One page of conversations, newest text first. */
export function useTextConversations(
  filters: TextConversationFilters = {},
  page = 0,
  options?: { enabled?: boolean },
) {
  const keys = useKeys();
  const query = { ...filters, limit: CONVERSATIONS_PAGE_SIZE, offset: page * CONVERSATIONS_PAGE_SIZE };
  return useQuery({
    ...options,
    queryKey: keys.textConversations(query),
    queryFn: () => api.getPage<TextConversation>(`/conversations${qs(query)}`),
    placeholderData: (previous) => previous,
    retry: (failures, error) => !textsMissing(error) && failures < 2,
    refetchInterval: (q) => (textsMissing(q.state.error) ? false : TEXTS_POLL),
  });
}

/**
 * An inbox's conversations in the order asked for, 50 at a time: the next page is asked for as the
 * list is scrolled, until the loaded count reaches `X-Total-Count`. The sort and the filters are
 * core-api's, and in the key, so a change starts again from the top and each page continues the one
 * before (ACL-593). A refetch (the poll, the `messages` hint, a read) asks again for every page
 * loaded so far, so what has been scrolled to stays.
 */
export function useTextConversationPages(filters: TextConversationFilters = {}) {
  const keys = useKeys();
  return useInfiniteQuery({
    queryKey: keys.textConversationPages({ ...filters }),
    queryFn: ({ pageParam }) =>
      api.getPage<TextConversation>(
        `/conversations${qs({ ...filters, limit: CONVERSATIONS_PAGE_SIZE, offset: pageParam })}`,
      ),
    initialPageParam: 0,
    getNextPageParam: (last, pages) => {
      const loaded = pages.reduce((n, p) => n + p.items.length, 0);
      // Without a total, a full page means there may be more; a short one is the end.
      const more = last.total !== null ? loaded < last.total : last.items.length === CONVERSATIONS_PAGE_SIZE;
      return more && last.items.length > 0 ? loaded : undefined;
    },
    placeholderData: (previous) => previous,
    retry: (failures, error) => !textsMissing(error) && failures < 2,
    refetchInterval: (q) => (textsMissing(q.state.error) ? false : TEXTS_POLL),
  });
}

/** One conversation by id (`GET /conversations/{id}`): the open one, when the list's page does not
 *  hold it — a link, or an older thread. */
export function useTextConversation(id: string | undefined) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.textConversation(id ?? "none"),
    queryFn: () => api.get<TextConversation>(`/conversations/${id}`),
    enabled: id !== undefined,
    retry: (failures, error) => !textsMissing(error) && failures < 2,
    refetchInterval: (q) => (textsMissing(q.state.error) ? false : TEXTS_POLL),
  });
}

/** A conversation's texts, newest first, a page at a time back through `next_before`. Reading them
 *  opens nothing: `useReadConversation` does. */
export function useTextMessages(id: string | undefined) {
  const keys = useKeys();
  return useInfiniteQuery({
    queryKey: keys.textMessages(id ?? "none"),
    queryFn: ({ pageParam }) =>
      api.get<TextMessages>(`/conversations/${id}/messages${qs({ before: pageParam, limit: 50 })}`),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_before ?? undefined,
    enabled: id !== undefined,
    refetchInterval: TEXTS_POLL,
  });
}

/** Opens a conversation for everybody who shares its inbox, or puts it back as unread. */
export function useReadConversation() {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, read = true }: { id: string; read?: boolean }) =>
      api.post<TextConversation>(`/conversations/${id}/read`, { read }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.scoped(["conversations"]) });
      // Opening an email thread reads the bell's notifications of its lead's emails (platform
      // ADR-199): this tab's bell drops them now, not at the next hint.
      void qc.invalidateQueries({ queryKey: keys.scoped(["notifications"]) });
    },
  });
}

// --- sending a text (ADR-098 in platform) ------------------------------------------------------------
//
// A reply leaves from the number its conversation is on. A text from a contact's record leaves from
// one of the numbers its compose answer lists. core-api decides what may leave: a send it refuses is
// a 409 whose body carries the reason as a sentence (`ApiError.message`) and as a code
// (`textRefusal` in `lib/texts.ts`). A text it kept answers 201 as it stands, `sent` or `failed`: a
// failed text is in the thread, with why.

/** Whether a reply in a conversation may leave now, and why not. The `messages` hint asks again;
 *  so does a minute passing, since the answer turns on the time of day where the person is. */
export function useTextCompose(id: string | undefined) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.textCompose(id ?? "none"),
    queryFn: () => api.get<TextCompose>(`/conversations/${id}/compose`),
    enabled: id !== undefined,
    refetchInterval: 60_000,
  });
}

/** Whether a contact may be texted now, and the numbers the signed-in person may text them from.
 *  `from` asks about one of those numbers instead of the default. */
export function useContactTextCompose(id: string | undefined, from: string | null = null) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.contactTextCompose(id ?? "none", from),
    queryFn: () => api.get<TextCompose>(`/leads/${id}/sms/compose${qs({ from_number: from ?? undefined })}`),
    enabled: id !== undefined,
    refetchInterval: 60_000,
  });
}

// --- sending a contract (ADR-134 in platform) --------------------------------------------------------
//
// core-api builds the request from the lead and asks the contract service while the rep waits. What
// it refuses is a flat `{code, detail}` body, with `fields` naming the lead's values to fix
// (`contractRefusal` in `lib/send-contract.ts`).

/** Whether Send contract can be pressed for a contact, and why not. */
export function useContractCheck(id: string | undefined) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.contactContract(id ?? "none"),
    queryFn: () => api.get<ContractCheck>(`/leads/${id}/contract`),
    enabled: id !== undefined,
  });
}

/** The contact's presses of Send contract, newest first (ACL-427). A core-api before ACL-422 has no
 *  route and answers 404, which draws nothing. */
export function useLeadContracts(id: string) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.contactContracts(id),
    queryFn: () => api.get<LeadContract[]>(`/leads/${id}/contracts`),
    retry: contactFieldsRetry,
  });
}

/** What Send contract offers to choose (ACL-578), asked while its dialog is open. A core-api or a
 *  contract service before ACL-578 answers with an error, and the dialog sends as it did: no retry. */
export function useContractAgreements(id: string, enabled: boolean) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.contactContractAgreements(id),
    queryFn: () => api.get<ContractAgreements>(`/leads/${id}/contract/agreements`),
    enabled,
    retry: false,
  });
}

/** Ask for the contact's agreement to be sent: the one the rep chose (`agreementId`, ACL-578), else
 *  the one core-api works out. Rejects with an `ApiError` when it was not. */
export function useSendContract() {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ leadId, agreementId }: { leadId: string; agreementId: number | null }) =>
      api.post<ContractSent>(
        `/leads/${leadId}/contract`,
        agreementId === null ? undefined : { agreement_id: agreementId },
      ),
    // A press is kept on the lead (ACL-422, ADR-158 in platform): on `sent` the DocuSeal Retainer
    // Link field is filled and the stage moves to About to sign, so the whole record reads again, and
    // the call's card too when it was sent from the Phone page (ACL-560): the card is under the lead,
    // not under its record (`leadCard`).
    // Another firm's agreement makes that firm the contact's (ACL-578), which the record reads again too.
    onSettled: (_sent, _error, { leadId }) => {
      void qc.invalidateQueries({ queryKey: keys.contact(leadId) });
      void qc.invalidateQueries({ queryKey: keys.scoped(["leads", leadId]) });
    },
  });
}

/** Replies in a conversation. Resolves with the text as it stands once the provider has answered:
 *  check its `status`, a kept text can be `failed`. Rejects with an `ApiError` when the text may
 *  not leave (`textRefusal`). The inboxes, the thread and the open record refetch either way. */
export function useSendText() {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ conversationId, text, mediaIds }: { conversationId: string; text: string; mediaIds?: string[] }) =>
      api.post<TextMessage>(`/conversations/${conversationId}/messages`, {
        text,
        // Left off entirely with no attachments, so a core-api that predates ADR-140 sees the
        // request it always saw.
        ...(mediaIds?.length ? { media_ids: mediaIds } : {}),
      } satisfies TextSend),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: keys.scoped(["conversations"]) });
      void qc.invalidateQueries({ queryKey: keys.scoped(["leads", "detail"]) });
    },
  });
}

/**
 * Uploads one file to a conversation's media store, before a send names it (ADR-140). Resolves with
 * the stored media — its `id` is what `TextSend.media_ids` carries.
 *
 * Nothing is invalidated: the upload changes no thread until a send names the media, and the send's
 * own mutations refetch the thread. core-api refuses a type or a size here (415, 413), so the
 * composer's own check (`lib/mms.ts`) is only to save the round trip.
 */
export function useUploadConversationMedia() {
  return useMutation({
    mutationFn: ({ conversationId, file }: { conversationId: string; file: File }) => {
      const form = new FormData();
      form.append("file", file);
      return api.upload<TextMedia>(`/conversations/${conversationId}/media`, form);
    },
  });
}

/** Texts a contact from their record, from `from_number` or the default. Resolves with the text as
 *  the SMS tab shows it; as `useSendText`, a kept text can be `failed`. */
export function useTextContact() {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ leadId, ...body }: { leadId: string } & TextSend) =>
      api.post<ContactSms>(`/leads/${leadId}/sms`, body),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: keys.scoped(["conversations"]) });
      void qc.invalidateQueries({ queryKey: keys.scoped(["leads", "detail"]) });
    },
  });
}

// --- sending an email (ADR-180 in platform) ----------------------------------------------------------
//
// An email leaves from the tenant's address with its thread's reply address to answer to. A reply
// goes in its email thread; an email from a contact's record joins the lead's email thread or starts
// it. As for a text, core-api decides what may leave: a send it refuses is a 409 with the reason as a
// sentence and a code, and one it kept answers 201 `sent` or `failed`. An answer carries no body:
// a list never holds a whole email, and `useMessage` reads one.

/** Whether a reply in an email thread may leave now, and why not. A minute passing asks again, as
 *  the day's limit and the settings can change under it. */
export function useEmailCompose(id: string | undefined) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.emailCompose(id ?? "none"),
    queryFn: () => api.get<EmailCompose>(`/conversations/${id}/email/compose`),
    enabled: id !== undefined,
    refetchInterval: 60_000,
  });
}

/** Whether a contact may be emailed now from their record, which thread it would join, and the
 *  subject a reply there takes. */
export function useContactEmailCompose(id: string | undefined) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.contactEmailCompose(id ?? "none"),
    queryFn: () => api.get<EmailCompose>(`/leads/${id}/email/compose`),
    enabled: id !== undefined,
    refetchInterval: 60_000,
  });
}

/** Replies in an email thread. As `useSendText`: resolves with the email as it stands, which can be
 *  `failed`; rejects with an `ApiError` when it may not leave. */
export function useSendEmail() {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ conversationId, text, subject }: { conversationId: string; text: string; subject?: string }) =>
      api.post<TextMessage>(`/conversations/${conversationId}/messages`, {
        text,
        ...(subject ? { subject } : {}),
      } satisfies ReplySend),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: keys.scoped(["conversations"]) });
      void qc.invalidateQueries({ queryKey: keys.scoped(["leads", "detail"]) });
    },
  });
}

/** Emails a contact from their record. Resolves with the email as the timeline shows it; as
 *  `useSendEmail`, a kept email can be `failed`. */
export function useEmailContact() {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ leadId, text, subject }: { leadId: string; text: string; subject?: string }) =>
      api.post<TextMessage>(`/leads/${leadId}/email`, { ...(subject ? { subject } : {}), text } satisfies EmailSend),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: keys.scoped(["conversations"]) });
      void qc.invalidateQueries({ queryKey: keys.scoped(["leads", "detail"]) });
    },
  });
}

/** One message whole: an email's text, its HTML and its headers. Asked only while its line is open
 *  in a thread core-api has; reading it marks nothing read. A 404 (gone, or not one the reader may
 *  open: one answer for both) is not asked again. */
export function useMessage(id: string | undefined) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.message(id ?? "none"),
    queryFn: () => api.get<MessageDetail>(`/messages/${id}`),
    enabled: id !== undefined && id !== "",
    retry: (failures, error) => !(error instanceof ApiError && error.status === 404) && retryQuery(failures, error),
  });
}

/** The firms, with each one's Signed this month counted from the viewer's first of the month
 *  (ACL-537), not the server's UTC one, read when the list is fetched. */
export function useFirms(options?: { enabled?: boolean }) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.firms(),
    queryFn: () => api.get<Firm[]>(`/firms${qs({ month_start: localMonthStart() })}`),
    staleTime: 5 * 60_000,
    ...options,
  });
}

/** The firms a search on the Firms page finds (`GET /firms?q=`, ADR-101): every word in a firm's label,
 *  name, code or tags, or in a firm field an admin marked searchable that this person may see. Asked only
 *  while there is something to search. */
export function useFirmSearch(q: string) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.firmSearch(q),
    queryFn: () => api.get<Firm[]>(`/firms${qs({ q, month_start: localMonthStart() })}`),
    enabled: q.length > 0,
    staleTime: 30_000,
  });
}

/** Every write to a contact refetches everything under `leads`: the list, the tiles, the record,
 *  its timeline and notes — and the phone's card of the same lead. */
function useInvalidateContacts() {
  const keys = useKeys();
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: keys.scoped(["leads"]) });
  };
}

export interface ContactInput {
  first_name?: string | null;
  last_name?: string | null;
  language?: string | null;
  phone?: string;
  state?: string | null;
  timezone?: string | null;
  email?: string | null;
  firm_id?: number;
  /** A rep only to `sms_only`; a supervisor either way (ACL-343). */
  contact_channel?: ContactChannel;
}

/** Set or change a lead's calling window, or clear it (`null`), on the record (ADR-156). */
export function useCallingWindow() {
  const invalidate = useInvalidateContacts();
  return useMutation({
    mutationFn: ({ id, window }: { id: string; window: { start: string; end: string } | null }) =>
      window === null
        ? api.delete<{ lead_id: string; calling_window: LeadCallingWindow | null }>(`/leads/${id}/calling-window`)
        : api.put<{ lead_id: string; calling_window: LeadCallingWindow | null }>(`/leads/${id}/calling-window`, window),
    onSuccess: invalidate,
  });
}

export function useUpdateContact() {
  const invalidate = useInvalidateContacts();
  return useMutation({
    mutationFn: ({ id, ...input }: ContactInput & { id: string }) =>
      api.patch<LeadCard>(`/leads/${id}`, input),
    onSuccess: invalidate,
  });
}

/** A core-api without the dynamic fields (ADR-087 not deployed to it), or a contact that is gone,
 *  answers `/leads/{id}/fields` with 404. Asking again changes nothing, so a 404 is not retried and
 *  stops the polling; the cards draw nothing for it. */
export function fieldsMissing(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404;
}

/** Retry for the contact's fields: the client's usual rule (no retry for a cancelled request, a 401 or
 *  a 403; otherwise two), and never on a 404 either. */
export function contactFieldsRetry(failures: number, error: unknown): boolean {
  if (fieldsMissing(error)) return false;
  if (error instanceof Error && error.name === "AbortError") return false;
  if (error instanceof ApiError && (error.status === 401 || error.status === 403)) return false;
  return failures < 2;
}

/** Poll the contact's fields, unless the last answer was a 404. */
export function contactFieldsPoll(error: unknown): number | false {
  return fieldsMissing(error) ? false : CONTACTS_POLL;
}

/** The contact's sections and fields with their values, as far as the signed-in person's role may
 *  see them (ACL-255): a field hidden from the role is not in the answer at all. */
export function useContactFields(id: string | undefined) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.contactFields(id ?? "none"),
    queryFn: () => api.get<LeadFields>(`/leads/${id}/fields`),
    enabled: id !== undefined,
    retry: contactFieldsRetry,
    refetchInterval: (query) => contactFieldsPoll(query.state.error),
  });
}

/** A core-api without the accident links (ACL-337), or a contact that is gone, answers
 *  `/leads/{id}/accident` with 404: like the fields, it is not retried and stops the polling, and
 *  the record and the phone draw nothing for it. */
function accidentPoll(error: unknown): number | false {
  return fieldsMissing(error) ? false : CONTACTS_POLL;
}

/** The contact's accident and the records listed beside it (ACL-337), read apart from the record
 *  itself so a fault here never blanks the record or the call's card. `poll` on the record; the call's
 *  card reads it once, as it reads the lead. */
/** A person and their cases (ADR-224). */
export function usePerson(id: string | undefined) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.person(id ?? "none"),
    queryFn: () => api.get<Person>(`/people/${id}`),
    enabled: id !== undefined,
    refetchInterval: CONTACTS_POLL,
    retry: retryLeadRead,
  });
}

export function useLeadAccident(id: string | null, { poll = false }: { poll?: boolean } = {}) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.contactAccident(id ?? "none"),
    queryFn: () => api.get<LeadAccident>(`/leads/${id}/accident`),
    enabled: id !== null,
    retry: contactFieldsRetry,
    refetchInterval: poll ? (query) => accidentPoll(query.state.error) : false,
  });
}

/** The records a phone or an e-mail already has, before there is a lead (ACL-409): the new-contact
 *  form asks once the rep has stopped typing (the caller waits), for a phone or an e-mail worth
 *  asking about, and null for one it should not ask about. Read once; an older core-api without the
 *  route answers 404, which draws nothing. */
export function useLeadHistory(phone: string | null, email: string | null) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.leadHistory(phone ?? "", email ?? ""),
    queryFn: () =>
      api.get<LeadHistory>(`/leads/history${qs({ phone: phone ?? undefined, email: email ?? undefined })}`),
    enabled: phone !== null || email !== null,
    retry: false,
    // The list on screen stays until the next answer is in: gone in between, the fields under it
    // jumped up and back with every pause in the typing. The caller draws nothing once neither value
    // is worth asking about, where this would keep the last answer.
    placeholderData: keepPreviousData,
  });
}

/** Say how another record relates to this one: the same accident (with the other's role, if said) or
 *  another. The answer is the record's accident as it now stands. */
export function useLinkAccident() {
  const invalidate = useInvalidateContacts();
  return useMutation({
    mutationFn: ({
      leadId, otherId, relation, role,
    }: {
      leadId: string;
      otherId: string;
      relation: "same_accident" | "another_accident";
      role?: AccidentRole | null;
    }) =>
      api.post<LeadAccident>(`/leads/${leadId}/accident/links`, {
        lead_id: otherId,
        relation,
        ...(role ? { role } : {}),
      }),
    onSuccess: invalidate,
  });
}

/** Take a record out of its accident: another record, or the page's own when `otherId` is its id.
 *  Anyone who may open both does it, a rep included. The other records of it keep theirs. */
export function useUnlinkAccident() {
  const invalidate = useInvalidateContacts();
  return useMutation({
    mutationFn: ({ leadId, otherId }: { leadId: string; otherId: string }) =>
      api.delete<LeadAccident>(`/leads/${leadId}/accident/links/${otherId}`),
    onSuccess: invalidate,
  });
}

/**
 * The same read for the Phone page's form (`softphone/call-form.tsx`), which has a lead only some of
 * the time, so it waits for an id. It shares the contact page's cache entry: an answer saved on one
 * is on the other. A 404 is the server saying this person will not get this form (no such route
 * yet, or a lead they may not open), which asking again does not change: it is not retried and it
 * stops the half-minute poll.
 */
export function useLeadFormFields(id: string | null) {
  const keys = useKeys();
  const missing = (error: unknown) => error instanceof ApiError && error.status === 404;
  return useQuery({
    queryKey: keys.contactFields(id ?? "none"),
    queryFn: () => api.get<LeadFields>(`/leads/${id}/fields`),
    enabled: id !== null,
    retry: (failures, error) =>
      !missing(error) && !(error instanceof ApiError && (error.status === 401 || error.status === 403)) &&
      error.name !== "AbortError" && failures < 2,
    refetchInterval: (query) => (missing(query.state.error) ? false : CONTACTS_POLL),
  });
}

/** `PATCH /leads/{id}/fields`: `{key: value}`, null clears. All or nothing on the server, and the
 *  answer is the whole view again, so it replaces what the page shows. A refusal reads the fields
 *  again: a 409 means somebody changed one at the same moment, and a 403 or 422 can mean the form
 *  changed under the dialog. */
export function useUpdateContactFields() {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, values }: { id: string; values: Record<string, unknown> }) =>
      api.patch<LeadFields>(`/leads/${id}/fields`, { values }),
    onSuccess: (saved, { id }) => {
      qc.setQueryData(keys.contactFields(id), saved);
      // A value Send contract needs may have just been filled in (ACL-419). A field's row sends no
      // `leads` hint, so the contract check is asked again here; an open dialog waits for it.
      void qc.invalidateQueries({ queryKey: keys.contactContract(id) });
    },
    onError: (_error, { id }) => void qc.invalidateQueries({ queryKey: keys.contactFields(id) }),
  });
}

// --- custom fields: the forms an admin defines (ACL-255, ACL-256, ADR-087) -----------------------------

/** Which form a call is about: the kind of record, and for a superadmin the tenant (null for everyone
 *  else, whose own it is). */
export interface FormScope {
  entity: FormEntity;
  tenantId: number | null;
}

/** `tenant_id` in a POST or PUT body: only when a superadmin names a tenant. */
const tenantBody = (tenantId: number | null) => (tenantId === null ? {} : { tenant_id: tenantId });

/** Every folder and field of a form, archived ones too, with each one's access rules and how many records
 *  hold a value in each field (`GET /forms/{entity}/design`, admin only). */
export function useFormDesign(
  entity: FormEntity,
  tenantId: number | null = null,
  options?: Partial<UseQueryOptions<AdminForm>>,
) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.formDesign(entity, tenantId),
    queryFn: () => api.get<AdminForm>(`/forms/${entity}/design${qs({ tenant_id: tenantId })}`),
    ...options,
  });
}

/** What this person's own search of contacts or firms looks in, besides the platform's own fields
 *  (`GET /forms/{entity}/searchable`, ADR-101): the hint over each search box names them. */
export function useSearchedFields(entity: FormEntity) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.searchedFields(entity),
    queryFn: () => api.get<SearchedFields>(`/forms/${entity}/searchable`),
    staleTime: 5 * 60_000,
  });
}

export interface FormSectionInput {
  title: string;
  /** Made from the title when it is left out; fixed once the folder exists. */
  key?: string;
  /** Each role's level on the folder; a role left out reads. */
  rules?: FormAccessRule[];
}

/** What happens to a folder's active fields when the folder is archived with any. */
export type SectionFieldsAction = { action: "move"; to: string } | { action: "archive" };

/** What `PATCH /forms/{entity}/sections/{key}` takes: only what is sent changes, and `rules` replaces
 *  the folder's whole rule set. */
export interface FormSectionUpdateInput {
  title?: string;
  archived?: boolean;
  /** Required to archive a folder that still holds active fields: the server refuses without it. */
  fields?: SectionFieldsAction;
  rules?: FormAccessRule[];
}

export interface FormFieldInput {
  section: string;
  title: string;
  type: FormFieldType;
  required?: boolean;
  config?: FormFieldConfig;
  /** The field's own rules; a role left out follows the folder. */
  rules?: FormAccessRule[];
  /** Made from the title when it is left out; fixed once the field exists. */
  key?: string;
  /** Left out, it goes last in its folder. */
  position?: number;
}

/** What `PATCH /forms/{entity}/fields/{key}` takes: only what is sent changes, `section` moves the field
 *  to the end of that folder, and `rules` replaces its whole rule set. A platform field takes a title, a
 *  folder and rules only. */
export interface FormFieldUpdateInput {
  title?: string;
  type?: FormFieldType;
  required?: boolean;
  config?: FormFieldConfig;
  section?: string;
  archived?: boolean;
  rules?: FormAccessRule[];
}

/** One folder of `PUT /forms/{entity}/layout`: its key and the keys of its fields, in order. */
export interface FormLayoutSection {
  key: string;
  fields: string[];
}

/** A form write changes the design and what every record drawn from the form shows: the design, and
 *  every contact's and firm's fields, refetch. */
function useInvalidateForms() {
  const keys = useKeys();
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: keys.scoped(["forms"]) });
    void qc.invalidateQueries({ queryKey: keys.scoped(["leads", "detail"]) });
    void qc.invalidateQueries({ queryKey: keys.scoped(["firms", "detail"]) });
  };
}

export function useCreateFormSection() {
  const invalidate = useInvalidateForms();
  return useMutation({
    mutationFn: ({ entity, tenantId, ...input }: FormSectionInput & FormScope) =>
      api.post<AdminFormSection>(`/forms/${entity}/sections`, { ...input, ...tenantBody(tenantId) }),
    onSuccess: invalidate,
  });
}

export function useUpdateFormSection() {
  const invalidate = useInvalidateForms();
  return useMutation({
    mutationFn: ({ entity, tenantId, key, ...input }: FormSectionUpdateInput & FormScope & { key: string }) =>
      api.patch<AdminFormSection>(`/forms/${entity}/sections/${key}${qs({ tenant_id: tenantId })}`, input),
    onSuccess: invalidate,
  });
}

export function useCreateFormField() {
  const invalidate = useInvalidateForms();
  return useMutation({
    mutationFn: ({ entity, tenantId, ...input }: FormFieldInput & FormScope) =>
      api.post<AdminFormField>(`/forms/${entity}/fields`, { ...input, ...tenantBody(tenantId) }),
    onSuccess: invalidate,
  });
}

export function useUpdateFormField() {
  const invalidate = useInvalidateForms();
  return useMutation({
    mutationFn: ({ entity, tenantId, key, ...input }: FormFieldUpdateInput & FormScope & { key: string }) =>
      api.patch<AdminFormField>(`/forms/${entity}/fields/${key}${qs({ tenant_id: tenantId })}`, input),
    onSuccess: invalidate,
  });
}

/** Every active folder once, each with its active fields in order: reorders folders, reorders fields and
 *  moves fields between folders, all or nothing. The answer is the whole design. */
export function useFormLayout() {
  const keys = useKeys();
  const qc = useQueryClient();
  const invalidate = useInvalidateForms();
  return useMutation({
    mutationFn: ({ entity, tenantId, sections }: FormScope & { sections: FormLayoutSection[] }) =>
      api.put<AdminForm>(`/forms/${entity}/layout`, { ...tenantBody(tenantId), sections }),
    onSuccess: (saved, { entity, tenantId }) => {
      qc.setQueryData(keys.formDesign(entity, tenantId), saved);
      invalidate();
    },
    // A refusal is usually a design out of date (somebody added a field since it was read): read it again.
    onError: invalidate,
  });
}

/** The fields the search also looks in (ADR-101), sent whole: every one by key, at most six; the rest are
 *  switched off. The answer is the whole design. */
export function useSetSearchable() {
  const keys = useKeys();
  const qc = useQueryClient();
  const invalidate = useInvalidateForms();
  return useMutation({
    mutationFn: ({ entity, tenantId, fields }: FormScope & { fields: string[] }) =>
      api.put<AdminForm>(`/forms/${entity}/searchable`, { ...tenantBody(tenantId), fields }),
    onSuccess: (saved, { entity, tenantId }) => {
      qc.setQueryData(keys.formDesign(entity, tenantId), saved);
      invalidate();
      // A search already on screen reads the new fields at once: the contacts list, its tiles and the
      // board, or the Firms page's search.
      const searches = entity === "lead" ? [["leads", "list"], ["leads", "summary"], ["leads", "board"]] : [["firms", "search"]];
      for (const key of searches) void qc.invalidateQueries({ queryKey: keys.scoped(key) });
    },
    onError: invalidate,
  });
}

/** The fields a voicemail greeting may say (ADR-113), sent whole: every one by key; the rest are switched
 *  off. The answer is the whole design. The greeting form's menu of fields and the voicemail library
 *  follow the ticks, so both are read again. */
export function useSetSpeakable() {
  const keys = useKeys();
  const qc = useQueryClient();
  const invalidate = useInvalidateForms();
  const greetings = () => void qc.invalidateQueries({ queryKey: keys.scoped(["voicemail-messages"]) });
  return useMutation({
    mutationFn: ({ entity, tenantId, fields }: FormScope & { fields: string[] }) =>
      api.put<AdminForm>(`/forms/${entity}/speakable`, { ...tenantBody(tenantId), fields }),
    onSuccess: (saved, { entity, tenantId }) => {
      qc.setQueryData(keys.formDesign(entity, tenantId), saved);
      invalidate();
      greetings();
    },
    onError: invalidate,
  });
}

/** The records a reference field can point at, searched on the server a page at a time (ACL-260): the
 *  tenant's firms, or the contacts the reader may open. Only for a field the reader may edit. */
export function useReferenceSearch(entity: FormEntity, key: string, q: string, enabled = true) {
  const keys = useKeys();
  return useInfiniteQuery({
    queryKey: keys.referenceSearch(entity, key, q),
    queryFn: ({ pageParam }) =>
      api.get<ReferenceSearch>(
        `/forms/${entity}/fields/${key}/references${qs({ q: q || undefined, limit: PICKER_PAGE_SIZE, offset: pageParam || undefined })}`,
      ),
    initialPageParam: 0,
    getNextPageParam: (last) => last.next_offset ?? undefined,
    enabled,
    staleTime: 30_000,
  });
}

/** A firm's own fields with their values, as far as the reader's role may see them. */
export function useFirmFields(id: number) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.firmFields(id),
    queryFn: () => api.get<FirmFields>(`/firms/${id}/fields`),
    // As the contact's: a 404 (no forms on this server) is not asked again.
    retry: contactFieldsRetry,
  });
}

/** `PATCH /firms/{id}/fields`, as the contact's: all or nothing, and the answer replaces the view. */
export function useUpdateFirmFields() {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, values }: { id: number; values: Record<string, unknown> }) =>
      api.patch<FirmFields>(`/firms/${id}/fields`, { values }),
    onSuccess: (saved, { id }) => qc.setQueryData(keys.firmFields(id), saved),
    onError: (_error, { id }) => void qc.invalidateQueries({ queryKey: keys.firmFields(id) }),
  });
}

export interface ContactCreateInput {
  phone: string;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  language?: string | null;
  state?: string | null;
  timezone?: string | null;
  firm_id?: number | null;
  /** The list the new contact joins: an admin's to name, or the campaign whose line the rep staffs
   *  (ADR-225). */
  campaign_id?: string | null;
  /** With `campaign_id`, the call the rep is on: the row waits for its outcome before the dialer
   *  takes it (ADR-225). */
  after_call?: string | null;
  duplicate?: boolean;
  caller_relation?: CallerRelation | null;
  /** Another person in this lead's accident (ACL-626): core-api links the new contact to it. */
  same_accident_as?: string;
  /** Another case of this record's person (ADR-220): it takes the record's phone and name. */
  person_of?: string;
  /** The contact starts an accident of its own (ADR-220), with its role there. Not with `same_accident_as`. */
  new_accident?: boolean;
  accident_role?: AccidentRole | null;
  /** The new contact's form answers by field key, stored with it (platform ADR-203). */
  fields?: Record<string, string>;
}

export function useCreateContact() {
  const invalidate = useInvalidateContacts();
  return useMutation({
    mutationFn: (input: ContactCreateInput) => api.post<LeadCard>("/leads", input),
    onSuccess: invalidate,
  });
}

/** A supervisor puts a contact the record already has on a campaign's dial list (ADR-125), and so
 *  does the rep dialing for the campaign, for a party they link on a call (ADR-225): `afterCall`
 *  then holds the row until the call's outcome. A 409 carries a `code` saying why it joins none:
 *  `signed`, `already_listed`, `stage_closed`, … */
export function useEnlistContact() {
  const invalidate = useInvalidateContacts();
  return useMutation({
    mutationFn: ({ leadId, campaignId, afterCall }: { leadId: string; campaignId: string; afterCall?: string | null }) =>
      api.post<LeadCard>(`/leads/${leadId}/campaigns`, {
        campaign_id: campaignId,
        ...(afterCall ? { after_call: afterCall } : {}),
      }),
    onSuccess: invalidate,
  });
}

/** Takes a contact off a campaign's list (ADR-225): the row is retired as an exit retires one; the
 *  record keeps it. A 404 with code `not_listed` when they are not on it. */
export function useDelistContact() {
  const invalidate = useInvalidateContacts();
  return useMutation({
    mutationFn: ({ leadId, campaignId }: { leadId: string; campaignId: string }) =>
      api.delete<LeadCard>(`/leads/${leadId}/campaigns/${campaignId}`),
    onSuccess: invalidate,
  });
}

export function useAddNote() {
  const invalidate = useInvalidateContacts();
  return useMutation({
    mutationFn: ({ leadId, body, callId }: { leadId: string; body: string; callId?: string | null }) =>
      api.post<LeadNote>(`/leads/${leadId}/notes`, { body, call_id: callId ?? null }),
    onSuccess: invalidate,
  });
}

export function useEditNote() {
  const invalidate = useInvalidateContacts();
  return useMutation({
    mutationFn: ({ leadId, noteId, body }: { leadId: string; noteId: number; body: string }) =>
      api.patch<LeadNote>(`/leads/${leadId}/notes/${noteId}`, { body }),
    onSuccess: invalidate,
  });
}

export function useRemoveNote() {
  const invalidate = useInvalidateContacts();
  return useMutation({
    mutationFn: ({ leadId, noteId }: { leadId: string; noteId: number }) =>
      api.delete<void>(`/leads/${leadId}/notes/${noteId}`),
    onSuccess: invalidate,
  });
}

/** Set outcome on the record: an outcome off a call, with the effects of one on a call. */
export interface CalendarCallbackFilters {
  [key: string]: QueryValue;
  /** Due from this instant (ISO). */
  start?: string;
  /** Due before this instant (ISO). */
  end?: string;
  /** Kept for or booked by any of these user ids, comma-separated. */
  user_ids?: string;
  /** Any of these, comma-separated. */
  statuses?: string;
  limit?: number;
}

/** Callbacks across leads, for the calendar (ACL-658). Read again every minute, so a backup the
 *  system booked after a miss shows without a reload; every outcome recorded reads it again too, its
 *  key being under `leads`. An older core-api has no such route: the calendar then shows none. */
export function useCalendarCallbacks(
  filters: CalendarCallbackFilters,
  options?: Partial<UseQueryOptions<CalendarCallback[]>>,
) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.scoped(["leads", "calendar-callbacks", filters]),
    queryFn: () => {
      const { user_ids, statuses, ...rest } = filters;
      return api.get<CalendarCallback[]>(withLists(`/callbacks${qs(rest)}`, { user_id: user_ids, status: statuses }));
    },
    placeholderData: (previous) => previous,
    refetchInterval: 60_000,
    retry: false,
    ...options,
  });
}

/** Who takes each callback outcome's call on this lead (ACL-657). An older core-api has no such
 *  route: the form then says nothing about it. */
export function useCallbackTakers(leadId: string, enabled = true) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.scoped(["contacts", leadId, "callback-takers"]),
    queryFn: () => api.get<CallbackTaker[]>(`/leads/${leadId}/callback-takers`),
    enabled,
    retry: false,
  });
}

export function useContactOutcome() {
  const invalidate = useInvalidateContacts();
  return useMutation({
    mutationFn: ({
      leadId, disposition, note, callbackAt, callbackPurpose, callbackBackupTimes,
    }: {
      leadId: string;
      disposition: string;
      note?: string | null;
      /** A callback's time, RFC 3339 with its offset (ADR-080). */
      callbackAt?: string | null;
      /** What the callback is for (ADR-162): a few words. */
      callbackPurpose?: string | null;
      /** Its backup times in order, for when the call before each reaches nobody (ACL-559): only with
       *  a time. */
      callbackBackupTimes?: readonly string[];
    }) =>
      api.post<ContactOutcome>(`/leads/${leadId}/disposition`, {
        disposition,
        note: note || null,
        ...(callbackAt ? { callback_at: callbackAt } : {}),
        ...(callbackPurpose ? { callback_purpose: callbackPurpose } : {}),
        ...(callbackAt ? backupFields(callbackBackupTimes) : {}),
      }),
    onSuccess: invalidate,
  });
}

/** An outcome's backup times as core-api takes them: the first as `callback_backup_at`, which a
 *  core-api keeping one backup saves alone, and all of them as `callback_backup_times` (ADR-210 in
 *  platform), which it ignores. */
function backupFields(times: readonly string[] | undefined) {
  if (!times || times.length === 0) return {};
  return { callback_backup_at: times[0], ...(times.length > 1 ? { callback_backup_times: [...times] } : {}) };
}

/**
 * The open callback's backup times, added or changed later (ACL-559, ADR-183 and ADR-210 in
 * platform). Core-api keeps them on the callback and tells the dialer nothing: the callback's own
 * time stays as it is. One time goes as `backup_at`, several as `backup_times`.
 */
export function useSetCallbackBackup() {
  const invalidate = useInvalidateContacts();
  return useMutation({
    mutationFn: ({ leadId, callbackId, backupTimes }: { leadId: string; callbackId: number; backupTimes: readonly string[] }) =>
      api.put<LeadCallback>(
        `/leads/${leadId}/callbacks/${callbackId}/backup`,
        (backupTimes.length === 1 ? { backup_at: backupTimes[0] } : { backup_times: [...backupTimes] }) satisfies CallbackBackupRequest,
      ),
    onSuccess: invalidate,
  });
}

/** The open callback's backup time taken away; the callback stays as it is. */
export function useRemoveCallbackBackup() {
  const invalidate = useInvalidateContacts();
  return useMutation({
    mutationFn: ({ leadId, callbackId }: { leadId: string; callbackId: number }) =>
      api.delete<LeadCallback>(`/leads/${leadId}/callbacks/${callbackId}/backup`),
    onSuccess: invalidate,
  });
}

/** An admin reopens a lead in an exit stage (ADR-076). */
export function useReopenLead() {
  const invalidate = useInvalidateContacts();
  return useMutation({
    mutationFn: ({ leadId, stage, reason }: { leadId: string; stage: string | null; reason: string }) =>
      api.post<LeadReopen>(`/leads/${leadId}/reopen`, { stage, reason }),
    onSuccess: invalidate,
  });
}

/** The board: a column per stage, each its first `perStage` cards, under the list's filters. With
 *  `stage` in the filters it is that one column, which is how a column shows more. */
export function useContactBoard(filters: ContactListFilters, perStage: number, enabled = true) {
  const keys = useKeys();
  const query = { ...filters, per_stage: perStage };
  return useQuery({
    queryKey: keys.contactBoard(query),
    queryFn: () => api.get<ContactBoard>(`/leads/board${qs(query)}`),
    placeholderData: (previous) => previous,
    refetchInterval: CONTACTS_POLL,
    enabled,
  });
}

/**
 * A hand move on the board (ADR-078), with the dial lists: core-api holds them until a scheduled
 * stage's `until` and asks the dialer to place them for the new stage. The card moves at once in every board query that holds it
 * (`moveCard`) — a drag that snapped back until the refetch read as a drop that had not taken — and
 * goes back if core-api refuses. Everything under `leads` is refetched after, either way.
 */
export function useMoveLead() {
  const keys = useKeys();
  const qc = useQueryClient();
  const invalidate = useInvalidateContacts();
  const boards = keys.scoped(["leads", "board"]);
  return useMutation({
    mutationFn: ({ leadId, stage, until = null }: { leadId: string; stage: string; until?: string | null }) =>
      api.post<LeadStageMove>(`/leads/${leadId}/stage`, { stage, until }),
    onMutate: async ({ leadId, stage }) => {
      await qc.cancelQueries({ queryKey: boards });
      const before = qc.getQueriesData<ContactBoard>({ queryKey: boards });
      const now = new Date().toISOString();
      qc.setQueriesData<ContactBoard>({ queryKey: boards }, (board) =>
        board ? moveCard(board, leadId, stage, now) : board,
      );
      return { before };
    },
    onError: (_error, _input, context) => {
      for (const [key, board] of context?.before ?? []) qc.setQueryData(key, board);
    },
    onSettled: invalidate,
  });
}

/** An admin deletes a lead (ADR-078): off every screen and dial list, kept underneath. Everything
 *  under `leads` is refetched except the deleted lead's own record, which is a 404 now and is
 *  dropped instead: the screen showing it closes on success. */
export function useDeleteLead() {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ leadId, reason }: { leadId: string; reason: string | null }) =>
      api.delete<LeadDeleted>(`/leads/${leadId}${qs({ reason: reason || undefined })}`),
    onSuccess: (_deleted, { leadId }) => {
      const gone = keys.contact(leadId);
      const itsOwn = (key: readonly unknown[]) => gone.every((part, i) => key[i] === part);
      void qc.cancelQueries({ queryKey: gone });
      void qc.invalidateQueries({ queryKey: keys.scoped(["leads"]), predicate: (q) => !itsOwn(q.queryKey) });
    },
  });
}

export function useBulkContacts() {
  const invalidate = useInvalidateContacts();
  return useMutation({
    mutationFn: (input: { ids: string[]; action: "set_firm"; firm_id: number }) =>
      api.post<ContactBulkResult>("/leads/bulk", input),
    onSuccess: invalidate,
  });
}

// --- stages (ADR-077) ---------------------------------------------------------------------------------

/** The tenant's stages, in the order the console lists them (working, scheduled, exit), each with its
 *  lead count. Anyone signed in reads them; a superadmin names the tenant, or gets the sole one. */
export function useStages(tenantId: number | null = null, options?: Partial<UseQueryOptions<StageRow[]>>) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.stages(tenantId),
    queryFn: () => api.get<StageRow[]>(`/stages${qs({ tenant_id: tenantId })}`),
    staleTime: 5 * 60_000,
    ...options,
  });
}

export interface StageInput {
  slug: string;
  label: string;
  description: string | null;
  group: StageRow["group"];
  exit_scope: StageRow["exit_scope"];
  dial_boost: boolean;
  color?: StageColor | null;
  tenant_id?: number | null;
}

/** What `PATCH /stages/{slug}` takes: only what is sent changes. A system stage takes the words
 *  and the order only. */
export interface StageUpdateInput {
  label?: string;
  description?: string | null;
  group?: StageRow["group"];
  exit_scope?: StageRow["exit_scope"];
  dial_boost?: boolean;
  color?: StageColor | null;
}

/** A stage write changes what every label says and every picker offers. A delete also moves the
 *  stage's leads, outcomes, line rules and voicemail messages, so all of those refetch. */
function useInvalidateStages() {
  const keys = useKeys();
  const qc = useQueryClient();
  return (moved = false) => {
    void qc.invalidateQueries({ queryKey: keys.scoped(["stages"]) });
    void qc.invalidateQueries({ queryKey: keys.scoped(["leads"]) });
    if (!moved) return;
    void qc.invalidateQueries({ queryKey: keys.scoped(["dispositions"]) });
    void qc.invalidateQueries({ queryKey: keys.scoped(["voicemail-messages"]) });
    void qc.invalidateQueries({ queryKey: keys.scoped(["campaigns"]) });
    void qc.invalidateQueries({ queryKey: keys.queues() });
  };
}

export function useCreateStage() {
  const invalidate = useInvalidateStages();
  return useMutation({
    mutationFn: (input: StageInput) => api.post<StageRow>("/stages", input),
    onSuccess: () => invalidate(),
  });
}

export function useUpdateStage() {
  const invalidate = useInvalidateStages();
  return useMutation({
    mutationFn: ({ slug, tenantId, ...input }: StageUpdateInput & { slug: string; tenantId: number | null }) =>
      api.patch<StageRow>(`/stages/${slug}${qs({ tenant_id: tenantId })}`, input),
    onSuccess: () => invalidate(),
  });
}

/** Every stage of the tenant, once, in the new order. */
export function useReorderStages() {
  const keys = useKeys();
  const qc = useQueryClient();
  const invalidate = useInvalidateStages();
  return useMutation({
    mutationFn: ({ slugs, tenantId }: { slugs: string[]; tenantId: number | null }) =>
      api.put<StageRow[]>("/stages/order", { slugs, tenant_id: tenantId }),
    onSuccess: (saved, { tenantId }) => {
      qc.setQueryData(keys.stages(tenantId), saved);
      invalidate();
    },
  });
}

/** Deletes a stage; `replaceWith` takes whatever used it. */
export function useDeleteStage() {
  const invalidate = useInvalidateStages();
  return useMutation({
    mutationFn: ({ slug, replaceWith, tenantId }: { slug: string; replaceWith: string | null; tenantId: number | null }) =>
      api.delete<StageRemoved>(`/stages/${slug}${qs({ replace_with: replaceWith, tenant_id: tenantId })}`),
    onSuccess: (result) => invalidate(result.replaced_by !== null),
  });
}

// --- firms (ADR-045) ----------------------------------------------------------------------------------

export function useFirm(id: number | undefined) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.firm(id ?? 0),
    queryFn: () => api.get<Firm>(`/firms/${id}`),
    enabled: id !== undefined,
  });
}

/** A firm write changes the list, the page, and every contact that names it (a pause holds them). */
function useInvalidateFirms() {
  const keys = useKeys();
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: keys.firms() });
    void qc.invalidateQueries({ queryKey: keys.scoped(["leads"]) });
  };
}

export function useCreateFirm() {
  const invalidate = useInvalidateFirms();
  return useMutation({
    mutationFn: (input: FirmInput) => api.post<Firm>("/firms", input),
    onSuccess: invalidate,
  });
}

export function useUpdateFirm() {
  const invalidate = useInvalidateFirms();
  return useMutation({
    mutationFn: ({ id, ...input }: FirmInput & { id: number }) => api.put<Firm>(`/firms/${id}`, input),
    onSuccess: invalidate,
  });
}

export function useAddFirmTag() {
  const invalidate = useInvalidateFirms();
  return useMutation({
    mutationFn: ({ firmId, tag }: { firmId: number; tag: string }) =>
      api.post<Firm>(`/firms/${firmId}/tags`, { tag }),
    onSuccess: invalidate,
  });
}

export function useRemoveFirmTag() {
  const invalidate = useInvalidateFirms();
  return useMutation({
    mutationFn: ({ firmId, tagId }: { firmId: number; tagId: number }) =>
      api.delete<Firm>(`/firms/${firmId}/tags/${tagId}`),
    onSuccess: invalidate,
  });
}

// --- cadences (ADR-066) ------------------------------------------------------------------------------

/** How often a lead's or a campaign's cadence view refreshes on its own. */
const CADENCE_POLL = CADENCE_POLL_MS;

export function useCadences(options?: Partial<UseQueryOptions<Cadence[]>>) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.cadences(),
    queryFn: () => api.get<Cadence[]>("/cadences"),
    ...options,
  });
}

export function useCadence(id: number | undefined) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.cadence(id ?? 0),
    queryFn: () => api.get<Cadence>(`/cadences/${id}`),
    enabled: id !== undefined,
  });
}

export function useCadenceVersions(id: number | undefined, options?: { enabled?: boolean }) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.cadenceVersions(id ?? 0),
    queryFn: () => api.get<CadenceVersion[]>(`/cadences/${id}/versions`),
    enabled: id !== undefined && (options?.enabled ?? true),
  });
}

export function useCadenceCampaigns(id: number | undefined, options?: { enabled?: boolean }) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.cadenceCampaigns(id ?? 0),
    queryFn: () => api.get<CadenceCampaignRef[]>(`/cadences/${id}/campaigns`),
    enabled: id !== undefined && (options?.enabled ?? true),
  });
}

/** The fields a text step's words can use (ADR-135 in platform), for the step's Insert field menu
 *  and for judging a draft: the platform's own, and the tenant's own fields an admin ticked as
 *  ones that may be said to a lead. An admin's to read, so a caller asks only for an admin. */
export function useCadenceTextFields(enabled = true) {
  const keys = useKeys();
  return useQuery({
    enabled,
    queryKey: keys.cadenceTextFields(),
    queryFn: () => api.get<GreetingField[]>("/cadences/text-fields"),
    staleTime: 5 * 60_000,
  });
}

/** A cadence write changes the catalog and every campaign that shows one. */
function useInvalidateCadences() {
  const keys = useKeys();
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: keys.cadences() });
    void qc.invalidateQueries({ queryKey: keys.scoped(["campaigns"]) });
  };
}

export interface CadenceInput {
  slug: string;
  name: string;
  body: CadenceBody;
  note?: string | null;
  tenant_id?: number | null;
}

export function useCreateCadence() {
  const invalidate = useInvalidateCadences();
  return useMutation({
    mutationFn: (input: CadenceInput) => api.post<Cadence>("/cadences", input),
    onSuccess: invalidate,
  });
}

export function useCreateCadenceVersion() {
  const invalidate = useInvalidateCadences();
  return useMutation({
    mutationFn: ({ id, ...input }: { id: number; body: CadenceBody; note?: string | null }) =>
      api.post<Cadence>(`/cadences/${id}/versions`, input),
    onSuccess: invalidate,
  });
}

export function useUpdateCadence() {
  const invalidate = useInvalidateCadences();
  return useMutation({
    mutationFn: ({ id, ...input }: { id: number; name?: string; active?: boolean }) =>
      api.put<Cadence>(`/cadences/${id}`, input),
    onSuccess: invalidate,
  });
}

/** The lead's place on each cadence campaign, and why (ADR-066). */
export function useLeadCadence(leadId: string | undefined, options?: { enabled?: boolean }) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.leadCadence(leadId ?? "none"),
    queryFn: () => api.get<LeadCadence>(`/leads/${leadId}/cadence`),
    enabled: leadId !== undefined && (options?.enabled ?? true),
    // Sooner while a request waits for the dialer, so the old cadence's phase is not left up.
    refetchInterval: (query) => leadCadencePoll(query.state.data),
  });
}

export interface CadenceActionInput {
  leadId: string;
  action: "pause" | "resume" | "re-enroll";
  campaign_id?: string | null;
  reason?: string | null;
  until?: string | null;
  idempotency_key?: string | null;
}

/** Pause, resume or re-enroll a lead on its cadence campaigns. Every contact query refetches:
 *  the hold shows on the record, the request on the lead's cadence view. */
export function useLeadCadenceAction() {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ leadId, action, ...input }: CadenceActionInput) =>
      api.post<CadenceActionResult>(`/leads/${leadId}/cadence/${action}`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.scoped(["leads"]) });
      void qc.invalidateQueries({ queryKey: keys.scoped(["campaigns"]) });
    },
  });
}

/** The campaign's work under its cadence: the buckets, one bucket's rows, and the gauge. */
export function useCadenceWork(campaignId: string, bucket: string | null) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.campaignCadenceWork(campaignId, bucket ?? "none"),
    queryFn: () =>
      api.get<CadenceWork>(`/campaigns/${campaignId}/cadence-work${qs({ bucket })}`),
    refetchInterval: CADENCE_POLL,
    placeholderData: (previous) => previous,
  });
}

/** How often the abandon rate reads again. */
const ABANDONMENT_POLL = 60_000;

/** The campaign's abandon rate over 30 days against the rule. */
export function useCampaignAbandonment(campaignId: string) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.campaignAbandonment(campaignId),
    queryFn: () => api.get<CampaignAbandonment>(`/campaigns/${campaignId}/abandonment`),
    refetchInterval: ABANDONMENT_POLL,
  });
}

/** Every dialing campaign's abandon rate, the highest first: the Live floor's warning. */
export function useAbandonmentRates(enabled = true) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.abandonmentRates(),
    queryFn: () => api.get<CampaignAbandonment[]>("/abandonment"),
    refetchInterval: ABANDONMENT_POLL,
    enabled,
  });
}

/** A preview of where each row would stand on the campaign's cadence; nothing is written. */
export function useCadenceMigratePreview() {
  return useMutation({
    mutationFn: (campaignId: string) =>
      api.post<CadenceMigrate>(`/campaigns/${campaignId}/cadence/migrate?dry_run=true`, {}),
  });
}

export interface TenantCadencePause {
  tenant_id: number;
  paused: boolean;
}

export function useTenantCadencePaused(tenantId: number | null | undefined) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.cadencePause(),
    queryFn: () => api.get<TenantCadencePause>(`/tenants/${tenantId}/cadence-pause`),
    enabled: tenantId !== null && tenantId !== undefined,
  });
}

export function useTenantCadencePause() {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, paused }: { tenantId: number; paused: boolean }) =>
      api.put<TenantCadencePause>(`/tenants/${tenantId}/cadence-pause`, { paused }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.cadencePause() });
      void qc.invalidateQueries({ queryKey: keys.scoped(["campaigns"]) });
    },
  });
}

/** The line a new lead on no campaign is given from (ADR-184 in `platform`); null is nobody. */
export interface TenantLeadLine {
  tenant_id: number;
  default_line_id: number | null;
  line_name: string | null;
}

export function useTenantLeadLine(tenantId: number | null | undefined) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.leadLine(),
    queryFn: () => api.get<TenantLeadLine>(`/tenants/${tenantId}/lead-line`),
    enabled: tenantId !== null && tenantId !== undefined,
  });
}

export function useSetTenantLeadLine() {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, lineId }: { tenantId: number; lineId: number | null }) =>
      api.put<TenantLeadLine>(`/tenants/${tenantId}/lead-line`, { default_line_id: lineId }),
    onSuccess: (saved) => qc.setQueryData(keys.leadLine(), saved),
  });
}

// --- calendars (ADR-085) ------------------------------------------------------------------------------

/** The tenant's calendars with their staff and hours. Anyone signed in reads them: booking an
 *  appointment picks one. */
export function useCalendars(filters: { active?: boolean } = {}, options?: Partial<UseQueryOptions<Calendar[]>>) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.calendars(filters),
    queryFn: () => api.get<Calendar[]>(`/calendars${qs(filters)}`),
    ...options,
  });
}

/** The calendar every appointment is booked on (ACL-655, ADR-215). A POST because core-api makes it
 *  when it is missing and adds any active rep not on it; read afresh each time a form opens, so a
 *  new rep can be booked. */
export function useMainCalendar(options?: Partial<UseQueryOptions<Calendar>>) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.scoped(["calendars", "main"]),
    queryFn: () => api.post<Calendar>("/calendars/main"),
    staleTime: 0,
    ...options,
  });
}

export function useCalendar(id: number | undefined) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.calendar(id ?? 0),
    queryFn: () => api.get<Calendar>(`/calendars/${id}`),
    enabled: id !== undefined,
  });
}

/** A staff member as `POST`/`PATCH /calendars` take them: the user and their settings, no names. */
export interface CalendarStaffInput {
  user_id: number;
  priority: StaffPriority;
  location_kind: MeetingLocationKind;
  location_detail: string | null;
  availability: AvailabilityRange[];
}

/** What `POST /calendars` takes. Unsent rules take the server's defaults. */
export interface CalendarInput {
  name: string;
  calendar_type: CalendarType;
  timezone: string;
  description?: string | null;
  tenant_id?: number | null;
  staff?: CalendarStaffInput[];
}

/** What `PATCH /calendars/{id}` takes: only what is sent changes, and `staff` replaces the list. */
export type CalendarUpdateInput = Partial<
  Omit<Calendar, "id" | "tenant_id" | "staff" | "created_at" | "updated_at">
> & { staff?: CalendarStaffInput[] };

function useInvalidateCalendars() {
  const keys = useKeys();
  const qc = useQueryClient();
  return (saved?: Calendar) => {
    if (saved) qc.setQueryData(keys.calendar(saved.id), saved);
    void qc.invalidateQueries({ queryKey: keys.scoped(["calendars", "list"]) });
    void qc.invalidateQueries({ queryKey: keys.scoped(["calendars", "main"]) });
    void qc.invalidateQueries({ queryKey: keys.scoped(["calendars", "slots"]) });
  };
}

export function useUpdateCalendar() {
  const invalidate = useInvalidateCalendars();
  return useMutation({
    mutationFn: ({ id, ...input }: CalendarUpdateInput & { id: number }) =>
      api.patch<Calendar>(`/calendars/${id}`, input),
    onSuccess: (saved) => invalidate(saved),
  });
}

/** Deletes a calendar nothing was booked on; core-api refuses one with appointments (409). */
export function useDeleteCalendar() {
  const keys = useKeys();
  const qc = useQueryClient();
  const invalidate = useInvalidateCalendars();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/calendars/${id}`),
    onSuccess: (_, id) => {
      qc.removeQueries({ queryKey: keys.calendar(id) });
      invalidate();
    },
  });
}

/** The free slots from `start` to `end` (days on the calendar's clock, inclusive; 62 at most). */
export function useCalendarSlots(
  id: number | undefined,
  start: string,
  end: string,
  options?: Partial<UseQueryOptions<CalendarSlots>>,
  /** An appointment being rescheduled: its own time and buffers count as free. */
  ignore?: number,
) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.calendarSlots(id ?? 0, start, end, ignore),
    queryFn: () => api.get<CalendarSlots>(`/calendars/${id}/slots${qs({ start, end, ignore })}`),
    enabled: id !== undefined,
    ...options,
  });
}

/** What `POST /calendars/{id}/appointments` takes. `start` must be one of the calendar's slots,
 *  unless `end` is sent too: a custom time (ACL-521, ADR-169 in platform). The same
 *  `idempotency_key` again returns the appointment it already made. */
export interface AppointmentBookInput {
  start: string;
  end?: string;
  lead_id?: string | null;
  user_id?: number | null;
  title?: string | null;
  notes?: string | null;
  source?: Appointment["source"];
  /** Booked as scheduled, or already confirmed with the contact. */
  status?: "scheduled" | "confirmed";
  idempotency_key?: string | null;
  /** Text the contact a confirmation once it is booked (ACL-504); off unless asked for. */
  send_confirmation?: boolean;
  /** Text the contact reminders before it (ACL-504); on unless turned off. */
  send_reminders?: boolean;
}

/** Books an appointment; the slot it took leaves every open slots list. */
export function useBookAppointment() {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ calendarId, ...input }: AppointmentBookInput & { calendarId: number }) =>
      api.post<AppointmentBooked>(`/calendars/${calendarId}/appointments`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.scoped(["calendars", "slots"]) });
      void qc.invalidateQueries({ queryKey: keys.scoped(["appointments", "list"]) });
    },
  });
}

// --- appointments (ACL-291) ---------------------------------------------------------------------------

export interface AppointmentFilters {
  [key: string]: QueryValue;
  /** Ending after this instant (ISO). */
  start?: string;
  /** Starting before this instant (ISO). */
  end?: string;
  lead_id?: string;
  /** In the title. */
  q?: string;
  /** Any of these, comma-separated: sent as `status=a&status=b`, which `qs` does not do. */
  statuses?: string;
  /** Hosted by any of these user ids, comma-separated. */
  user_ids?: string;
  /** On any of these calendar ids, comma-separated. */
  calendar_ids?: string;
}

/** `path?a=1` plus `name=v` for each comma-separated value of each list: the API reads a list as
 *  the parameter repeated, which `qs` does not write. */
function withLists(path: string, lists: Record<string, string | undefined>): string {
  const extra = Object.entries(lists).flatMap(([name, csv]) =>
    (csv ?? "").split(",").filter(Boolean).map((v) => `${name}=${encodeURIComponent(v)}`),
  );
  if (extra.length === 0) return path;
  return `${path}${path.includes("?") ? "&" : "?"}${extra.join("&")}`;
}

function appointmentsPath({ statuses, user_ids, calendar_ids, ...rest }: AppointmentFilters): string {
  return withLists(`/appointments${qs(rest)}`, { status: statuses, user_id: user_ids, calendar_id: calendar_ids });
}

/** Appointments that overlap `start`–`end`, earliest first. A rep gets the ones they host or booked. */
export function useAppointments(filters: AppointmentFilters, options?: Partial<UseQueryOptions<AppointmentListItem[]>>) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.appointments(filters),
    queryFn: () => api.get<AppointmentListItem[]>(appointmentsPath(filters)),
    placeholderData: (previous) => previous,
    ...options,
  });
}

export function useAppointment(id: number | undefined) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.appointment(id ?? 0),
    queryFn: () => api.get<AppointmentDetail>(`/appointments/${id}`),
    enabled: id !== undefined,
  });
}

/** A change to an appointment moves its list rows and, for a cancel or a reschedule, the slots. */
function useAppointmentSaved() {
  const keys = useKeys();
  const qc = useQueryClient();
  return (saved: AppointmentDetail, previousId?: number) => {
    qc.setQueryData(keys.appointment(saved.id), saved);
    if (previousId !== undefined) void qc.invalidateQueries({ queryKey: keys.appointment(previousId) });
    void qc.invalidateQueries({ queryKey: keys.scoped(["appointments", "list"]) });
    void qc.invalidateQueries({ queryKey: keys.scoped(["calendars", "slots"]) });
  };
}

export interface AppointmentUpdateInput {
  title?: string;
  notes?: string | null;
  location_kind?: MeetingLocationKind;
  location_detail?: string | null;
  /** Whether the contact is texted reminders before it (ACL-504); until it starts. */
  send_reminders?: boolean;
}

export function useUpdateAppointment() {
  const saved = useAppointmentSaved();
  return useMutation({
    mutationFn: ({ id, ...input }: AppointmentUpdateInput & { id: number }) =>
      api.patch<AppointmentDetail>(`/appointments/${id}`, input),
    onSuccess: (detail) => saved(detail),
  });
}

/** Scheduled, confirmed, showed, no-show or cancelled; `note` is kept on the history row. */
export function useSetAppointmentStatus() {
  const saved = useAppointmentSaved();
  return useMutation({
    mutationFn: ({ id, status, note }: { id: number; status: string; note?: string | null }) =>
      api.post<AppointmentDetail>(`/appointments/${id}/status`, { status, note }),
    onSuccess: (detail) => saved(detail),
  });
}

/** A contact takes a seat in a class session (ACL-293). */
export function useAddAttendee() {
  const saved = useAppointmentSaved();
  return useMutation({
    mutationFn: ({ id, leadId }: { id: number; leadId: string }) =>
      api.post<AppointmentDetail>(`/appointments/${id}/attendees`, { lead_id: leadId }),
    onSuccess: (detail) => saved(detail),
  });
}

/** A contact leaves a class session; the seat is free again. */
export function useRemoveAttendee() {
  const saved = useAppointmentSaved();
  return useMutation({
    mutationFn: ({ id, leadId }: { id: number; leadId: string }) =>
      api.delete<AppointmentDetail>(`/appointments/${id}/attendees/${leadId}`),
    onSuccess: (detail) => saved(detail),
  });
}

/** Books the meeting again at `start`; the answer is the new appointment, the old one closes. */
export function useRescheduleAppointment() {
  const saved = useAppointmentSaved();
  return useMutation({
    /** `end` makes the new time a custom one (ACL-521). */
    mutationFn: ({ id, start, end }: { id: number; start: string; end?: string }) =>
      api.post<AppointmentDetail>(`/appointments/${id}/reschedule`, { start, end }),
    onSuccess: (detail, { id }) => saved(detail, id),
  });
}

// --- time blocked off (ACL-292) -------------------------------------------------------------------------

export interface CalendarBlockFilters {
  [key: string]: QueryValue;
  start?: string;
  end?: string;
  /** Comma-separated; a block matches if it is one of these people's or these calendars'. */
  user_ids?: string;
  calendar_ids?: string;
}

export function useCalendarBlocks(filters: CalendarBlockFilters, options?: Partial<UseQueryOptions<CalendarBlock[]>>) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.calendarBlocks(filters),
    queryFn: () => {
      const { user_ids, calendar_ids, ...rest } = filters;
      return api.get<CalendarBlock[]>(withLists(`/calendar-blocks${qs(rest)}`, { user_id: user_ids, calendar_id: calendar_ids }));
    },
    placeholderData: (previous) => previous,
    ...options,
  });
}

export interface CalendarBlockInput {
  user_id?: number | null;
  calendar_id?: number | null;
  title?: string | null;
  start: string;
  end: string;
}

/** A block closes slots: the slots refetch with the blocks. */
function useBlocksChanged() {
  const keys = useKeys();
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: keys.scoped(["calendar-blocks"]) });
    void qc.invalidateQueries({ queryKey: keys.scoped(["calendars", "slots"]) });
  };
}

export function useCreateCalendarBlock() {
  const changed = useBlocksChanged();
  return useMutation({
    mutationFn: (input: CalendarBlockInput) => api.post<CalendarBlock>("/calendar-blocks", input),
    onSuccess: changed,
  });
}

export function useDeleteCalendarBlock() {
  const changed = useBlocksChanged();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/calendar-blocks/${id}`),
    onSuccess: changed,
  });
}

// --- Opportunities' smart lists (ADR-078) -------------------------------------------------------

export function useOpportunityLists() {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.opportunityLists(),
    queryFn: () => api.get<OpportunityListRow[]>("/opportunity-lists"),
    staleTime: 60_000,
  });
}

function useInvalidateOpportunityLists() {
  const keys = useKeys();
  const client = useQueryClient();
  return () => client.invalidateQueries({ queryKey: keys.opportunityLists() });
}

export function useCreateOpportunityList() {
  const invalidate = useInvalidateOpportunityLists();
  return useMutation({
    mutationFn: (input: OpportunityListInput) => api.post<OpportunityListRow>("/opportunity-lists", input),
    onSuccess: () => invalidate(),
  });
}

export function useUpdateOpportunityList() {
  const invalidate = useInvalidateOpportunityLists();
  return useMutation({
    mutationFn: ({ id, ...input }: Partial<OpportunityListInput> & { id: string }) =>
      api.patch<OpportunityListRow>(`/opportunity-lists/${id}`, input),
    onSuccess: () => invalidate(),
  });
}

export function useDeleteOpportunityList() {
  const invalidate = useInvalidateOpportunityLists();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/opportunity-lists/${id}`),
    onSuccess: () => invalidate(),
  });
}
